import * as THREE from "three";
import { closePopover, formatSig4, openLinearPopover, syncLinearPopover, SliderTransform } from "./numPopover";
import { ModalAction, ModalCheckbox, showActionModal, showUploadSuccessModal, showWaitModal } from "./modal";
import { attachTooltip } from "./tooltip";
import { EXPORT_FPS } from "../videoExport";
import { RailTimbre, Timbre, kitPieceIndex, TIMBRE_CATALOG, RAIL_TIMBRE_CATALOG } from "../audio";
import { micStore, MicPitch } from "../micStore";
import { showRecordModal } from "./recordModal";
import { hasTosConsent, recordTosConsent, showMicLifecycleModal, showTermsModal } from "../legal";
import { hiddenReportedIds, hideReported } from "../reportedHidden";
import {
  CLOCK_BPM_MAX,
  CLOCK_BPM_MIN,
  DECAY_DEFAULT_S,
  DECAY_MAX_S,
  DELAY_DEFAULT_S,
  DELAY_MAX_S,
  ROUTER_HOLD_DEFAULT_S,
  ROUTER_HOLD_MAX_S,
  DEFAULT_FIELD_FADE,
  DEFAULT_FIELD_K,
  DEFAULT_FIELD_OMEGA,
  DEFAULT_FIELD_RADIUS,
  DEFAULT_FIELD_STRENGTH,
  EMIT_DRAG_MAX,
  EMIT_RATE_MAX,
  EMIT_RATE_MIN,
  FALL_KILL_DIST_MAX,
  FALL_KILL_DIST_MIN,
  FALL_KILL_LIFE_DIV_MAX,
  FALL_KILL_LIFE_DIV_MIN,
  FIELD_K_MAX,
  FIELD_OMEGA_MAX,
  FIELD_RADIUS_MAX,
  FIELD_RADIUS_MIN,
  FIELD_STRENGTH_MAX,
  LIFESPAN_SLIDER_MAX,
  STREAM_RATE_LIFE_BUDGET,
  MASS_MAX,
  SEQ_MAX_ROWS,
  TAP_TEMPO_MIN_TAPS,
  TAP_TEMPO_TIMEOUT_MS,
  TRANSPOSE_RANGE,
  KNOB_MIN_DEFAULT,
  KNOB_MAX_DEFAULT,
} from "../constants";
import { ARRAY_LEN_MAX, ARRAY_ROWS_MAX, ButtonMode, ObjectTemplate, ObjectType, OrbitParams, TimerMode, ValueOperator, valueOperatorKind, VALUE_OPERATORS } from "../objects";
import { EditSnap } from "../snap";
import { EmitterParams, Meter, seqColumns } from "../emitters";
import { ExprLine } from "../exprBuilder";
import { BulkKind, GroupBulkType, ParamState } from "../groupBulkParams";
import { paramLabel } from "../paramLabels";
import { BloomMode, DebugRenderState, ViewMode, WALL_DEFAULT_NORMAL_SCALE, WALL_DEFAULT_PARAMS, WALL_DEFAULT_RESOLUTION, WALL_DEFAULT_TILE, WALL_DEFAULT_Z, WALL_RESOLUTIONS, WallParams, WallPreset, wallParamsToPreset, wallPresetNormalScale, wallPresetParams } from "../scene";
import { FEATURE_CATEGORIES, FeatureCategory, loadBookmarkThumb, loadMicDeleteWarnSuppressed, saveBookmarkThumb, saveMicDeleteWarnSuppressed, Settings, SlotEntry } from "../settings";
import { AuthStatus, BookmarkCategory, BookmarkItem, DISPLAY_NAME_MAX, MicAsset, MIC_MAX_COUNT, SceneListItem, SceneQuery, SceneSort, SceneTagOps, TagMutationResult, UploadResult } from "../share";
import { sanitizeTags, TAG_MAX_COUNT, TAG_MAX_LEN } from "../tags";
import { avatarFor } from "../avatar";
import { Tool } from "../appContext";
import { FeedConsumer, FeedCoreApi, FeedLoadResult, FeedSpinner, openFeed } from "./feedCore";
import { createRadialMenu, RadialAnchor, RadialItem, RadialMenuApi } from "./radialMenu";
import { Language, MessageKey, t } from "../i18n";
import { isNodeGatedTool, MULTIPLAYER_ENABLED } from "../featureGate";
import { track } from "../telemetry";

/**
 * What the right-hand context panel currently shows: a placement template (when
 * a tool is armed but nothing is selected) or a live selection, of either kind;
 * `none` hides every control row (off tool + empty selection = pure pick mode).
 */
export type ContextTarget =
  | { kind: "object"; template: ObjectTemplate; selected: boolean }
  | { kind: "emitter"; params: EmitterParams; selected: boolean }
  | { kind: "none" };

export const OPERATOR_LABELS: Record<ValueOperator, string> = {
  add: "+", sub: "−", mul: "×", div: "÷", mod: "mod",
  floor: "floor", frac: "frac", round: "round", ceil: "ceil",
  abs: "abs", neg: "neg", sign: "sign",
  sqrt: "√", square: "x²", recip: "1÷x", sin: "sin", cos: "cos",
  random: "rand",
};

export interface UploadFieldSummary {
  gravityY: number;
  clockPeriod: number;
  features: FeatureCategory[];
  simpleMode: boolean;
  cameraEnabled: boolean;
  viewMode: ViewMode;
  wallPreset: WallPreset;
  usesMic: boolean;
}

interface ToolbarHandlers {
  onViewMode: (mode: ViewMode) => void;
  onUndo: () => void;
  onRedo: () => void;
  onBounce: (value: number) => void;
  onFriction: (value: number) => void;
  onMass: (value: number) => void;
  onMassEnabled: (enabled: boolean) => void;
  onKillOnContact: (enabled: boolean) => void;
  onFade: (value: number) => void;
  onCollisionEnabled: (enabled: boolean) => void;
  onValueOperator: (operator: ValueOperator) => void;
  onValueOperand: (value: number) => void;
  valueOperand2Wired: (id: string) => boolean;
  onDecayS: (seconds: number) => void;
  onDelayS: (seconds: number) => void;
  onRouterHoldS: (seconds: number) => void;
  onRouterHoldResetOnPulse: (enabled: boolean) => void;
  onSetSeqParam: (id: string, key: "seqMin" | "seqMax" | "seqLen" | "seqCycleLength", value: number) => void;
  onTimerMode: (mode: TimerMode) => void;
  onButtonMode: (mode: ButtonMode) => void;
  onButtonPulseOnReset: (enabled: boolean) => void;
  onLampValue: (value: number) => void;
  onLampHue: (hue: number) => void;
  onLampSat: (sat: number) => void;
  onKnobMin: (value: number) => void;
  onKnobMax: (value: number) => void;
  onKnobInt: (enabled: boolean) => void;
  onTimbreSet: (timbres: Timbre[]) => void;
  onTimbreParam: (name: string, value: number) => void;
  onSize: (params: THREE.Vector4) => void;
  onArc: (arcDeg: number) => void;
  onOrbit: (params: OrbitParams) => void;
  orbitTargetMode: (id: string) => boolean;
  onPiece: (pieceIndex: number) => void;
  onRailTimbre: (timbre: RailTimbre) => void;
  onTimbre: (timbre: Timbre) => void;
  onObjectVolume: (value: number) => void;
  onObjectMuted: (enabled: boolean) => void;
  onName: (name: string) => void;
  groupBulkModel: (id: string) => GroupBulkType[];
  onGroupBulkParam: (type: BulkKind, name: string, value: number) => void;
  buildGroupExpressions: (id: string) => ExprLine[];
  entityLabel: (id: string) => string;
  countOwner: (id: string) => { name: string; color: string | null } | null;
  onVolume: (value: number) => void;
  onWoodDecay: (seconds: number) => void;
  onBellTranspose: (octaves: number) => void;
  onBellModRatio: (ratio: number) => void;
  onBellModIndex: (index: number) => void;
  onBellLevel: (level: number) => void;
  onDensityLow: (rate: number) => void;
  onDensityHigh: (rate: number) => void;
  onDensityTransient: (amount: number) => void;
  onDensityDetune: (cents: number) => void;
  onDensityVoiceGain: (floor: number) => void;
  onDensityDuck: (floor: number) => void;
  onDensityReverb: (amount: number) => void;
  onEmitterRate: (rate: number) => void;
  onEmitterBounce: (value: number) => void;
  onEmitterFriction: (value: number) => void;
  onEmitterDrag: (value: number) => void;
  onEmitterCone: (deg: number) => void;
  onEmitterSpeed: (min: number, max: number) => void;
  onEmitterLife: (min: number, max: number) => void;
  onEmitterPulseSpeed: (enabled: boolean) => void;
  onEmitterMeter: (meter: Meter) => void;
  onEmitterNum: (num: number) => void;
  onPointSize: (size: number) => void;
  onGravityY: (value: number) => void;
  onFallKillDist: (value: number) => void;
  onFallKillLifeDiv: (value: number) => void;
  onClockPeriod: (seconds: number) => void;
  onClockReset: () => void;
  onEditSnap: (snap: EditSnap) => void;
  onCommunitySceneCameraEnabled: (on: boolean) => void;
  onCommunitySceneObjectPlacement: (on: boolean) => void;
  onShowNoteName: (show: boolean) => void;
  onLanguage: (lang: Language) => void;
  onFoldout: (title: string, collapsed: boolean) => void;
  onFeature: (category: FeatureCategory, on: boolean) => void;
  onDebugRender: (state: DebugRenderState) => void;
  onDebugSim: (running: boolean) => void;
  onDebugReadback: (running: boolean) => void;
  onDebugGrid: (useGrid: boolean) => void;
  onWallTile: (tiles: number) => void;
  onWallNormalScale: (scale: number) => void;
  onWallResolution: (w: number, h: number) => void;
  onWallZ: (z: number) => void;
  onWallLightDir: (x: number, y: number) => void;
  onWallRegenerate: (params: WallParams) => void;
  onWallShadowRes: (size: number) => void;
  onSimpleMode: (on: boolean) => void;
  onCameraEnabled: (on: boolean) => void;
  onTouchPlayToggle: () => void;
  onCreateRoom: () => void;
  onLeaveRoom: () => void;
  onBrowsePublic: () => void;
  onShowTutorial: () => void;
  onStopOnBlur: (on: boolean) => void;
  onPaletteDragStart: (tool: Tool, ev: PointerEvent) => void;
  onSubmenuToolDragStart: (tool: Tool, ev: PointerEvent) => void;
  onNodeMenuDragStart: (ev: PointerEvent, wasOpen: boolean) => void;
  onCopy: () => void;
  onPasteDragStart: (ev: PointerEvent) => void;
  onRadialPlaceTool: (tool: Tool, clientX: number, clientY: number) => void;
  onRadialPaste: (clientX: number, clientY: number) => void;
  onRadialSuspendControls: () => void;
  onRadialResumeControls: () => void;
  onExportVideo: (cycles: number, onProgress: (text: string) => void) => Promise<string | null>;
  onCycleSec: () => number;
  onSaveSlot: (name: string) => string | null;
  onExportScene: () => string;
  onLoadSlot: (scene: unknown) => string | null;
  onResetScene: () => void;
  onApplySceneInPlace: (scene: unknown) => string | null;
  onFeedSettle: (scene: unknown, meta: SceneListItem) => Promise<void>;
  onPrefetchMic: (scene: unknown, owner: string) => void;
  onCommunityEnter: () => void;
  onCommunityExit: () => void;
  onRecordSimPause: (open: boolean) => void;
  onCommunityKeepScene: (scene: unknown, meta: SceneListItem) => void;
  onSetFeedViewport: (el: HTMLElement | null) => void;
  onCaptureFeedPreview: (id: string) => Promise<string | null>;
  onFetchScene: (id: string) => Promise<{ scene: unknown } | { error: string }>;
  onQueuePlay: (id: string) => void;
  onFlushPlays: () => void;
  onGetSlots: () => SlotEntry[];
  onDeleteSlot: (name: string) => void;
  onCaptureThumb: (cb: (dataUrl: string) => void) => void;
  onCaptureUploadPreview: () => Promise<string>;
  shareEnabled: () => boolean;
  onUploadScene: (title: string) => Promise<UploadResult>;
  onUploadSummary: () => UploadFieldSummary;
  onListScenes: (q: SceneQuery) => Promise<{ items: SceneListItem[] } | { error: string }>;
  onApplySceneTagOps: (sceneId: string, ops: SceneTagOps) => Promise<TagMutationResult>;
  onReportScene: (id: string, reason: string) => Promise<string | null>;
  onDeleteScene: (id: string) => Promise<string | null>;
  onCurrentUid: () => Promise<string | null>;
  onSignInWithGoogle: () => Promise<string | null>;
  onSignOut: () => Promise<void>;
  onAuthStatus: () => Promise<AuthStatus>;
  onIsRealAccount: () => Promise<boolean>;
  onSetDisplayName: (name: string) => Promise<string | null>;
  onSubmitFeedback: (body: string, includeScene: boolean) => Promise<string | null>;
  onListMicAssets: () => Promise<{ items: MicAsset[] } | { error: string }>;
  onDeleteMicAsset: (hash: string) => Promise<string | null>;
  onLikeScene: (id: string) => Promise<string | null>;
  onUnlikeScene: (id: string) => Promise<string | null>;
  onHasLiked: (id: string) => Promise<boolean>;
  onLikedSceneIds: (ids: string[]) => Promise<string[]>;
  onBookmarkedSceneIds: (ids: string[]) => Promise<string[]>;
  onAddBookmark: (sceneId: string, categoryId: string | null) => Promise<string | null>;
  onRemoveBookmark: (sceneId: string) => Promise<string | null>;
  onSetBookmarkCategory: (sceneId: string, categoryId: string | null) => Promise<string | null>;
  onListBookmarks: () => Promise<{ items: BookmarkItem[] } | { error: string }>;
  onListCategories: () => Promise<{ items: BookmarkCategory[] } | { error: string }>;
  onAddCategory: (name: string) => Promise<string | null>;
  onRenameCategory: (id: string, name: string) => Promise<string | null>;
  onDeleteCategory: (id: string) => Promise<string | null>;
}

/**
 * Set while a Community modal is open to its account-search setter, so an author
 * name clicked inside a scene modal can drive the already-open search instead of
 * stacking a second modal. Null when no Community modal is open.
 */
let activeCommunitySearch: ((account: string) => void) | null = null;

/**
 * Set by setupToolbar to the top-left back button's show/hide, and by the open
 * Community modal to its close(). Together they let the back button that lives
 * in the top-controls row (built in a different scope) drive the feed close.
 */
let setCommunityBackVisible: ((visible: boolean) => void) | null = null;
let activeCommunityClose: (() => void) | null = null;

function shareUrlFor(id: string): string {
  return `${location.origin}${location.pathname}?scene=${id}`;
}

/** Formats a video length in seconds as `m:ss` from one minute up, else `N.Ns`. */
function formatVideoLength(sec: number): string {
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  }
  return `${sec.toFixed(1)}s`;
}

/**
 * Slider value formatter that shows up to `maxDecimals` places but trims
 * trailing zeros and a bare decimal point, so an integer-valued slider reads as
 * a plain integer while a typed fractional value keeps its decimals (120 →
 * "120", 120.5 → "120.5", 120.50 → "120.5"). Lets the number field accept free
 * decimals without forcing every display to a fixed width.
 */
function decimalFmt(maxDecimals: number, suffix: string = ""): (v: number) => string {
  return (v: number): string => {
    const fixed = v.toFixed(maxDecimals);
    const trimmed = fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
    return `${trimmed}${suffix}`;
  };
}

type GalleryItem =
  | { source: "local"; name: string; thumb: string | null; scene: unknown }
  | {
      source: "uploaded";
      id: string;
      owner: string;
      ownerName: string | null;
      title: string;
      playCount: number;
      likeCount: number;
      bookmarkCount: number;
      mine: boolean;
    };

type GalleryMenuAction = { label: string; onClick: (item: GalleryItem, anchor: HTMLElement) => void };

function galleryCard(item: GalleryItem, onOpen: (item: GalleryItem) => void, menuActions: GalleryMenuAction[]): HTMLDivElement {
  const card = document.createElement("div");
  card.className = "scene-card";
  const img = document.createElement("img");
  img.loading = "lazy";
  const cap = document.createElement("div");
  cap.className = "scene-card-cap";
  const titleSpan = document.createElement("span");
  titleSpan.className = "scene-card-title";
  if (item.source === "local") {
    if (item.thumb !== null) {
      img.src = item.thumb;
    }
    titleSpan.textContent = item.name;
    cap.appendChild(titleSpan);
  } else {
    const localThumb = loadBookmarkThumb(item.id);
    if (localThumb !== null) {
      img.src = localThumb;
    }
    titleSpan.textContent = item.title;
    const meta = document.createElement("span");
    meta.className = "scene-card-meta";
    meta.innerHTML = `▶${item.playCount} · ♥${item.likeCount} · ${bookmarkGlyph(false)}${item.bookmarkCount}`;
    cap.append(titleSpan, meta);
  }
  card.append(img, cap);
  card.addEventListener("click", (): void => onOpen(item));

  if (menuActions.length > 0) {
    const menu = document.createElement("div");
    menu.className = "scene-card-menu";
    const menuBtn = document.createElement("button");
    menuBtn.className = "scene-card-menu-btn";
    menuBtn.textContent = "⋮";
    menuBtn.setAttribute("aria-label", t("gallery.more"));
    const menuPop = document.createElement("div");
    menuPop.className = "scene-card-menu-pop";
    function dismiss(): void {
      menu.classList.remove("open");
      document.removeEventListener("pointerdown", onOutside, true);
    }
    function onOutside(ev: PointerEvent): void {
      if (ev.target instanceof Node && !menu.contains(ev.target)) {
        dismiss();
      }
    }
    for (const action of menuActions) {
      const b = document.createElement("button");
      b.textContent = action.label;
      b.addEventListener("click", (ev: MouseEvent): void => {
        ev.stopPropagation();
        dismiss();
        action.onClick(item, menuBtn);
      });
      menuPop.appendChild(b);
    }
    menuBtn.addEventListener("click", (ev: MouseEvent): void => {
      ev.stopPropagation();
      if (menu.classList.contains("open")) {
        dismiss();
        return;
      }
      menu.classList.add("open");
      setTimeout((): void => document.addEventListener("pointerdown", onOutside, true), 0);
    });
    menu.append(menuBtn, menuPop);
    card.appendChild(menu);
  }
  return card;
}

function renderGalleryGrid(grid: HTMLDivElement, items: GalleryItem[], onOpen: (item: GalleryItem) => void, menuActions: GalleryMenuAction[]): void {
  grid.textContent = "";
  for (const item of items) {
    grid.appendChild(galleryCard(item, onOpen, menuActions));
  }
}

function localGridItems(handlers: ToolbarHandlers): GalleryItem[] {
  return [...handlers.onGetSlots()]
    .sort((a: SlotEntry, b: SlotEntry): number => b.savedAt.localeCompare(a.savedAt))
    .map((s: SlotEntry): GalleryItem => ({ source: "local", name: s.name, thumb: s.thumb ?? null, scene: s.scene }));
}

async function uploadedGridItems(handlers: ToolbarHandlers): Promise<GalleryItem[]> {
  if (!handlers.shareEnabled()) {
    return [];
  }
  const res = await handlers.onListScenes({ mine: true, sort: "play_count" });
  if ("error" in res) {
    return [];
  }
  return res.items.map((i: SceneListItem): GalleryItem => ({
    source: "uploaded",
    id: i.id,
    owner: i.owner,
    ownerName: i.ownerName,
    title: i.title,
    playCount: i.playCount,
    likeCount: i.likeCount,
    bookmarkCount: i.bookmarkCount,
    mine: true,
  }));
}

function bookmarkGlyph(filled: boolean): string {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="${filled ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M6 4 H18 V21 L12 16 L6 21 Z"/></svg>`;
}

/** A simple padlock: a rounded-rectangle body plus an arc shackle, closed when locked and open (right leg lifted) when unlocked. White via currentColor. */
function lockGlyph(locked: boolean): string {
  const shackle = locked ? "M8 10 V7 a4 4 0 0 1 8 0 V10" : "M8 10 V7 a4 4 0 0 1 8 0";
  return `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2.5" fill="currentColor"/><path d="${shackle}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
}

const LONG_PRESS_MS = 450;
const PANEL_VIEWPORT_MARGIN = 8;

/**
 * Fires onLongPress after a press is held in place; a press that lifts early or
 * moves past a small slop is treated as a tap (timer cancelled). `wasLongPress`
 * stays true until the next press starts so the click that follows a long press
 * can be suppressed by the caller.
 */
function attachLongPress(el: HTMLElement, onLongPress: () => void): { wasLongPress: () => boolean } {
  let timer: number | undefined;
  let longPressed = false;
  let startX = 0;
  let startY = 0;
  const clear = (): void => {
    window.clearTimeout(timer);
    timer = undefined;
  };
  el.addEventListener("pointerdown", (ev: PointerEvent): void => {
    longPressed = false;
    startX = ev.clientX;
    startY = ev.clientY;
    timer = window.setTimeout((): void => {
      longPressed = true;
      onLongPress();
    }, LONG_PRESS_MS);
  });
  el.addEventListener("pointermove", (ev: PointerEvent): void => {
    if (timer !== undefined && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 8) {
      clear();
    }
  });
  el.addEventListener("pointerup", clear);
  el.addEventListener("pointercancel", clear);
  el.addEventListener("pointerleave", clear);
  el.addEventListener("contextmenu", (ev: Event): void => ev.preventDefault());
  return { wasLongPress: (): boolean => longPressed };
}

/**
 * Adds a category and resolves its new id by re-reading the list, because
 * addCategory only reports an error string. The newest row with the entered name
 * is the one just created (categories list ascending by created_at).
 */
async function createCategory(handlers: ToolbarHandlers, name: string): Promise<string | null> {
  const err = await handlers.onAddCategory(name);
  if (err !== null) {
    return null;
  }
  const res = await handlers.onListCategories();
  if ("error" in res) {
    return null;
  }
  const trimmed = name.trim();
  const matches = res.items.filter((c: BookmarkCategory): boolean => c.name === trimmed);
  return matches.length === 0 ? null : matches[matches.length - 1].id;
}

/**
 * Creates an empty floating menu anchored to an element (above it when the
 * anchor sits in the lower viewport half, else below) and wires dismiss-on-
 * outside-pointerdown observed in capture so canvas / feed-scroll gestures are
 * not consumed. Caller fills the returned pop and may dismiss() it.
 */
function openFloatingMenu(anchor: HTMLElement): { pop: HTMLDivElement; dismiss: () => void } {
  document.querySelector(".bookmark-cat-pop")?.remove();
  const pop = document.createElement("div");
  pop.className = "bookmark-cat-pop";
  const r = anchor.getBoundingClientRect();
  pop.style.left = `${Math.round(r.left)}px`;
  if (r.top > window.innerHeight / 2) {
    pop.style.bottom = `${Math.round(window.innerHeight - r.top + 4)}px`;
  } else {
    pop.style.top = `${Math.round(r.bottom + 4)}px`;
  }
  function dismiss(): void {
    pop.remove();
    document.removeEventListener("pointerdown", onOutside, true);
  }
  function onOutside(ev: PointerEvent): void {
    if (ev.target instanceof Node && !pop.contains(ev.target)) {
      dismiss();
    }
  }
  document.body.appendChild(pop);
  setTimeout((): void => document.addEventListener("pointerdown", onOutside, true), 0);
  return { pop, dismiss };
}

/**
 * Small floating picker of bookmark categories (Uncategorized + the user's
 * categories with counts + a "New category…" inline-create row).
 */
function openCategoryPopover(anchor: HTMLElement, handlers: ToolbarHandlers, onPick: (categoryId: string | null) => void): void {
  const { pop, dismiss } = openFloatingMenu(anchor);

  function addOption(label: string, categoryId: string | null): void {
    const opt = document.createElement("button");
    opt.className = "bookmark-cat-opt";
    opt.textContent = label;
    opt.addEventListener("click", (ev: MouseEvent): void => {
      ev.stopPropagation();
      dismiss();
      onPick(categoryId);
    });
    pop.appendChild(opt);
  }

  void Promise.all([handlers.onListCategories(), handlers.onListBookmarks()]).then(
    ([catRes, bmRes]: [{ items: BookmarkCategory[] } | { error: string }, { items: BookmarkItem[] } | { error: string }]): void => {
      const counts = new Map<string | null, number>();
      if (!("error" in bmRes)) {
        for (const b of bmRes.items) {
          counts.set(b.categoryId, (counts.get(b.categoryId) ?? 0) + 1);
        }
      }
      addOption(t("community.categoryUncategorized", { count: counts.get(null) ?? 0 }), null);
      if (!("error" in catRes)) {
        for (const c of catRes.items) {
          addOption(`${c.name} (${counts.get(c.id) ?? 0})`, c.id);
        }
      }
      const newRow = document.createElement("button");
      newRow.className = "bookmark-cat-opt bookmark-cat-new";
      newRow.textContent = t("community.categoryNew");
      newRow.addEventListener("click", (ev: MouseEvent): void => {
        ev.stopPropagation();
        const input = document.createElement("input");
        input.type = "text";
        input.className = "text-input bookmark-cat-input";
        input.placeholder = t("community.categoryNamePlaceholder");
        let done = false;
        const commit = async (): Promise<void> => {
          if (done) {
            return;
          }
          const name = input.value.trim();
          if (name === "") {
            done = true;
            dismiss();
            return;
          }
          done = true;
          input.disabled = true;
          const id = await createCategory(handlers, name);
          dismiss();
          onPick(id);
        };
        input.addEventListener("keydown", (ke: KeyboardEvent): void => {
          if (ke.key === "Enter") {
            void commit();
          }
        });
        input.addEventListener("blur", (): void => void commit());
        newRow.replaceWith(input);
        input.focus();
      });
      pop.appendChild(newRow);
    },
  );
}

/**
 * A bookmark toggle reused on feed cards and the opened-scene overlay. Adding
 * opens the category picker first (default Uncategorized); removing is a direct toggle.
 * Bookmark writes auto-create an anonymous session like Like, so no signed-in
 * gate is applied here.
 */
function makeBookmarkButton(
  handlers: ToolbarHandlers,
  sceneId: string,
  initialBookmarked: boolean,
  onStatus: (msg: string) => void,
  extraClass: string = "",
  initialCount?: number,
  onChange?: (sceneId: string, bookmarked: boolean) => void,
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = `bookmark-btn ${extraClass}`.trim();
  let bookmarked = initialBookmarked;
  let count = initialCount;
  const icon = document.createElement("span");
  icon.className = "bookmark-btn-icon";
  btn.appendChild(icon);
  const countSpan = count === undefined ? null : document.createElement("span");
  if (countSpan !== null) {
    countSpan.className = "bookmark-btn-count";
    btn.appendChild(countSpan);
  }
  const sync = (): void => {
    icon.innerHTML = bookmarkGlyph(bookmarked);
    if (countSpan !== null && count !== undefined) {
      countSpan.textContent = ` ${count}`;
    }
    btn.classList.toggle("bookmarked", bookmarked);
    btn.setAttribute("aria-label", bookmarked ? t("bookmark.remove") : t("bookmark.add"));
  };
  sync();
  btn.addEventListener("click", (ev: MouseEvent): void => {
    ev.stopPropagation();
    if (bookmarked) {
      btn.disabled = true;
      void handlers.onRemoveBookmark(sceneId).then((err: string | null): void => {
        btn.disabled = false;
        if (err !== null) {
          onStatus(err);
          return;
        }
        if (count !== undefined) {
          count = Math.max(0, count - 1);
        }
        bookmarked = false;
        sync();
        onChange?.(sceneId, false);
      });
      return;
    }
    openCategoryPopover(btn, handlers, (categoryId: string | null): void => {
      btn.disabled = true;
      void handlers.onAddBookmark(sceneId, categoryId).then((err: string | null): void => {
        btn.disabled = false;
        if (err !== null) {
          onStatus(err);
          return;
        }
        if (count !== undefined) {
          count += 1;
        }
        bookmarked = true;
        sync();
        onChange?.(sceneId, true);
      });
    });
  });
  return btn;
}

type FeedItem = {
  id: string;
  owner: string;
  ownerName: string | null;
  title: string;
  playCount: number;
  likeCount: number;
  bookmarkCount: number;
  status: string;
  tags: string[];
  autoTags: string[];
  lockedTags: string[];
  mine: boolean;
};

const REPORT_REASONS = ["rights", "sexual", "hate", "spam", "other"] as const;
type ReportReason = (typeof REPORT_REASONS)[number];

function openCommunityModal(handlers: ToolbarHandlers, initialAccount: string = ""): void {
  track("community_open");
  openFeed<FeedItem>((api: FeedCoreApi<FeedItem>): FeedConsumer<FeedItem> =>
    communityFeedConsumer(handlers, initialAccount, api),
  );
}

/**
 * Community (scenes) as the first consumer of the feed core: it supplies the
 * scene query + search controls, the like/play/bookmark/tags/author card info,
 * the index rows, scene fetch + keep, report, and the author mic-kit settle.
 * The play count is bumped once per scene per session when a focus dwells past a
 * threshold; the author's mic kit (Layer B) plays once a card comes fully to
 * rest. Tag edits are queued optimistically and flushed as one batched diff RPC
 * per touched scene when the feed closes.
 */
function communityFeedConsumer(
  handlers: ToolbarHandlers,
  initialAccount: string,
  api: FeedCoreApi<FeedItem>,
): FeedConsumer<FeedItem> {
  /** Wraps a search input with a trailing ✕ that clears it and reruns the query; the button hides itself via CSS while the field is empty (:placeholder-shown). */
  function clearable(input: HTMLInputElement, onClear: () => void): HTMLDivElement {
    const wrap = document.createElement("div");
    wrap.className = "input-clearable";
    const clearBtn = document.createElement("button");
    clearBtn.className = "input-clear-btn";
    clearBtn.textContent = "✕";
    clearBtn.setAttribute("aria-label", t("community.clearInput"));
    clearBtn.addEventListener("click", (): void => {
      input.value = "";
      input.focus();
      onClear();
    });
    wrap.append(input, clearBtn);
    return wrap;
  }

  const searchRow = document.createElement("div");
  searchRow.className = "row gallery-search";
  const textInput = document.createElement("input");
  textInput.type = "text";
  textInput.className = "text-input";
  textInput.placeholder = t("community.searchTitle");
  const accountInput = document.createElement("input");
  accountInput.type = "text";
  accountInput.className = "text-input";
  accountInput.placeholder = t("community.searchAuthor");
  accountInput.value = initialAccount;
  const sortSelect = document.createElement("select");
  for (const [value, key] of [["created_at", "community.sortNewest"], ["play_count", "community.sortMostPlayed"], ["like_count", "community.sortMostLiked"]] as const) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = t(key);
    sortSelect.appendChild(opt);
  }
  sortSelect.value = "like_count";
  const periodSelect = document.createElement("select");
  for (const [value, key] of [["", "community.periodAny"], ["1", "community.periodDay"], ["7", "community.periodWeek"], ["30", "community.periodMonth"], ["365", "community.periodYear"]] as const) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = t(key);
    periodSelect.appendChild(opt);
  }
  periodSelect.value = "";
  searchRow.append(clearable(textInput, (): void => void api.reload()), clearable(accountInput, (): void => void api.reload()), sortSelect, periodSelect);

  const tagSearchInput = document.createElement("input");
  tagSearchInput.type = "text";
  tagSearchInput.className = "text-input";
  tagSearchInput.placeholder = t("community.tagSearch");

  const micSelect = document.createElement("select");
  micSelect.className = "feed-mic-filter";
  for (const [value, key] of [["both", "community.filterBoth"], ["include", "community.filterInclude"], ["exclude", "community.filterExclude"]] as const) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = t(key);
    micSelect.appendChild(opt);
  }
  micSelect.value = "exclude";
  micSelect.addEventListener("change", (): void => void api.reload());

  const filterRow = document.createElement("div");
  filterRow.className = "row gallery-search feed-tag-filter";
  filterRow.append(clearable(tagSearchInput, (): void => void api.reload()), micSelect);
  const searchStack = document.createElement("div");
  searchStack.className = "feed-search-stack";
  searchStack.append(searchRow, filterRow);

  let likedSet = new Set<string>();
  let bookmarkedSet = new Set<string>();
  let canEditTags = false;
  const tagEdits = new Map<string, { orig: { tags: string[]; locked: string[] }; cur: { tags: string[]; locked: string[] } }>();

  let searchTimer: number | undefined;
  const debouncedLoad = (): void => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout((): void => {
      void api.reload();
    }, 250);
  };
  textInput.addEventListener("input", debouncedLoad);
  accountInput.addEventListener("input", debouncedLoad);
  tagSearchInput.addEventListener("input", debouncedLoad);
  sortSelect.addEventListener("change", (): void => void api.reload());
  periodSelect.addEventListener("change", (): void => void api.reload());

  const searchByAuthor = (account: string): void => {
    textInput.value = "";
    accountInput.value = account;
    void api.reload();
  };
  const searchByTag = (tag: string): void => {
    tagSearchInput.value = tag;
    void api.reload();
  };

  /**
   * Flushes the session's queued tag edits as one batched diff RPC per touched
   * scene (fire-and-forget, like play counts): edits are applied optimistically
   * in the UI while the feed is open and only written when it closes, so a flurry
   * of add/remove/lock toggles costs one request per scene instead of one each.
   */
  function flushTagEdits(): void {
    for (const [sceneId, e] of tagEdits) {
      const adds = e.cur.tags.filter((x: string): boolean => !e.orig.tags.includes(x));
      const removes = e.orig.tags.filter((x: string): boolean => !e.cur.tags.includes(x));
      const locks = e.cur.locked.filter((x: string): boolean => !e.orig.locked.includes(x));
      const unlocks = e.orig.locked.filter((x: string): boolean => !e.cur.locked.includes(x));
      if (adds.length > 0 || removes.length > 0 || locks.length > 0 || unlocks.length > 0) {
        void handlers.onApplySceneTagOps(sceneId, { adds, removes, locks, unlocks });
      }
    }
    tagEdits.clear();
  }

  function feedItemToListItem(item: FeedItem): SceneListItem {
    return {
      id: item.id,
      owner: item.owner,
      ownerName: item.ownerName,
      title: item.title,
      playCount: item.playCount,
      likeCount: item.likeCount,
      bookmarkCount: item.bookmarkCount,
      status: item.status,
      tags: item.tags,
      autoTags: item.autoTags,
      lockedTags: item.lockedTags,
    };
  }

  async function submitReport(item: FeedItem, reason: string): Promise<void> {
    const err = await handlers.onReportScene(item.id, reason);
    if (err !== null) {
      api.setStatus(err);
      return;
    }
    hideReported(item.id);
    await api.reload();
    api.setToast(t("community.reportedHidden"));
  }

  /**
   * Warms the immediate neighbours' author mic kits into the Layer B cache (no
   * activation, no sim freeze) so scrolling onto them plays instantly with no
   * spinner. Driven from settle, not every focus change, so a fast scroll starts
   * no downloads. Each kit resolves once — micStore dedupes already-cached hashes.
   */
  function prefetchNeighbourMic(id: string): void {
    for (const nid of api.neighbourIds(id)) {
      const item = api.getItem(nid);
      if (item === undefined) {
        continue;
      }
      void api.prefetchScene(nid).then((scene: unknown | null): void => {
        if (api.isClosed() || scene === null) {
          return;
        }
        const manifest = (scene as { micManifest?: unknown }).micManifest;
        if (Array.isArray(manifest) && manifest.length > 0) {
          handlers.onPrefetchMic(scene, item.owner);
        }
      });
    }
  }

  function buildCardInfo(item: FeedItem): HTMLElement {
    const info = document.createElement("div");
    info.className = "feed-card-info";
    const main = document.createElement("div");
    main.className = "feed-card-info-main";
    const titleSpan = document.createElement("div");
    titleSpan.className = "feed-card-title";
    titleSpan.textContent = item.title;

    const meta = document.createElement("div");
    meta.className = "feed-card-meta";
    const plays = document.createElement("span");
    plays.className = "feed-card-plays";
    plays.textContent = `▶ ${item.playCount}`;
    const likeBtn = document.createElement("button");
    likeBtn.className = "feed-card-like";
    const heart = document.createElement("span");
    heart.className = "feed-card-heart";
    const likeCountSpan = document.createElement("span");
    likeBtn.append(heart, likeCountSpan);
    let liked = likedSet.has(item.id);
    let count = item.likeCount;
    const syncLike = (): void => {
      heart.textContent = liked ? "♥" : "♡";
      likeCountSpan.textContent = ` ${count}`;
      likeBtn.classList.toggle("liked", liked);
    };
    syncLike();
    if (item.mine) {
      likeBtn.disabled = true;
    } else {
      likeBtn.addEventListener("click", async (): Promise<void> => {
        likeBtn.disabled = true;
        const err = liked ? await handlers.onUnlikeScene(item.id) : await handlers.onLikeScene(item.id);
        likeBtn.disabled = false;
        if (err !== null) {
          api.setStatus(err);
          return;
        }
        count += liked ? -1 : 1;
        liked = !liked;
        syncLike();
      });
    }
    const bookmarkBtn = makeBookmarkButton(
      handlers,
      item.id,
      bookmarkedSet.has(item.id),
      (msg: string): void => {
        api.setStatus(msg);
      },
      "feed-card-bookmark",
      item.bookmarkCount,
      (sceneId: string, bookmarked: boolean): void => {
        if (!bookmarked) {
          bookmarkedSet.delete(sceneId);
          return;
        }
        bookmarkedSet.add(sceneId);
        const preview = api.getPreview(sceneId);
        if (preview !== undefined) {
          saveBookmarkThumb(sceneId, preview);
        }
      },
    );
    meta.append(plays, likeBtn, bookmarkBtn);
    for (const tag of item.autoTags) {
      const chip = document.createElement("span");
      chip.className = "tag-chip tag-chip-fixed";
      chip.textContent = tag;
      attachTooltip(chip, (): string => t("community.fixedTagHint"));
      meta.appendChild(chip);
    }
    const author = document.createElement("button");
    author.className = "scene-modal-author feed-card-author";
    author.textContent = item.ownerName ?? t("community.unknownAuthor");
    author.addEventListener("click", (): void => {
      if (item.ownerName !== null) {
        searchByAuthor(item.ownerName);
      }
    });
    const titleRow = document.createElement("div");
    titleRow.className = "feed-card-titlerow";
    titleRow.append(titleSpan, author);
    main.append(titleRow, meta);

    const tagsWrap = document.createElement("div");
    tagsWrap.className = "feed-card-tags";

    const userWrap = document.createElement("div");
    userWrap.className = "tag-editor";

    let tags = [...item.tags];
    let locked = [...item.lockedTags];

    const queueTagEdit = (): void => {
      const existing = tagEdits.get(item.id);
      const orig = existing?.orig ?? { tags: [...item.tags], locked: [...item.lockedTags] };
      tagEdits.set(item.id, { orig, cur: { tags: [...tags], locked: [...locked] } });
    };

    let tagInput: HTMLInputElement | null = null;
    if (canEditTags) {
      const input = document.createElement("input");
      input.type = "text";
      input.className = "text-input tag-editor-input";
      input.placeholder = t("community.tagAdd");
      input.maxLength = TAG_MAX_LEN;
      input.addEventListener("keydown", (ev: KeyboardEvent): void => {
        if (ev.key !== "Enter" && ev.key !== ",") {
          return;
        }
        ev.preventDefault();
        const clean = sanitizeTags([input.value])[0];
        input.value = "";
        if (clean === undefined || tags.includes(clean) || tags.length >= TAG_MAX_COUNT) {
          return;
        }
        tags = [...tags, clean];
        queueTagEdit();
        renderTagChips();
      });
      tagInput = input;
    }

    const toggleLock = (tag: string, isLocked: boolean): void => {
      locked = isLocked ? locked.filter((x: string): boolean => x !== tag) : [...locked, tag];
      queueTagEdit();
      renderTagChips();
    };
    const removeTag = (tag: string): void => {
      tags = tags.filter((x: string): boolean => x !== tag);
      locked = locked.filter((x: string): boolean => x !== tag);
      queueTagEdit();
      renderTagChips();
    };
    /**
     * Long-press action menu for a tag chip: an owner gets lock/unlock + delete,
     * a non-owner gets delete only on unlocked tags. When no action is permitted
     * (non-owner on a locked tag, or not signed in) the menu does not open.
     */
    const openTagActions = (anchor: HTMLElement, tag: string, isLocked: boolean): void => {
      const canToggle = item.mine;
      const canDelete = item.mine || (canEditTags && !isLocked);
      if (!canToggle && !canDelete) {
        return;
      }
      const { pop, dismiss } = openFloatingMenu(anchor);
      if (canToggle) {
        const lockOpt = document.createElement("button");
        lockOpt.className = "bookmark-cat-opt";
        lockOpt.textContent = isLocked ? t("community.tagUnlock") : t("community.tagLock");
        lockOpt.addEventListener("click", (ev: MouseEvent): void => {
          ev.stopPropagation();
          dismiss();
          toggleLock(tag, isLocked);
        });
        pop.appendChild(lockOpt);
      }
      if (canDelete) {
        const delOpt = document.createElement("button");
        delOpt.className = "bookmark-cat-opt tag-action-delete";
        delOpt.textContent = t("community.tagRemove");
        delOpt.addEventListener("click", (ev: MouseEvent): void => {
          ev.stopPropagation();
          dismiss();
          removeTag(tag);
        });
        pop.appendChild(delOpt);
      }
    };

    function renderTagChips(): void {
      for (const el of Array.from(userWrap.querySelectorAll(".tag-chip-user"))) {
        el.remove();
      }
      const frag = document.createDocumentFragment();
      for (const tag of tags) {
        const isLocked = locked.includes(tag);
        const chip = document.createElement("span");
        chip.className = isLocked ? "tag-chip tag-chip-user tag-chip-locked" : "tag-chip tag-chip-user";
        const label = document.createElement("button");
        label.className = "tag-chip-label tag-chip-search";
        label.textContent = tag;
        const lp = attachLongPress(chip, (): void => openTagActions(chip, tag, isLocked));
        label.addEventListener("click", (): void => {
          if (lp.wasLongPress()) {
            return;
          }
          searchByTag(tag);
        });
        chip.appendChild(label);
        if (isLocked) {
          const lockIcon = document.createElement("span");
          lockIcon.className = "tag-chip-lockicon";
          lockIcon.innerHTML = lockGlyph(true);
          attachTooltip(lockIcon, (): string => t("community.tagLockedHint"));
          chip.appendChild(lockIcon);
        }
        frag.appendChild(chip);
      }
      if (tagInput !== null) {
        userWrap.insertBefore(frag, tagInput);
        tagInput.style.display = tags.length >= TAG_MAX_COUNT ? "none" : "";
      } else {
        userWrap.appendChild(frag);
      }
    }
    if (tagInput !== null) {
      userWrap.appendChild(tagInput);
    }
    renderTagChips();
    tagsWrap.appendChild(userWrap);

    main.appendChild(tagsWrap);
    info.append(main);
    return info;
  }

  function buildCardMenu(item: FeedItem): HTMLElement | null {
    if (item.mine) {
      return null;
    }
    const menuEl = document.createElement("div");
    menuEl.className = "feed-card-menu";
    const menuBtn = document.createElement("button");
    menuBtn.className = "feed-card-menu-btn";
    menuBtn.textContent = "⋮";
    menuBtn.setAttribute("aria-label", t("gallery.more"));
    const menuPop = document.createElement("div");
    menuPop.className = "feed-card-menu-pop";
    const reportAction = document.createElement("button");
    reportAction.textContent = t("community.report");
    reportAction.addEventListener("click", (): void => {
      menuEl.classList.remove("open");
      showActionModal(
        t("report.title"),
        null,
        REPORT_REASONS.map((r: ReportReason): ModalAction<string> => ({ label: t(`report.reason_${r}`), value: r })),
        (value: string): void => {
          void submitReport(item, value);
        },
        { vertical: true, dismissable: { label: t("report.cancel") } },
      );
    });
    menuPop.appendChild(reportAction);
    menuBtn.addEventListener("click", (ev: MouseEvent): void => {
      ev.stopPropagation();
      const wasOpen = menuEl.classList.contains("open");
      api.closeCardMenus();
      menuEl.classList.toggle("open", !wasOpen);
    });
    menuEl.append(menuBtn, menuPop);
    return menuEl;
  }

  function buildIndexRow(item: FeedItem): HTMLButtonElement {
    const row = document.createElement("button");
    row.className = "feed-index-row";
    row.dataset.sceneId = item.id;
    const title = document.createElement("span");
    title.className = "feed-index-row-title";
    title.textContent = item.title;
    const rowAuthor = document.createElement("span");
    rowAuthor.className = "feed-index-row-meta";
    rowAuthor.textContent = item.ownerName ?? t("community.unknownAuthor");
    const rowStats = document.createElement("span");
    rowStats.className = "feed-index-row-stats";
    rowStats.innerHTML = `▶${item.playCount} · ♥${item.likeCount} · ${bookmarkGlyph(false)}${item.bookmarkCount}`;
    row.append(title, rowAuthor, rowStats);
    row.addEventListener("click", (): void => {
      api.closeIndexDrawer();
      api.goToCard(item.id);
    });
    return row;
  }

  async function loadItems(): Promise<FeedLoadResult<FeedItem>> {
    const period = periodSelect.value;
    const res = await handlers.onListScenes({
      text: textInput.value,
      account: accountInput.value,
      sort: sortSelect.value as SceneSort,
      sinceDays: period === "" ? null : Number(period),
      tags: sanitizeTags(tagSearchInput.value.split(/[\s,]+/)),
      autoInclude: micSelect.value === "include" ? ["mic"] : [],
      autoExclude: micSelect.value === "exclude" ? ["mic"] : [],
    });
    if (api.isClosed()) {
      return { items: [] };
    }
    if ("error" in res) {
      return { error: res.error };
    }
    const hidden = hiddenReportedIds();
    const visible = res.items.filter((i: SceneListItem): boolean => !hidden.has(i.id));
    const [uid, auth] = await Promise.all([handlers.onCurrentUid(), handlers.onAuthStatus()]);
    canEditTags = auth.signedIn;
    if (api.isClosed()) {
      return { items: [] };
    }
    const items: FeedItem[] = visible.map((i: SceneListItem): FeedItem => {
      const pending = tagEdits.get(i.id);
      return {
        id: i.id,
        owner: i.owner,
        ownerName: i.ownerName,
        title: i.title,
        playCount: i.playCount,
        likeCount: i.likeCount,
        bookmarkCount: i.bookmarkCount,
        status: i.status,
        tags: pending !== undefined ? [...pending.cur.tags] : i.tags,
        autoTags: i.autoTags,
        lockedTags: pending !== undefined ? [...pending.cur.locked] : i.lockedTags,
        mine: uid !== null && i.owner === uid,
      };
    });
    const ids = items.map((i: FeedItem): string => i.id);
    const [liked, bookmarked] = await Promise.all([handlers.onLikedSceneIds(ids), handlers.onBookmarkedSceneIds(ids)]);
    likedSet = new Set(liked);
    bookmarkedSet = new Set(bookmarked);
    if (api.isClosed()) {
      return { items: [] };
    }
    return { items };
  }

  return {
    title: t("community.title"),
    chrome: { search: true, index: true, count: true },
    searchControls: searchStack,
    strings: {
      loading: t("common.loading"),
      empty: t("community.noScenes"),
      loadFailed: t("scene.loadFailed"),
      indexToggleAria: t("community.indexToggle"),
      indexCount: (n: number): string => t("community.indexCount", { count: n }),
    },
    onEnter: (): void => {
      handlers.onCommunityEnter();
      activeCommunityClose = api.close;
      setCommunityBackVisible?.(true);
      activeCommunitySearch = searchByAuthor;
    },
    onExit: (): void => {
      handlers.onCommunityExit();
    },
    onClose: (): void => {
      flushTagEdits();
      window.clearTimeout(searchTimer);
      handlers.onFlushPlays();
      activeCommunitySearch = null;
      activeCommunityClose = null;
      setCommunityBackVisible?.(false);
    },
    loadItems,
    fetchScene: (id: string): Promise<{ scene: unknown } | { error: string }> => handlers.onFetchScene(id),
    applySceneInPlace: (scene: unknown): string | null => handlers.onApplySceneInPlace(scene),
    setViewport: (stage: HTMLElement | null): void => handlers.onSetFeedViewport(stage),
    capturePreview: (id: string): Promise<string | null> => handlers.onCaptureFeedPreview(id),
    buildCardInfo,
    buildIndexRow,
    buildCardMenu,
    onPreviewCaptured: (id: string, dataUrl: string): void => {
      if (bookmarkedSet.has(id)) {
        saveBookmarkThumb(id, dataUrl);
      }
    },
    onDwell: (id: string): void => {
      handlers.onQueuePlay(id);
    },
    onSettle: (item: FeedItem, scene: unknown, spinner: FeedSpinner): void => {
      const id = item.id;
      const manifest = (scene as { micManifest?: unknown }).micManifest;
      const hasMic = Array.isArray(manifest) && manifest.length > 0;
      if (hasMic) {
        spinner.show(id);
      }
      void handlers.onFeedSettle(scene, feedItemToListItem(item)).then((): void => spinner.hide(id));
      prefetchNeighbourMic(id);
    },
    onCommit: (item: FeedItem, scene: unknown): void => {
      handlers.onCommunityKeepScene(scene, feedItemToListItem(item));
    },
  };
}

export function setupToolbar(
  init: Settings,
  emitterInit: EmitterParams,
  handlers: ToolbarHandlers,
  initialBloomMode: BloomMode = "half",
): {
  syncControls: (target: ContextTarget) => void;
  highlightTool: (tool: Tool | null) => void;
  setToolsEnabled: (enabled: boolean) => void;
  setGlobalPlaced: (placed: boolean) => void;
  applyLocks: (emitInDriven: boolean, driven: Map<string, number>) => void;
  applyGlobalParamLocks: (driven: Map<string, number>) => void;
  setUndoRedoEnabled: (canUndo: boolean, canRedo: boolean) => void;
  setCopyPasteEnabled: (canCopy: boolean, canPaste: boolean) => void;
  showPaletteHint: (tool: Tool) => void;
  nodeSubmenu: {
    toolAt: (x: number, y: number) => Tool | null;
    setHover: (tool: Tool | null) => void;
    close: () => void;
  };
  setSimpleChecked: (on: boolean) => void;
  setFeatureChecked: (cat: FeatureCategory, on: boolean) => void;
  setCameraState: (checked: boolean) => void;
  setTouchPlayToggleVisible: (visible: boolean) => void;
  setTouchPlayToggleActive: (on: boolean) => void;
  setDimensionToggleVisible: (visible: boolean) => void;
  setDimensionToggleActive: (on: boolean) => void;
  setViewMode: (mode: ViewMode) => void;
  setPlayModeHidden: (hidden: boolean) => void;
  syncSceneSettings: (clockPeriod: number, gravityY: number) => void;
  syncWall: (params: WallParams) => void;
  setOpenedScene: (meta: SceneListItem | null) => void;
  setRoomChip: (info: { name: string; ownerName: string | null; count: number; capacity: number | null } | null) => void;
  setForeignSceneLock: (blocked: boolean) => void;
  setCommunitySceneLoaded: (loaded: boolean) => void;
  setRoomGuestLock: (blocked: boolean) => void;
  setRoomModeLock: (locked: boolean) => void;
  setObjectPanelLocked: (locked: boolean) => void;
  setObjectPanelBottom: (bottom: boolean, clearancePx?: number) => void;
  setObjectPanelTopOffset: (px: number) => void;
  syncDebugRender: (bloom: BloomMode, dprCap1: boolean) => void;
  tutorialTargets: {
    sequencerBtn: () => HTMLElement | null;
    boxBtn: () => HTMLElement | null;
    scenePanelBtn: HTMLElement;
    sceneSettingsNavBtn: HTMLElement;
    featuresNavBtn: HTMLElement;
    communityNavBtn: () => HTMLElement | null;
    touchPlayBtn: HTMLElement;
    timbreRow: () => HTMLElement | null;
  };
  tutorialOpenScenePanelSection: (sectionTitle: string) => void;
  setHoverPanelsSuppressed: (suppressed: boolean) => void;
  setHoverLeaveDisabled: (disabled: boolean) => void;
  setMasterVolumeUnmuted: (v: number) => void;
  setRoomActive: (active: boolean) => void;
  isRadialMode: () => boolean;
  radial: RadialMenuApi;
  uiMin: boolean;
  uiView: boolean;
  uiNoSeq: boolean;
} {
  let orbitSync = false;
  let currentClockPeriod = init.clockPeriod;
  const topControlsBar = document.createElement("div");
  topControlsBar.className = "top-controls";

  const communityBackBtn = document.createElement("button");
  communityBackBtn.className = "icon-btn icon-btn-square community-back";
  communityBackBtn.textContent = "✕";
  communityBackBtn.setAttribute("aria-label", t("community.title"));
  attachTooltip(communityBackBtn, (): string => `${t("community.title")} : ${t("toolTip.communityBack")}`);
  communityBackBtn.style.display = "none";
  communityBackBtn.addEventListener("click", (): void => activeCommunityClose?.());
  topControlsBar.appendChild(communityBackBtn);
  setCommunityBackVisible = (visible: boolean): void => {
    communityBackBtn.style.display = visible ? "" : "none";
  };

  interface IconGroup {
    panel: HTMLDivElement;
    btn: HTMLButtonElement;
    resetNav?: () => void;
  }
  const iconGroups: IconGroup[] = [];

  /**
   * The one icon panel kept open after hover-out because its contents were used
   * (slider moved, tool picked) or its button was clicked. Switching hover to
   * another group clears it, so only the actively-engaged panel persists.
   */
  let pinnedPanel: IconGroup | null = null;
  let hoverPanelsSuppressed = false;
  let hoverLeaveDisabled = false;

  /**
   * Pulls a just-opened panel left so it never spills past the viewport's right
   * edge. The panel sits absolute at left:0 inside its relative .icon-group, so a
   * negative left offset shifts it back toward the bar. Reset to "" before
   * measuring so the previous offset never compounds across reopens.
   */
  function clampPanelHorizontal(panel: HTMLDivElement): void {
    panel.style.left = "";
    const overflow = panel.getBoundingClientRect().right - (window.innerWidth - PANEL_VIEWPORT_MARGIN);
    if (overflow > 0) {
      panel.style.left = `${-overflow}px`;
    }
  }

  function setExclusivePanel(open: IconGroup | null): void {
    for (const g of iconGroups) {
      const isOpen = g === open;
      g.panel.classList.toggle("open", isOpen);
      if (!isOpen) {
        g.panel.style.left = "";
      }
      if (g !== open && g.resetNav !== undefined) {
        g.resetNav();
      }
    }
    if (open !== null) {
      clampPanelHorizontal(open.panel);
    }
  }

  /**
   * `interceptClick` lets a group repurpose its button click (the ☰ button
   * becomes ← inside a detail section); returning true consumes the click,
   * false falls through to the default pin/unpin toggle. `iconClass` renders a
   * CSS-drawn glyph instead of the text label (the label stays as the
   * accessible name and the group identity).
   */
  function makeIconGroup(label: string, interceptClick?: () => boolean, iconClass?: string, suppressHover?: boolean, desc?: string): { hoverPanel: HTMLDivElement; btn: HTMLButtonElement; entry: IconGroup } {
    const group = document.createElement("div");
    group.className = "icon-group";
    const btn = document.createElement("button");
    btn.className = "icon-btn";
    if (iconClass) {
      const glyph = document.createElement("span");
      glyph.className = `icon-glyph ${iconClass}`;
      btn.appendChild(glyph);
      btn.classList.add("icon-btn-square");
      btn.setAttribute("aria-label", label);
      attachTooltip(btn, (): string => (desc !== undefined ? `${label} : ${desc}` : label));
    } else {
      btn.textContent = label;
    }
    const hoverPanel = document.createElement("div");
    hoverPanel.className = "panel hover-panel";
    group.append(btn, hoverPanel);
    topControlsBar.appendChild(group);
    const entry: IconGroup = { panel: hoverPanel, btn };
    iconGroups.push(entry);
    if (!suppressHover) {
      group.addEventListener("mouseenter", (): void => {
        if (hoverPanelsSuppressed) {
          return;
        }
        if (pinnedPanel !== entry) {
          pinnedPanel = null;
        }
        setExclusivePanel(entry);
      });
      group.addEventListener("mouseleave", (): void => {
        if (hoverLeaveDisabled || pinnedPanel === entry) {
          return;
        }
        setExclusivePanel(null);
      });
    }
    btn.addEventListener("click", (): void => {
      if (interceptClick && interceptClick()) {
        return;
      }
      if (pinnedPanel === entry) {
        pinnedPanel = null;
        setExclusivePanel(null);
      } else {
        pinnedPanel = entry;
        setExclusivePanel(entry);
      }
    });
    for (const type of ["input", "change", "click"]) {
      hoverPanel.addEventListener(type, (): void => {
        pinnedPanel = entry;
      });
    }
    return { hoverPanel, btn, entry };
  }

  /**
   * Closes a pinned icon panel when a pointerdown lands outside both the panel
   * and its toggle button. The event is only observed (never consumed) so canvas
   * drags / OrbitControls keep working; clicks on the button fall through to its
   * own toggle handler.
   */
  document.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (pinnedPanel === null) {
      return;
    }
    const target = ev.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (pinnedPanel.panel.contains(target) || pinnedPanel.btn.contains(target)) {
      return;
    }
    if (target instanceof Element && target.closest(".bookmark-cat-pop") !== null) {
      return;
    }
    pinnedPanel = null;
    setExclusivePanel(null);
  });

  /**
   * Collapses any text field's selection the moment it blurs. A panel hidden via
   * display:none blurs its focused input but leaves the selection range intact,
   * so a selection-based browser overlay (e.g. the DeepL button) stays pinned
   * over the closed field. One capture-phase listener covers every text field
   * (blur does not bubble), including dynamically-created ones. Guarded to the
   * input types that support setSelectionRange (number/range/etc. throw).
   */
  const TEXTUAL_INPUT_TYPES = new Set(["text", "search", "url", "tel", "password"]);
  document.addEventListener("blur", (ev: FocusEvent): void => {
    const el = ev.target;
    if (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXTUAL_INPUT_TYPES.has(el.type))) {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, true);

  const _uiTokens = (new URLSearchParams(location.search).get("ui") ?? "").split(",");
  const uiView = _uiTokens.includes("view");
  const uiMin = uiView || _uiTokens.includes("min");
  const uiNoSeq = _uiTokens.includes("noseq");
  /**
   * One drill-down panel behind a top-controls icon button: a root nav list whose
   * buttons reveal a top section (some sections nest a second grandchild nav). The
   * button doubles as the back control (☰/icon → ← while drilled in). Two of these
   * coexist — settings behind ☰, scene-related behind its own button — each owning
   * its own nav / scroll / sections / detail state so they never cross-drive.
   */
  interface NavPanel {
    hoverPanel: HTMLDivElement;
    scroll: HTMLDivElement;
    nav: HTMLDivElement;
    sections: HTMLDivElement[];
    navButtons: Map<string, HTMLButtonElement>;
    nestedBySection: Map<HTMLDivElement, { nav: HTMLDivElement; subs: HTMLDivElement[]; flat: boolean }>;
    btn: HTMLButtonElement;
    restoreRootLabel: () => void;
    detail: HTMLDivElement | null;
    subDetail: HTMLDivElement | null;
    showSection: (section: HTMLDivElement) => void;
    showNav: () => void;
    showSubSection: (sub: HTMLDivElement) => void;
    showNestedNav: () => void;
  }
  const navPanelByHover = new Map<HTMLElement, NavPanel>();

  function makeNavPanel(iconLabel: string, restoreRootLabel: () => void, iconClass?: string): NavPanel {
    const group = makeIconGroup(iconLabel, (): boolean => {
      if (panel.subDetail !== null) {
        panel.showNestedNav();
        return true;
      }
      if (panel.detail !== null) {
        panel.showNav();
        return true;
      }
      return false;
    }, iconClass);
    group.hoverPanel.classList.add("hover-panel-main");
    const scroll = document.createElement("div");
    scroll.className = "main-scroll";
    group.hoverPanel.appendChild(scroll);
    const nav = document.createElement("div");
    nav.className = "main-nav";
    scroll.appendChild(nav);
    group.btn.classList.add("icon-btn-square");
    if (uiMin) group.btn.style.display = "none";
    const panel: NavPanel = {
      hoverPanel: group.hoverPanel,
      scroll,
      nav,
      sections: [],
      navButtons: new Map<string, HTMLButtonElement>(),
      nestedBySection: new Map(),
      btn: group.btn,
      restoreRootLabel,
      detail: null,
      subDetail: null,
      showNestedNav: (): void => {
        const nested = panel.detail !== null ? panel.nestedBySection.get(panel.detail) : undefined;
        if (nested === undefined) {
          return;
        }
        for (const s of nested.subs) {
          s.style.display = "none";
        }
        nested.nav.style.display = "";
        panel.subDetail = null;
      },
      showSubSection: (sub: HTMLDivElement): void => {
        const nested = panel.detail !== null ? panel.nestedBySection.get(panel.detail) : undefined;
        if (nested === undefined) {
          return;
        }
        for (const s of nested.subs) {
          s.style.display = "none";
        }
        nested.nav.style.display = "none";
        sub.style.display = "";
        panel.subDetail = sub;
        panel.btn.textContent = "←";
      },
      showSection: (section: HTMLDivElement): void => {
        for (const s of panel.sections) {
          s.style.display = "none";
        }
        panel.nav.style.display = "none";
        section.style.display = "";
        const nested = panel.nestedBySection.get(section);
        if (nested !== undefined) {
          for (const s of nested.subs) {
            s.style.display = nested.flat ? "" : "none";
          }
          nested.nav.style.display = nested.flat ? "none" : "";
        }
        panel.subDetail = null;
        panel.detail = section;
        panel.btn.textContent = "←";
      },
      showNav: (): void => {
        for (const s of panel.sections) {
          s.style.display = "none";
        }
        clearFlashedStatuses();
        panel.nav.style.display = "";
        panel.detail = null;
        panel.subDetail = null;
        panel.restoreRootLabel();
      },
    };
    group.entry.resetNav = (): void => panel.showNav();
    navPanelByHover.set(group.hoverPanel, panel);
    return panel;
  }

  const SCENE_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 16l5-5 4 4 3-3 6 6"/><circle cx="9" cy="9" r="1.6" fill="currentColor" stroke="none"/></svg>`;
  const settingsPanelCtl = makeNavPanel("☰", (): void => {});
  settingsPanelCtl.restoreRootLabel = (): void => {
    settingsPanelCtl.btn.textContent = "☰";
  };
  const scenePanelCtl = makeNavPanel(t("section.sceneSettings"), (): void => {}, "icon-scene");
  const renderSceneIcon = (): void => {
    scenePanelCtl.btn.innerHTML = "";
    const glyph = document.createElement("span");
    glyph.className = "icon-glyph icon-scene";
    glyph.innerHTML = SCENE_ICON_SVG;
    scenePanelCtl.btn.appendChild(glyph);
  };
  scenePanelCtl.restoreRootLabel = renderSceneIcon;
  renderSceneIcon();
  const mainHoverPanel = settingsPanelCtl.hoverPanel;

  const MP_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
  const mpGroup = makeIconGroup(t("multiplayer.menuButton"), undefined, "icon-multiplayer", undefined, t("toolTip.multiplayer"));
  const mpGlyph = mpGroup.btn.querySelector(".icon-glyph");
  if (mpGlyph !== null) {
    mpGlyph.innerHTML = MP_ICON_SVG;
  }
  const mpCreateBtn = document.createElement("button");
  mpCreateBtn.textContent = t("multiplayer.createRoom");
  mpCreateBtn.addEventListener("click", (): void => handlers.onCreateRoom());
  const mpPublicBtn = document.createElement("button");
  mpPublicBtn.textContent = t("multiplayer.viewPublic");
  mpPublicBtn.addEventListener("click", (): void => handlers.onBrowsePublic());
  const mpLeaveBtn = document.createElement("button");
  mpLeaveBtn.textContent = t("multiplayer.leaveRoom");
  mpLeaveBtn.addEventListener("click", (): void => handlers.onLeaveRoom());
  const mpDisconnected = document.createElement("div");
  mpDisconnected.className = "multiplayer-panel-group";
  mpDisconnected.append(mpCreateBtn, mpPublicBtn);
  const mpConnected = document.createElement("div");
  mpConnected.className = "multiplayer-panel-group";
  mpConnected.append(mpLeaveBtn);
  mpGroup.hoverPanel.append(mpDisconnected, mpConnected);
  if (!MULTIPLAYER_ENABLED) {
    mpGroup.btn.parentElement?.remove();
  }
  let mpRoomActive = false;
  let stopOnBlurInput: HTMLInputElement | null = null;
  let stopOnBlurRow: HTMLDivElement | null = null;
  let tutorialStartBtn: HTMLButtonElement | null = null;
  let stopOnBlurBeforeRoom = init.stopOnBlur;
  const syncStopOnBlurLock = (): void => {
    if (stopOnBlurInput === null || stopOnBlurRow === null) {
      return;
    }
    if (mpRoomActive) {
      stopOnBlurInput.checked = false;
      stopOnBlurInput.disabled = true;
      stopOnBlurRow.dataset.hintTip = t("multiplayer.disabledInRoom");
      return;
    }
    stopOnBlurInput.checked = stopOnBlurBeforeRoom;
    stopOnBlurInput.disabled = false;
    stopOnBlurRow.dataset.hintTip = "";
  };
  const setRoomActive = (active: boolean): void => {
    mpRoomActive = active;
    mpDisconnected.style.display = active ? "none" : "";
    mpConnected.style.display = active ? "" : "none";
    syncStopOnBlurLock();
  };
  setRoomActive(false);

  if (matchMedia("(pointer: coarse)").matches && document.fullscreenEnabled === true) {
    const ENTER_FULLSCREEN_GLYPH = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"/></svg>`;
    const EXIT_FULLSCREEN_GLYPH = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5"/></svg>`;
    const fullscreenBtn = document.createElement("button");
    fullscreenBtn.className = "icon-btn icon-btn-square";
    const fullscreenGlyph = document.createElement("span");
    fullscreenGlyph.className = "icon-glyph";
    fullscreenBtn.appendChild(fullscreenGlyph);

    /**
     * `fullscreenchange` is the single source of truth for the icon — the click
     * handler never flips it directly, so a native exit (back gesture / ESC)
     * and a button tap can't drift out of sync.
     */
    const syncFullscreenBtn = (): void => {
      const active = document.fullscreenElement !== null;
      fullscreenGlyph.innerHTML = active ? EXIT_FULLSCREEN_GLYPH : ENTER_FULLSCREEN_GLYPH;
      const label = active ? t("fullscreen.exit") : t("fullscreen.enter");
      fullscreenBtn.setAttribute("aria-label", label);
    };
    attachTooltip(fullscreenBtn, (): string => {
      const label = document.fullscreenElement !== null ? t("fullscreen.exit") : t("fullscreen.enter");
      return `${label} : ${t("toolTip.fullscreen")}`;
    });

    fullscreenBtn.addEventListener("click", (): void => {
      const req = document.fullscreenElement === null
        ? document.documentElement.requestFullscreen()
        : document.exitFullscreen();
      req.catch((): void => {
        // denied gesture (permission policy) — swallow to keep console clean
      });
    });
    document.addEventListener("fullscreenchange", syncFullscreenBtn);
    syncFullscreenBtn();
    topControlsBar.appendChild(fullscreenBtn);
  }

  const touchPlayToggleBtn = document.createElement("button");
  touchPlayToggleBtn.className = "icon-btn icon-btn-square touchplay-toggle";
  touchPlayToggleBtn.setAttribute("aria-label", t("feature.objectPlacement"));
  attachTooltip(touchPlayToggleBtn, (): string => `${t("feature.objectPlacement")} : ${t("toolTip.objectPlacement")}`);
  const touchPlayGlyph = document.createElement("span");
  touchPlayGlyph.className = "icon-glyph icon-touchplay";
  touchPlayGlyph.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="12.5" height="12.5" rx="1.5"/><path d="M19.5 13.5v6M16.5 16.5h6"/></svg>`;
  touchPlayToggleBtn.appendChild(touchPlayGlyph);
  touchPlayToggleBtn.classList.toggle("active", init.objectPlacement);
  touchPlayToggleBtn.addEventListener("click", (): void => handlers.onTouchPlayToggle());
  topControlsBar.appendChild(touchPlayToggleBtn);
  const setTouchPlayToggleVisible = (visible: boolean): void => {
    touchPlayToggleBtn.style.display = visible ? "" : "none";
  };
  const setTouchPlayToggleActive = (on: boolean): void => {
    touchPlayToggleBtn.classList.toggle("active", on);
  };

  const cameraToggleBtn = document.createElement("button");
  cameraToggleBtn.className = "icon-btn icon-btn-square camera-toggle";
  cameraToggleBtn.setAttribute("aria-label", t("feature.cameraControl"));
  attachTooltip(cameraToggleBtn, (): string => `${t("feature.cameraControl")} : ${t("toolTip.cameraControl")}`);
  const cameraGlyph = document.createElement("span");
  cameraGlyph.className = "icon-glyph";
  cameraGlyph.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`;
  cameraToggleBtn.appendChild(cameraGlyph);
  cameraToggleBtn.classList.toggle("active", init.cameraEnabled);
  cameraToggleBtn.addEventListener("click", (): void => {
    const next = !cameraToggleBtn.classList.contains("active");
    cameraToggleBtn.classList.toggle("active", next);
    handlers.onCameraEnabled(next);
  });
  topControlsBar.appendChild(cameraToggleBtn);
  function setCameraState(checked: boolean): void {
    cameraToggleBtn.classList.toggle("active", checked);
  }

  const dimensionToggleBtn = document.createElement("button");
  dimensionToggleBtn.className = "icon-btn icon-btn-square dimension-toggle";
  dimensionToggleBtn.setAttribute("aria-label", t("view.dimension"));
  attachTooltip(dimensionToggleBtn, (): string => `${t("view.dimension")} : ${t("toolTip.dimension")}`);
  const dimensionGlyph = document.createElement("span");
  dimensionGlyph.className = "icon-glyph";
  dimensionGlyph.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><g transform="translate(12 13)"><path d="M0 0V-10.5"/><path d="M0 -10.5L-2.2 -7.2"/><path d="M0 -10.5L2.2 -7.2"/></g><g transform="translate(12 13) rotate(120)"><path d="M0 0V-10.5"/><path d="M0 -10.5L-2.2 -7.2"/><path d="M0 -10.5L2.2 -7.2"/></g><g transform="translate(12 13) rotate(240)"><path d="M0 0V-10.5"/><path d="M0 -10.5L-2.2 -7.2"/><path d="M0 -10.5L2.2 -7.2"/></g></svg>`;
  dimensionToggleBtn.appendChild(dimensionGlyph);
  dimensionToggleBtn.style.display = "none";
  let viewMode: ViewMode = init.viewMode;
  const setDimensionToggleActive = (on: boolean): void => {
    dimensionToggleBtn.classList.toggle("active", on);
  };
  const setViewMode = (mode: ViewMode): void => {
    viewMode = mode;
    setDimensionToggleActive(mode === "3d");
  };
  setViewMode(init.viewMode);
  dimensionToggleBtn.addEventListener("click", (): void => {
    const next: ViewMode = viewMode === "3d" ? "2d" : "3d";
    setViewMode(next);
    handlers.onViewMode(next);
  });
  topControlsBar.appendChild(dimensionToggleBtn);
  const setDimensionToggleVisible = (visible: boolean): void => {
    dimensionToggleBtn.style.display = visible ? "" : "none";
  };

  const { hoverPanel: clockHoverPanel } = makeIconGroup(t("tool.clock"), undefined, "icon-clock", undefined, t("toolTip.clock"));
  let muted = false;
  let lastVolume = init.volume > 0 ? init.volume : 0.5;
  let audioEntry: IconGroup | null = null;
  const toggleMute = (): boolean => {
    if (muted) {
      muted = false;
      handlers.onVolume(lastVolume);
      volumeSlider.set(lastVolume);
      renderAudioIcon(lastVolume, false);
      pinnedPanel = audioEntry;
      setExclusivePanel(audioEntry);
    } else {
      const cur = parseFloat(volumeSlider.input.value);
      if (cur > 0) {
        lastVolume = cur;
      }
      muted = true;
      handlers.onVolume(0);
      renderAudioIcon(0, true);
      pinnedPanel = null;
      setExclusivePanel(null);
    }
    return true;
  };
  const { hoverPanel: audioHoverPanel, btn: audioBtn, entry: audioGroupEntry } = makeIconGroup(t("tool.audio"), toggleMute, "icon-audio", true, t("toolTip.audio"));
  audioEntry = audioGroupEntry;
  clockHoverPanel.classList.add("hover-panel-wide");
  audioHoverPanel.classList.add("hover-panel-wide");

  const audioGlyph = audioBtn.querySelector(".icon-glyph");

  /**
   * Repaints the Audio icon: an ✕ speaker when muted, a bare speaker (no arcs)
   * at zero volume, otherwise a speaker with one to three sound-wave arcs
   * quantised from the 0-1 volume. Mute is a state distinct from a zero master
   * volume, so the two read differently. The SVG is rebuilt rather than toggling
   * CSS classes because the variable arc count is simpler to express as geometry.
   */
  function renderAudioIcon(volume: number, muted: boolean): void {
    if (audioGlyph === null) {
      return;
    }
    const speaker = `<path d="M3 9 H7 L12 4 V20 L7 15 H3 Z" fill="currentColor" stroke="none"/>`;
    let extra: string;
    if (muted) {
      extra = `<path d="M15 9 L21 15 M21 9 L15 15"/>`;
    } else if (volume <= 0) {
      extra = "";
    } else {
      const arcs = volume > 0.66 ? 3 : volume > 0.33 ? 2 : 1;
      const paths = [
        `<path d="M14.5 9 Q16.5 12 14.5 15"/>`,
        `<path d="M17 7 Q20.5 12 17 17"/>`,
        `<path d="M19.5 5 Q24 12 19.5 19"/>`,
      ];
      extra = paths.slice(0, arcs).join("");
    }
    audioGlyph.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${speaker}${extra}</svg>`;
  }
  renderAudioIcon(init.volume, false);

  const mainNav = settingsPanelCtl.nav;
  const navButtons = settingsPanelCtl.navButtons;

  const objectPanel = document.createElement("div");
  objectPanel.className = "panel panel-object";
  const objectPanelScroll = document.createElement("div");
  objectPanelScroll.className = "panel-object-scroll";
  objectPanelScroll.appendChild(objectPanel);

  /**
   * Developer-only sections of the ☰ panel, hidden unless the page is loaded
   * with a `?debug` URL param. Not persisted — purely a per-load flag.
   */
  const DEBUG_SECTIONS: readonly string[] = ["Debug Settings"];
  const debugUi = new URLSearchParams(location.search).has("debug");

  const hoverPanelParents = new Set<HTMLElement>([clockHoverPanel, audioHoverPanel]);

  /**
   * foldoutKey decouples the persisted collapse state from the visible title, so
   * sections in different panels can share a label (e.g. "Audio", "Physics")
   * without sharing their open/closed state. Hover-panel sections (☰ details and
   * Tool/Clock/Audio) carry a plain, non-collapsible title — the panel itself is
   * the show/hide unit there, and the title names the panel once the buttons
   * become icons. Only the object panel keeps collapsible foldouts.
   */
  /**
   * Section titles double as stable lookup keys (navButtons, DEBUG_SECTIONS,
   * NAV_ORDER, foldout state), so the raw English title stays the key while only
   * the displayed heading / nav-button text is localized. Titles absent here
   * (e.g. the developer-only "Debug Settings") render in English unchanged.
   */
  const SECTION_LABELS: Record<string, MessageKey> = {
    "Scene Settings": "section.sceneSettings",
    "Scene Local": "section.sceneLocal",
    "Scene Uploaded": "section.sceneUploaded",
    "Scene Mic": "section.sceneMic",
    Export: "section.export",
    "Personal Settings": "section.personal",
    License: "section.license",
    Feedback: "section.feedback",
    Privacy: "section.privacy",
    Account: "section.account",
    Bookmarks: "section.bookmarks",
    Clock: "section.clock",
    Shape: "section.shape",
    Physics: "section.physics",
    Audio: "section.audio",
    Field: "section.field",
    Operator: "section.operator",
    Timer: "section.timer",
    Decay: "section.decay",
    Delay: "section.delay",
    Router: "section.router",
    Button: "section.button",
    Lamp: "section.lamp",
    Owner: "count.ownerLabel",
    Array: "section.array",
    Curve: "section.curve",
    PitchQuantize: "section.pitchQuantize",
    Params: "section.params",
    Rotation: "section.rotation",
    Wave: "section.wave",
    Bulk: "section.bulk",
    Emit: "section.emit",
  };
  const sectionLabel = (title: string): string => {
    const key = SECTION_LABELS[title];
    return key !== undefined ? t(key) : title;
  };


  /**
   * Param name -> explanatory tooltip key. Only names present here get a custom
   * tooltip; others pass through untouched. Debug-panel params are intentionally
   * absent (those sections stay English-only with no custom tooltips).
   */
  const PARAM_TIPS: Record<string, MessageKey> = {
    gravity: "tip.gravity",
    tempo: "tip.tempo",
    bounce: "tip.bounce",
    friction: "tip.friction",
    mass: "tip.mass",
    drag: "tip.drag",
    timbre: "tip.timbre",
    volume: "tip.volume",
    mute: "tip.mute",
    piece: "tip.piece",
    value: "tip.value",
    hue: "tip.hue",
    sat: "tip.sat",
    spread: "tip.spread",
    speed: "tip.speed",
    spdRange: "tip.spdRange",
    life: "tip.life",
    lifeRange: "tip.lifeRange",
    rate: "tip.rate",
    bars: "tip.bars",
    radius: "tip.radius",
    strength: "tip.strength",
    fade: "tip.fade",
    arc: "tip.arc",
    massOn: "tip.massOn",
    killOnHit: "tip.killOnHit",
    collisionOn: "tip.collisionOn",
    operator: "tip.operator",
    timerMode: "tip.timerMode",
    buttonMode: "tip.buttonMode",
    buttonPulseOnReset: "tip.buttonPulseOnReset",
    decayTime: "tip.decayTime",
    delayTime: "tip.delayTime",
    routerHold: "tip.routerHold",
    routerReset: "tip.routerReset",
    fieldMode: "tip.fieldMode",
    omega: "tip.omega",
    k: "tip.k",
    transpose: "tip.transpose",
    pitchQuantize: "tip.pitchQuantize",
    "global sustain": "tip.globalSustain",
    "global volume": "tip.globalVolume",
    play: "tip.play",
    rot: "tip.rot",
    amp: "tip.amp",
    period: "tip.period",
    phase: "tip.phase",
    meter: "tip.meter",
    speedFromSettings: "tip.speedFromSettings",
    simple: "tip.simple",
    feat_physics: "tip.featPhysics",
    "feat_3d": "tip.feat3d",
    feat_node: "tip.featNode",
    feat_touchplay: "tip.featTouchplay",
    camera: "tip.camera",
    dimension: "tip.dimension",
    noteNames: "tip.noteNames",
    language: "tip.language",
    snap: "tip.snap",
    stopInBackground: "tip.stopInBackground",
    wallPreset: "tip.wallPreset",
    wallZ: "tip.wallZ",
    wallTile: "tip.wallTile",
    wallNormal: "tip.wallNormal",
    wallResolution: "tip.wallResolution",
    wallLight: "tip.wallLight",
    shadowRes: "tip.shadowRes",
  };

  /**
   * Resolves the tooltip body for a param name, appending the driven note when
   * the row carries data-driven (set while the param is fed by a connection).
   * A row's data-hintTip (a disabled-state reason set elsewhere) takes priority
   * over the normal param body when non-empty. Returns "" when neither applies,
   * which suppresses the popup.
   */
  const paramTip = (name: string, row: HTMLElement): string => {
    const hint = row.dataset.hintTip;
    if (hint !== undefined && hint !== "") {
      return hint;
    }
    const key = PARAM_TIPS[name];
    if (key === undefined) {
      return "";
    }
    const body = t(key);
    return row.dataset.driven === "1" ? `${body}\n${t("tip.drivenNote")}` : body;
  };

  const tipRow = (row: HTMLElement, name: string): void => {
    attachTooltip(row, (): string => paramTip(name, row));
  };

  function addSection(title: string, parent: HTMLElement = mainHoverPanel, foldoutKey: string = title): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "section";
    const heading = document.createElement("div");
    heading.className = "section-title";
    heading.textContent = sectionLabel(title);
    section.appendChild(heading);
    if (hoverPanelParents.has(parent)) {
      heading.classList.add("section-title-plain");
      parent.appendChild(section);
      return section;
    }
    const navPanel = navPanelByHover.get(parent);
    if (navPanel !== undefined) {
      heading.classList.add("section-title-plain");
      section.style.display = "none";
      const navBtn = document.createElement("button");
      navBtn.className = "main-nav-btn";
      navBtn.textContent = sectionLabel(title);
      navBtn.addEventListener("click", (): void => navPanel.showSection(section));
      if (!debugUi && DEBUG_SECTIONS.includes(title)) {
        navBtn.style.display = "none";
      }
      navPanel.navButtons.set(title, navBtn);
      navPanel.nav.appendChild(navBtn);
      navPanel.sections.push(section);
      navPanel.scroll.appendChild(section);
      return section;
    }
    if (init.foldouts[foldoutKey] === true) {
      section.classList.add("collapsed");
    }
    heading.addEventListener("click", (): void => {
      closePopover();
      const collapsed = section.classList.toggle("collapsed");
      handlers.onFoldout(foldoutKey, collapsed);
    });
    parent.appendChild(section);
    return section;
  }

  const INLINE_SLIDER_MIN_W = 1100;
  const sliderInputs: HTMLInputElement[] = [];

  function applySliderMode(inp: HTMLInputElement): void {
    const inline = window.innerWidth >= INLINE_SLIDER_MIN_W;
    inp.style.display = inline ? "" : "none";
    inp.step = inline ? (inp.dataset.originalStep ?? "any") : "any";
  }

  window.addEventListener("resize", (): void => {
    for (const inp of sliderInputs) {
      applySliderMode(inp);
    }
  });

  function addSlider(
    parent: HTMLElement,
    label: string,
    min: number,
    max: number,
    step: number,
    value: number,
    format: (v: number) => string,
    onChange: (v: number) => void,
    onCommit?: (v: number) => void,
    transform?: SliderTransform,
    allowOutOfRange: boolean = false,
  ): { input: HTMLInputElement; row: HTMLLabelElement; set: (v: number) => void } {
    const row = document.createElement("label");
    row.className = "row";
    const name = document.createElement("span");
    name.className = "row-label";
    name.textContent = label;
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = "any";
    input.dataset.originalStep = String(step);
    input.value = String(value);
    input.style.display = "none";
    const out = document.createElement("span");
    out.className = "row-value row-value--num";
    out.textContent = format(value);

    /**
     * Direct numeric entry while the inline slider is shown. preventDefault on
     * the opening click stops the wrapping <label> from forwarding activation
     * to the range input, which would otherwise blur the editor immediately.
     */
    function openNumberField(): void {
      const editor = document.createElement("input");
      editor.type = "number";
      editor.className = "num-input num-input--row";
      const lo = transform ? transform.toDisplay(parseFloat(input.min)) : parseFloat(input.min);
      const hi = transform ? transform.toDisplay(parseFloat(input.max)) : parseFloat(input.max);
      if (!allowOutOfRange) {
        editor.min = String(Math.min(lo, hi));
        editor.max = String(Math.max(lo, hi));
      }
      editor.step = transform ? "any" : (input.dataset.originalStep ?? "any");
      editor.value = String(transform ? transform.toDisplay(parseFloat(input.value)) : parseFloat(input.value));
      out.style.display = "none";
      out.after(editor);
      editor.focus();
      editor.select();
      let done = false;
      const close = (commit: boolean): void => {
        if (done) return;
        done = true;
        document.removeEventListener("pointerdown", onOutsidePointerDown, true);
        if (commit) {
          const entered = parseFloat(editor.value);
          if (Number.isFinite(entered)) {
            const raw = transform ? transform.fromDisplay(entered) : entered;
            const committed = allowOutOfRange ? raw : Math.min(parseFloat(input.max), Math.max(parseFloat(input.min), raw));
            input.value = String(committed);
            out.textContent = format(committed);
            onChange(committed);
            onCommit?.(committed);
            syncLinearPopover(input, committed);
          }
        }
        editor.remove();
        out.style.display = "";
      };
      /**
       * Commits on a pointerdown anywhere outside the editor, in the capture
       * phase so the value lands before a same-click handler (canvas deselect →
       * panel rebuild) tears the editor down — at which point the trailing blur
       * would commit onto an already-deselected target and lose the edit.
       */
      const onOutsidePointerDown = (e: PointerEvent): void => {
        if (e.target instanceof Node && !editor.contains(e.target)) {
          close(true);
        }
      };
      editor.addEventListener("keydown", (e: KeyboardEvent): void => {
        if (e.key === "Enter") close(true);
        if (e.key === "Escape") close(false);
      });
      editor.addEventListener("blur", (): void => close(true));
      document.addEventListener("pointerdown", onOutsidePointerDown, true);
    }

    out.addEventListener("click", (ev: MouseEvent): void => {
      if (input.disabled) return;
      if (window.innerWidth >= INLINE_SLIDER_MIN_W) {
        ev.preventDefault();
        openNumberField();
      } else {
        openLinearPopover(out, input, format, onChange, out, onCommit, transform, allowOutOfRange);
      }
    });
    row.append(name, input, out);
    parent.appendChild(row);
    input.addEventListener("input", (): void => {
      const v = parseFloat(input.value);
      out.textContent = format(v);
      onChange(v);
      syncLinearPopover(input, v);
    });
    if (onCommit) {
      input.addEventListener("change", (): void => {
        onCommit(parseFloat(input.value));
      });
    }
    sliderInputs.push(input);
    applySliderMode(input);
    const set = (v: number): void => {
      input.value = String(v);
      out.textContent = format(v);
      syncLinearPopover(input, v);
    };
    return { input, row, set };
  }

  function addSelectRow(parent: HTMLElement, label: string, options: [string, string][]): HTMLSelectElement {
    const row = document.createElement("div");
    row.className = "row";
    const name = document.createElement("span");
    name.className = "row-label";
    name.textContent = label;
    const select = document.createElement("select");
    for (const [value, text] of options) {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = text;
      select.appendChild(option);
    }
    row.append(name, select);
    parent.appendChild(row);
    return select;
  }

  function addCheckbox(
    parent: HTMLElement,
    label: string,
    checked: boolean,
    onChange: (v: boolean) => void,
  ): { input: HTMLInputElement; row: HTMLDivElement } {
    const row = document.createElement("div");
    row.className = "row row-check";
    const name = document.createElement("span");
    name.className = "row-label";
    name.textContent = label;
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.addEventListener("change", (): void => onChange(input.checked));
    row.append(name, input);
    parent.appendChild(row);
    return { input, row };
  }

  /**
   * Builds a top section that drills into grandchildren. Its own section title is
   * hidden so a grandchild (e.g. Env) renders at the same position a top-level
   * section would, and its sub-nav looks identical to the root nav.
   */
  function makeNestedSection(title: string, panel: NavPanel = settingsPanelCtl, flat: boolean = false): { section: HTMLDivElement; addSub: (subTitle: string) => HTMLDivElement; subNav: Map<string, HTMLButtonElement> } {
    const section = addSection(title, panel.hoverPanel);
    section.classList.add("nested-host");
    const heading = section.querySelector<HTMLDivElement>(".section-title");
    if (heading !== null) {
      heading.style.display = "none";
    }
    const nav = document.createElement("div");
    nav.className = "main-nav";
    if (flat) {
      nav.style.display = "none";
    }
    section.appendChild(nav);
    const subs: HTMLDivElement[] = [];
    const subNav = new Map<string, HTMLButtonElement>();
    panel.nestedBySection.set(section, { nav, subs, flat });
    const addSub = (subTitle: string): HTMLDivElement => {
      const sec = document.createElement("div");
      sec.className = "section";
      const subHeading = document.createElement("div");
      subHeading.className = "section-title section-title-plain";
      subHeading.textContent = subTitle;
      sec.appendChild(subHeading);
      sec.style.display = "none";
      section.appendChild(sec);
      subs.push(sec);
      const navBtn = document.createElement("button");
      navBtn.className = "main-nav-btn";
      navBtn.textContent = subTitle;
      navBtn.addEventListener("click", (): void => panel.showSubSection(sec));
      nav.appendChild(navBtn);
      subNav.set(subTitle, navBtn);
      return sec;
    };
    return { section, addSub, subNav };
  }

  const sceneSettings = makeNestedSection("Scene Settings", scenePanelCtl);
  const featuresSub = sceneSettings.addSub(t("section.features"));
  const environmentSub = sceneSettings.addSub(t("section.wallpaper"));

  const personal = makeNestedSection("Personal Settings", settingsPanelCtl, true);
  const debug = makeNestedSection("Debug Settings");
  const viewSub = personal.addSub(t("section.view"));
  const editSub = personal.addSub(t("section.edit"));
  const communitySub = personal.addSub(t("section.community"));
  const personalEnvironmentSub = personal.addSub(t("section.env"));
  const playbackSub = personal.addSub(t("section.playback"));
  const particleSub = debug.addSub("Particle");
  const timbreSub = debug.addSub("Timbre");
  const densitySub = debug.addSub("Density");
  const perfSub = debug.addSub("Perf");
  const licenseSection = addSection("License");

  const features: Record<FeatureCategory, boolean> = { ...init.features };
  const FEATURE_LABELS: Record<FeatureCategory, MessageKey> = {
    physics: "feature.physics",
    "3d": "feature.3d",
    node: "feature.node",
  };
  let simpleOn = init.simpleMode;
  let roomModeLocked = false;
  const featureInputByCat = new Map<FeatureCategory, HTMLInputElement>();
  const featureRowByCat = new Map<FeatureCategory, HTMLElement>();
  const effectiveFeature = (cat: FeatureCategory): boolean => (simpleOn ? false : features[cat]);
  const applyFeature = (cat: FeatureCategory, v: boolean): void => {
    features[cat] = v;
    applyFeatureVisibility();
    applyOrbitRowGates();
    if (cat === "3d" && !v && viewMode === "3d") {
      setViewMode("2d");
      handlers.onViewMode("2d");
    }
    handlers.onFeature(cat, v);
  };

  /** Syncs the Simple checkbox and disables the feature checkboxes while Simple is on. */
  function setSimple(on: boolean): void {
    simpleOn = on;
    simpleToggle.input.checked = on;
    featureInputs.forEach((inp: HTMLInputElement): void => {
      inp.disabled = on || roomModeLocked;
    });
    applyFeatureVisibility();
    applyOrbitRowGates();
  }

  /**
   * Reflects a programmatic feature toggle into the checkbox + local mirror, used
   * when a mode change or a node-driven setParam flips a feature without a user
   * click. Re-runs feature visibility so the palette / radial / dimension toggle
   * track the change (a node turned off must drop the node palette section).
   */
  function setFeatureChecked(cat: FeatureCategory, on: boolean): void {
    features[cat] = on;
    const inp = featureInputByCat.get(cat);
    if (inp) {
      inp.checked = on;
    }
    applyFeatureVisibility();
  }

  const simpleToggle = addCheckbox(featuresSub, t("feature.simple"), simpleOn, (v: boolean): void => {
    setSimple(v);
    handlers.onSimpleMode(v);
  });
  tipRow(simpleToggle.row, "simple");
  const featureInputs: HTMLInputElement[] = [];
  for (const cat of FEATURE_CATEGORIES) {
    if (cat === "physics" || (cat === "3d" && !debugUi)) {
      continue;
    }
    const box = addCheckbox(featuresSub, t(FEATURE_LABELS[cat]), features[cat], (v: boolean): void => {
      applyFeature(cat, v);
    });
    tipRow(box.row, `feat_${cat}`);
    featureInputs.push(box.input);
    featureInputByCat.set(cat, box.input);
    featureRowByCat.set(cat, box.row);
  }
  if (simpleOn) {
    featureInputs.forEach((inp: HTMLInputElement): void => {
      inp.disabled = true;
    });
  }
  /**
   * Tool palette pinned to the left screen edge, always resident. Tool buttons
   * are feature-gated and rebuilt on feature changes via rebuildPaletteTools.
   * Each button starts a one-shot drag-to-place gesture; no tool stays armed.
   */
  const palette = document.createElement("div");
  palette.className = "simple-palette";
  const toolButtons = new Map<Tool, HTMLButtonElement>();

  function highlightTool(tool: Tool | null): void {
    toolButtons.forEach((btn: HTMLButtonElement, key: Tool): void => {
      btn.classList.toggle("active", key === tool);
    });
    submenuButtons.forEach((btn: HTMLButtonElement, key: Tool): void => {
      btn.classList.toggle("active", key === tool);
    });
  }

  let globalPlaced = false;

  /**
   * Disables the Global palette tool once a global node exists: it is a scene-wide
   * singleton, so a second placement is never offered. Re-applied after every
   * setToolsEnabled(true) so a blanket re-enable cannot resurrect the tool while a
   * global is already placed.
   */
  function setGlobalPlaced(placed: boolean): void {
    globalPlaced = placed;
    const btn = submenuButtons.get("global");
    if (btn) {
      btn.disabled = placed;
    }
  }

  function setToolsEnabled(enabled: boolean): void {
    toolButtons.forEach((btn: HTMLButtonElement): void => {
      btn.disabled = !enabled;
    });
    submenuButtons.forEach((btn: HTMLButtonElement): void => {
      btn.disabled = !enabled;
    });
    if (nodeParentBtn) {
      nodeParentBtn.disabled = !enabled;
    }
    if (enabled && globalPlaced) {
      const g = submenuButtons.get("global");
      if (g) {
        g.disabled = true;
      }
    }
  }

  /**
   * Hides / restores the object-placement palette for play mode (active
   * TouchPlay). The uiView layer keeps the palette permanently hidden, so it
   * wins regardless of play state.
   */
  function setPlayModeHidden(hidden: boolean): void {
    if (uiView) {
      return;
    }
    palette.style.display = hidden ? "none" : "";
  }

  function sequencerIconBody(): string {
    const cells: readonly [number, number, boolean][] = [
      [7.5, 7.5, true],
      [16.5, 7.5, true],
      [7.5, 16.5, false],
      [16.5, 16.5, true],
    ];
    let body = "";
    for (const [cx, cy, filled] of cells) {
      body += filled
        ? `<circle cx="${cx}" cy="${cy}" r="3.3" fill="currentColor"/>`
        : `<circle cx="${cx}" cy="${cy}" r="3.3" fill="none" stroke="currentColor" stroke-width="1.4"/>`;
    }
    return body;
  }

  function streamIconBody(): string {
    let body = "";
    for (let row = 0; row < 4; row++) {
      for (let i = 0; i <= row; i++) {
        const cx = 12 + (i - row / 2) * 5;
        const cy = 18 - row * 4;
        body += `<circle cx="${cx}" cy="${cy}" r="1.5" fill="currentColor"/>`;
      }
    }
    return body;
  }

  function forceIconBody(): string {
    let body = "";
    for (const x of [6, 12, 18]) {
      body += `<path d="M ${x} 18 V 8 M ${x - 2.4} 10.4 L ${x} 7.4 L ${x + 2.4} 10.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`;
    }
    return body;
  }

  function valueIconBody(): string {
    const ops: readonly [string, number, number][] = [["+", 7, 11.8], ["−", 17, 11.8], ["×", 7, 21.3], ["÷", 17, 21.3]];
    let body = "";
    for (const [glyph, x, y] of ops) {
      body += `<text x="${x}" y="${y}" font-size="11.5" font-family="system-ui, sans-serif" text-anchor="middle" fill="currentColor">${glyph}</text>`;
    }
    return body;
  }

  function countIconBody(): string {
    return `<path d="M 3 16.5 H 8 V 7.5 H 16 V 16.5 H 21" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`;
  }

  function displayIconBody(): string {
    return `<rect x="3.5" y="6.5" width="17" height="11" rx="1.5" fill="currentColor"/><path d="M 8 9.5 H 10.5 M 8 10 V 12.3 M 10.8 10 V 12.3 M 8 12.6 H 10.5 M 8 12.9 V 15.2 M 10.8 12.9 V 15.2 M 8 15.5 H 10.5 M 13.5 9.5 H 16 M 13.5 10 V 12.3 M 16.3 10 V 12.3 M 13.5 12.6 H 16 M 13.5 12.9 V 15.2 M 16.3 12.9 V 15.2 M 13.5 15.5 H 16" fill="none" stroke="#0a0c0e" stroke-width="1" stroke-linecap="round"/>`;
  }

  type PaletteEntry = readonly [Tool, MessageKey, string, MessageKey];
  const EMITTER_PALETTE_TOOLS: readonly PaletteEntry[] = [
    ["emitter-sync", "tool.sequencer", sequencerIconBody(), "toolTip.sequencer"],
    ["emitter-full", "tool.stream", streamIconBody(), "toolTip.stream"],
  ];
  const SHAPE_PALETTE_TOOLS: readonly PaletteEntry[] = [
    ["box", "tool.box", `<rect x="5" y="5" width="14" height="14" rx="1" fill="currentColor"/>`, "toolTip.box"],
    ["sphere", "tool.sphere", `<circle cx="12" cy="12" r="7.5" fill="currentColor"/>`, "toolTip.sphere"],
    ["funnel", "tool.funnel", `<rect x="6.2" y="6" width="2.6" height="12" rx="0.6" transform="rotate(-26 7.5 12)" fill="currentColor"/><rect x="15.2" y="6" width="2.6" height="12" rx="0.6" transform="rotate(26 16.5 12)" fill="currentColor"/><path d="M 5.4 6.9 H 18.6 M 10.4 17.2 H 13.6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>`, "toolTip.funnel"],
    ["rail", "tool.rail", `<path d="M 3.5 19 C 10 19 7.5 9.5 14 9.5 L 20.5 9.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M 3.5 14.5 C 8.5 14.5 6.5 5 13 5 L 20.5 5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`, "toolTip.rail"],
  ];
  const PHYSICS_PALETTE_TOOLS: readonly PaletteEntry[] = [["force", "tool.force", forceIconBody(), "toolTip.force"]];
  const ORBIT_ICON = `<circle cx="12" cy="12" r="3" fill="currentColor"/><path d="M 19.6 9.3 A 8 8 0 1 0 20 13.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M 17.7 12.3 L 20 13.6 L 19.1 16.1" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`;
  const GROUP_ICON = `<rect x="4.5" y="4.5" width="15" height="15" rx="1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2.2"/>`;
  const TRANSFORM_PALETTE_TOOLS: readonly PaletteEntry[] = [
    ["orbit", "tool.orbit", ORBIT_ICON, "toolTip.orbit"],
    ["group", "tool.group", GROUP_ICON, "toolTip.group"],
  ];
  const NODE_PALETTE_TOOLS: readonly PaletteEntry[] = [
    ["calc", "tool.calc", valueIconBody(), "toolTip.calc"],
    ["count", "tool.count", countIconBody(), "toolTip.count"],
    ["display", "tool.display", displayIconBody(), "toolTip.display"],
    ["timer", "tool.timer", `<circle cx="12" cy="13" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M 12 13 V 8.5 M 12 13 L 15 14.8" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M 10 4.5 H 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`, "toolTip.timer"],
    ["decay", "tool.decay", `<path d="M 4.5 5 V 19 H 19.5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M 6.5 6.5 L 17.5 17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>`, "toolTip.decay"],
    ["delay", "tool.delay", `<rect x="7" y="8.5" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M 3 12 H 7 M 17 12 H 21" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/>`, "toolTip.delay"],
    ["router", "tool.router", `<circle cx="6" cy="12" r="1.6" fill="currentColor"/><circle cx="18" cy="5.5" r="1.6" fill="currentColor"/><circle cx="18" cy="10" r="1.6" fill="currentColor"/><circle cx="18" cy="14" r="1.6" fill="currentColor"/><circle cx="18" cy="18.5" r="1.6" fill="currentColor"/><path d="M 7.4 12 H 12 M 12 12 V 5.5 H 16.4 M 12 10 H 16.4 M 12 14 H 16.4 M 12 12 V 18.5 H 16.4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`, "toolTip.router"],
    ["button", "tool.button", `<circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="4" fill="currentColor"/>`, "toolTip.button"],
    ["lamp", "tool.lamp", `<circle cx="12" cy="13.5" r="4.2" fill="currentColor"/><path d="M 12 5 V 7.4 M 5.6 8.6 L 7.4 10.2 M 18.4 8.6 L 16.6 10.2 M 4 14.5 H 6.4 M 17.6 14.5 H 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`, "toolTip.lamp"],
    ["knob", "tool.knob", `<circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M 12 5.6 L 12 3.2" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`, "toolTip.knob"],
    ["envelope", "tool.envelope", `<path d="M 3 19 L 8 5 L 13 11 H 18 L 21 19" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`, "toolTip.envelope"],
    ["array", "tool.array", `<rect x="4" y="4" width="4.5" height="4.5" rx="0.8" fill="currentColor"/><rect x="9.75" y="4" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="15.5" y="4" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="4" y="9.75" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="9.75" y="9.75" width="4.5" height="4.5" rx="0.8" fill="currentColor"/><rect x="15.5" y="9.75" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="4" y="15.5" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="9.75" y="15.5" width="4.5" height="4.5" rx="0.8" fill="currentColor" opacity="0.35"/><rect x="15.5" y="15.5" width="4.5" height="4.5" rx="0.8" fill="currentColor"/>`, "toolTip.array"],
    ["curve", "tool.curve", `<rect x="4" y="4" width="16" height="16" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.2" opacity="0.5"/><path d="M 5 17 C 8 17 8 8 12 8 C 16 8 16 14 19 14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`, "toolTip.curve"],
    ["timbre", "tool.timbre", `<path d="M 3 12 H 6 L 8.5 5.5 L 12 18.5 L 15 9 L 17 12 H 21" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`, "toolTip.timbre"],
    ["pitchQuantize", "tool.pitchQuantize", `<rect x="4" y="5" width="16" height="14" rx="1" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M 8.6 5 V 14 M 13.4 5 V 14 M 4 14 H 20" fill="none" stroke="currentColor" stroke-width="1.1"/><rect x="7" y="5" width="3.2" height="6" rx="0.5" fill="currentColor"/><rect x="12" y="5" width="3.2" height="6" rx="0.5" fill="currentColor"/>`, "toolTip.pitchQuantize"],
    ["global", "tool.global", `<circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M 4.5 12 H 19.5 M 12 4.5 V 19.5 M 6.2 7 C 9 9 15 9 17.8 7 M 6.2 17 C 9 15 15 15 17.8 17" fill="none" stroke="currentColor" stroke-width="1.2"/>`, "toolTip.global"],
  ];
  const toolSections: HTMLDivElement[] = [];

  function makePaletteDivider(): HTMLDivElement {
    const divider = document.createElement("div");
    divider.className = "simple-palette-divider";
    return divider;
  }

  /**
   * Appends a palette button's persistent caption (shown beneath its icon, and
   * hidden by the .no-tool-labels class in icon-only mode) and wires a non-modal
   * tooltip that works on desktop hover and mobile long-press. The caption drops
   * any trailing parenthetical usage hint (e.g. "Paste (drag to canvas)" →
   * "Paste") to stay short, while the tooltip shows the full name plus, when
   * descKey is given, " : <description>". getText is lazy so it tracks language
   * changes. No native title is set, so the custom tooltip is the sole popup.
   */
  function setPaletteBtnLabel(btn: HTMLButtonElement, nameKey: MessageKey, descKey?: MessageKey): void {
    const label = document.createElement("span");
    label.className = "simple-palette-btn-label";
    label.textContent = t(nameKey).replace(/\s*\([^)]*\)\s*$/, "");
    btn.appendChild(label);
    attachTooltip(btn, (): string => {
      const name = t(nameKey);
      const desc = descKey !== undefined ? t(descKey) : "";
      return desc !== "" ? `${name} : ${desc}` : name;
    });
  }

  /**
   * Hides any divider that lands at the top or bottom of a wrapped column, so a
   * line break never shows a dangling separator. Buttons wrap individually now
   * (sections are display:contents), so columns are identified by item center-x
   * (buttons left-align but dividers auto-center, yet both share the column's
   * center). visibility keeps layout stable so toggling cannot re-trigger the wrap.
   */
  function syncPaletteWrapDividers(): void {
    const items = Array.from(palette.querySelectorAll<HTMLElement>(".simple-palette-section > *"));
    const centers = items.map((el: HTMLElement): number => {
      const r = el.getBoundingClientRect();
      return r.left + r.width / 2;
    });
    const sameCol = (a: number, b: number): boolean => Math.abs(a - b) < 1;
    items.forEach((el: HTMLElement, i: number): void => {
      if (!el.classList.contains("simple-palette-divider")) {
        return;
      }
      const topOfCol = i === 0 || !sameCol(centers[i - 1], centers[i]);
      const bottomOfCol = i === items.length - 1 || !sameCol(centers[i + 1], centers[i]);
      el.style.visibility = topOfCol || bottomOfCol ? "hidden" : "";
    });
  }

  const NODE_PARENT_ICON = `<circle cx="6" cy="7" r="2.2" fill="currentColor"/><circle cx="17.5" cy="12" r="2.2" fill="currentColor"/><circle cx="7" cy="17.5" r="2.2" fill="currentColor"/><path d="M 8 8 L 15.5 11 M 8.6 16.3 L 15.6 13.2" fill="none" stroke="currentColor" stroke-width="1.4"/>`;
  const nodeSubmenu = document.createElement("div");
  nodeSubmenu.className = "simple-palette-submenu";
  const submenuButtons = new Map<Tool, HTMLButtonElement>();
  for (const [tool, label, icon, descKey] of NODE_PALETTE_TOOLS) {
    const btn = document.createElement("button");
    btn.className = "simple-palette-btn simple-palette-tool";
    btn.setAttribute("data-palette-tool", tool);
    btn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${icon}</svg>`;
    setPaletteBtnLabel(btn, label, descKey);
    btn.addEventListener("pointerdown", (ev: PointerEvent): void => {
      ev.preventDefault();
      btn.setPointerCapture(ev.pointerId);
      handlers.onSubmenuToolDragStart(tool, ev);
    });
    nodeSubmenu.appendChild(btn);
    submenuButtons.set(tool, btn);
  }
  palette.appendChild(nodeSubmenu);
  let nodeParentBtn: HTMLButtonElement | null = null;

  window.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (!nodeSubmenu.classList.contains("open")) {
      return;
    }
    const target = ev.target as Element | null;
    if (target && target.closest(".simple-palette")) {
      return;
    }
    closeNodeSubmenu();
  });

  function clearSubmenuHover(): void {
    submenuButtons.forEach((btn: HTMLButtonElement): void => btn.classList.remove("hover"));
  }

  function openNodeSubmenu(): void {
    if (!nodeParentBtn) {
      return;
    }
    nodeSubmenu.classList.add("open");
    const r = nodeParentBtn.getBoundingClientRect();
    const h = nodeSubmenu.getBoundingClientRect().height;
    const top = Math.max(8, Math.min(r.top, window.innerHeight - h - 8));
    nodeSubmenu.style.left = `${r.right + 8}px`;
    nodeSubmenu.style.top = `${top}px`;
    nodeParentBtn.classList.add("active");
  }

  function closeNodeSubmenu(): void {
    nodeSubmenu.classList.remove("open");
    clearSubmenuHover();
    if (nodeParentBtn) {
      nodeParentBtn.classList.remove("active");
    }
  }

  function setNodeMenuHover(tool: Tool | null): void {
    clearSubmenuHover();
    if (tool === null) {
      return;
    }
    const btn = submenuButtons.get(tool);
    if (btn) {
      btn.classList.add("hover");
    }
  }

  function nodeSubmenuToolAt(x: number, y: number): Tool | null {
    if (!nodeSubmenu.classList.contains("open")) {
      return null;
    }
    const el = document.elementFromPoint(x, y);
    const btn = el ? el.closest<HTMLElement>("[data-palette-tool]") : null;
    if (!btn || !nodeSubmenu.contains(btn)) {
      return null;
    }
    return btn.getAttribute("data-palette-tool") as Tool;
  }

  function buildNodeSection(): void {
    const wrap = document.createElement("div");
    wrap.className = "simple-palette-section";
    wrap.appendChild(makePaletteDivider());
    const parent = document.createElement("button");
    parent.className = "simple-palette-btn simple-palette-tool simple-palette-node-parent";
    parent.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${NODE_PARENT_ICON}</svg>`;
    setPaletteBtnLabel(parent, "tool.node", "toolTip.node");
    parent.addEventListener("pointerdown", (ev: PointerEvent): void => {
      ev.preventDefault();
      parent.setPointerCapture(ev.pointerId);
      const wasOpen = nodeSubmenu.classList.contains("open");
      openNodeSubmenu();
      handlers.onNodeMenuDragStart(ev, wasOpen);
    });
    wrap.appendChild(parent);
    palette.insertBefore(wrap, undoSection);
    toolSections.push(wrap);
    nodeParentBtn = parent;
  }

  function rebuildPaletteTools(): void {
    palette.classList.toggle("no-tool-labels", !effectiveFeature("node"));
    toolButtons.clear();
    closeNodeSubmenu();
    nodeParentBtn = null;
    for (const section of toolSections) {
      section.remove();
    }
    toolSections.length = 0;
    const transformTools = TRANSFORM_PALETTE_TOOLS.filter(([tool]: PaletteEntry): boolean =>
      effectiveFeature("node") || !isNodeGatedTool(tool),
    );
    const sections: PaletteEntry[][] = [
      [...EMITTER_PALETTE_TOOLS],
      SHAPE_PALETTE_TOOLS.filter(([tool]: PaletteEntry): boolean => tool !== "funnel" || effectiveFeature("3d")),
      ...(effectiveFeature("physics") ? [[...PHYSICS_PALETTE_TOOLS]] : []),
      transformTools,
    ].filter((section: PaletteEntry[]): boolean => section.length > 0);
    sections.forEach((section: PaletteEntry[], idx: number): void => {
      const wrap = document.createElement("div");
      wrap.className = "simple-palette-section";
      if (idx > 0) {
        wrap.appendChild(makePaletteDivider());
      }
      for (const [tool, label, icon, descKey] of section) {
        const btn = document.createElement("button");
        btn.className = "simple-palette-btn simple-palette-tool";
        btn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${icon}</svg>`;
        setPaletteBtnLabel(btn, label, descKey);
        btn.addEventListener("pointerdown", (ev: PointerEvent): void => {
          ev.preventDefault();
          btn.setPointerCapture(ev.pointerId);
          handlers.onPaletteDragStart(tool, ev);
        });
        wrap.appendChild(btn);
        toolButtons.set(tool, btn);
      }
      palette.insertBefore(wrap, undoSection);
      toolSections.push(wrap);
    });
    if (effectiveFeature("node")) {
      buildNodeSection();
    }
    syncPaletteWrapDividers();
  }

  const undoSection = document.createElement("div");
  undoSection.className = "simple-palette-section";
  undoSection.appendChild(makePaletteDivider());
  palette.appendChild(undoSection);
  const UNDO_ICON = `<path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" fill="currentColor"/>`;
  const REDO_ICON = `<path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" fill="currentColor"/>`;
  let undoBtn = document.createElement("button");
  undoBtn.className = "simple-palette-btn simple-palette-tool";
  undoBtn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${UNDO_ICON}</svg>`;
  setPaletteBtnLabel(undoBtn, "action.undo");
  undoBtn.disabled = true;
  undoBtn.addEventListener("click", (): void => handlers.onUndo());
  undoSection.appendChild(undoBtn);
  let redoBtn = document.createElement("button");
  redoBtn.className = "simple-palette-btn simple-palette-tool";
  redoBtn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${REDO_ICON}</svg>`;
  setPaletteBtnLabel(redoBtn, "action.redo");
  redoBtn.disabled = true;
  redoBtn.addEventListener("click", (): void => handlers.onRedo());
  undoSection.appendChild(redoBtn);
  const COPY_ICON = `<path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>`;
  const PASTE_ICON = `<path d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z" fill="currentColor"/>`;
  const copyBtn = document.createElement("button");
  copyBtn.className = "simple-palette-btn simple-palette-tool";
  copyBtn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${COPY_ICON}</svg>`;
  setPaletteBtnLabel(copyBtn, "action.copy");
  copyBtn.disabled = true;
  copyBtn.addEventListener("click", (): void => handlers.onCopy());
  undoSection.appendChild(copyBtn);
  const pasteBtn = document.createElement("button");
  pasteBtn.className = "simple-palette-btn simple-palette-tool";
  pasteBtn.innerHTML = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">${PASTE_ICON}</svg>`;
  setPaletteBtnLabel(pasteBtn, "action.paste");
  pasteBtn.disabled = true;
  pasteBtn.addEventListener("pointerdown", (ev: PointerEvent): void => {
    ev.preventDefault();
    pasteBtn.setPointerCapture(ev.pointerId);
    handlers.onPasteDragStart(ev);
    showPaletteHintEl(pasteBtn);
  });
  undoSection.appendChild(pasteBtn);
  window.addEventListener("resize", (): void => syncPaletteWrapDividers());
  requestAnimationFrame((): void => syncPaletteWrapDividers());

  function setUndoRedoEnabled(canUndo: boolean, canRedo: boolean): void {
    undoBtn.disabled = !canUndo;
    redoBtn.disabled = !canRedo;
  }

  function setCopyPasteEnabled(canCopy: boolean, canPaste: boolean): void {
    copyBtn.disabled = !canCopy;
    pasteBtn.disabled = !canPaste;
  }

  function showPaletteHint(tool: Tool): void {
    const btn = toolButtons.get(tool) ?? submenuButtons.get(tool);
    if (!btn) return;
    showPaletteHintEl(btn);
  }

  function showPaletteHintEl(btn: HTMLElement): void {
    const rect = btn.getBoundingClientRect();
    const NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("width", "100");
    svg.setAttribute("height", "52");
    svg.style.left = `${rect.right}px`;
    svg.style.top = `${rect.top + rect.height / 2 - 26}px`;
    svg.classList.add("palette-hint-svg");
    const defs = document.createElementNS(NS, "defs");
    const marker = document.createElementNS(NS, "marker");
    marker.setAttribute("id", "palette-hint-head");
    marker.setAttribute("markerWidth", "8");
    marker.setAttribute("markerHeight", "6");
    marker.setAttribute("refX", "7");
    marker.setAttribute("refY", "3");
    marker.setAttribute("orient", "auto");
    const head = document.createElementNS(NS, "path");
    head.setAttribute("d", "M 0 0 L 8 3 L 0 6");
    head.setAttribute("stroke", "white");
    head.setAttribute("fill", "none");
    head.setAttribute("stroke-width", "1.5");
    marker.appendChild(head);
    defs.appendChild(marker);
    svg.appendChild(defs);
    const arc = document.createElementNS(NS, "path");
    arc.setAttribute("d", "M 5 30 Q 50 4 92 30");
    arc.setAttribute("stroke", "rgba(255,255,255,0.9)");
    arc.setAttribute("stroke-width", "2");
    arc.setAttribute("fill", "none");
    arc.setAttribute("stroke-linecap", "round");
    arc.setAttribute("marker-end", "url(#palette-hint-head)");
    svg.appendChild(arc);
    document.body.appendChild(svg);
    setTimeout((): void => svg.remove(), 1000);
  }

  const view = viewSub;
  const noteNameToggle = addCheckbox(view, t("view.noteNames"), init.showNoteName, (v: boolean): void =>
    handlers.onShowNoteName(v),
  );
  tipRow(noteNameToggle.row, "noteNames");
  const languageSelect = addSelectRow(view, t("settings.language"), [
    ["en", t("settings.language.en")],
    ["ja", t("settings.language.ja")],
  ]);
  tipRow(languageSelect.parentElement as HTMLElement, "language");
  languageSelect.value = init.language;
  languageSelect.addEventListener("change", (): void => handlers.onLanguage(languageSelect.value as Language));

  let refreshWallRows: () => void = (): void => {};
  let radialRefreshHook: () => void = (): void => {};
  function applyFeatureVisibility(): void {
    setDimensionToggleVisible(effectiveFeature("3d"));
    rebuildPaletteTools();
    refreshWallRows();
    radialRefreshHook();
  }
  applyFeatureVisibility();

  const particleSection = particleSub;
  addSlider(
    particleSection,
    "size",
    0.01,
    0.6,
    0.01,
    init.pointSize,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onPointSize(v),
  );
  addSlider(
    particleSub,
    "kill dist",
    FALL_KILL_DIST_MIN,
    FALL_KILL_DIST_MAX,
    1,
    init.fallKillDistance,
    (v: number): string => (v >= FALL_KILL_DIST_MAX ? "off" : decimalFmt(2)(v)),
    (v: number): void => handlers.onFallKillDist(v),
  );
  addSlider(
    particleSub,
    "kill time",
    FALL_KILL_LIFE_DIV_MIN,
    FALL_KILL_LIFE_DIV_MAX,
    1,
    init.fallKillLifeDivisor,
    (v: number): string => `life/${v.toFixed(0)}`,
    (v: number): void => handlers.onFallKillLifeDiv(v),
  );

  const editSection = editSub;
  const editSnapSelect = addSelectRow(editSection, t("edit.snap"), [
    ["0.1", "100mm"],
    ["0.01", "10mm"],
    ["0.001", "1mm"],
  ]);
  tipRow(editSnapSelect.parentElement as HTMLElement, "snap");
  editSnapSelect.value = init.editSnap;
  editSnapSelect.addEventListener("change", (): void => handlers.onEditSnap(editSnapSelect.value as EditSnap));
  const communitySection = communitySub;
  addCheckbox(
    communitySection,
    t("settings.communitySceneCameraEnabled"),
    init.communitySceneCameraEnabled,
    (v: boolean): void => handlers.onCommunitySceneCameraEnabled(v),
  );
  addCheckbox(
    communitySection,
    t("settings.communitySceneObjectPlacement"),
    init.communitySceneObjectPlacement,
    (v: boolean): void => handlers.onCommunitySceneObjectPlacement(v),
  );

  const clockSection = addSection("Clock", clockHoverPanel);
  const gravitySlider = addSlider(
    clockSection,
    t("param.gravity"),
    0,
    30,
    0.5,
    -init.gravityY,
    (v: number): string => v.toFixed(1),
    (v: number): void => handlers.onGravityY(v),
  );
  tipRow(gravitySlider.row, "gravity");
  const tempoSlider = addSlider(
    clockSection,
    t("param.tempo"),
    CLOCK_BPM_MIN,
    CLOCK_BPM_MAX,
    1,
    60 / init.clockPeriod,
    decimalFmt(2, " BPM"),
    (v: number): void => {
      currentClockPeriod = 60 / v;
      handlers.onClockPeriod(currentClockPeriod);
    },
  );
  tipRow(tempoSlider.row, "tempo");
  const tapRow = document.createElement("div");
  tapRow.className = "row";
  const tapBtn = document.createElement("button");
  tapBtn.textContent = t("clock.tapTempo");
  let tapTimes: number[] = [];
  let tapResetTimer = 0;
  const commitTapPeriod = (period: number): void => {
    const bpm = Math.min(CLOCK_BPM_MAX, Math.max(CLOCK_BPM_MIN, 60 / period));
    currentClockPeriod = 60 / bpm;
    tempoSlider.set(bpm);
    handlers.onClockPeriod(currentClockPeriod);
  };
  tapBtn.addEventListener("click", (): void => {
    const now = performance.now();
    if (tapTimes.length > 0 && now - tapTimes[tapTimes.length - 1] > TAP_TEMPO_TIMEOUT_MS) {
      tapTimes = [];
    }
    tapTimes.push(now);
    tapBtn.textContent = t("clock.measuring");
    window.clearTimeout(tapResetTimer);
    tapResetTimer = window.setTimeout((): void => {
      tapBtn.textContent = t("clock.tapTempo");
    }, TAP_TEMPO_TIMEOUT_MS);
    if (tapTimes.length < TAP_TEMPO_MIN_TAPS) {
      return;
    }
    const span = tapTimes[tapTimes.length - 1] - tapTimes[0];
    const avgIntervalSec = span / (tapTimes.length - 1) / 1000;
    commitTapPeriod(avgIntervalSec);
  });
  tapRow.append(tapBtn);
  clockSection.appendChild(tapRow);
  const resetRow = document.createElement("div");
  resetRow.className = "row";
  const resetBtn = document.createElement("button");
  resetBtn.textContent = t("clock.reset");
  resetBtn.addEventListener("click", (): void => {
    handlers.onClockReset();
  });
  resetRow.append(resetBtn);
  clockSection.appendChild(resetRow);
  const stopOnBlurToggle = addCheckbox(
    playbackSub,
    t("playback.stopInBackground"),
    init.stopOnBlur,
    (v: boolean): void => {
      stopOnBlurBeforeRoom = v;
      handlers.onStopOnBlur(v);
    },
  );
  stopOnBlurInput = stopOnBlurToggle.input;
  stopOnBlurRow = stopOnBlurToggle.row;
  tipRow(stopOnBlurToggle.row, "stopInBackground");
  const tutorialRow = document.createElement("div");
  tutorialRow.className = "row";
  tutorialStartBtn = document.createElement("button");
  tutorialStartBtn.textContent = t("tutorial.menu");
  tutorialStartBtn.addEventListener("click", (): void => handlers.onShowTutorial());
  tutorialRow.append(tutorialStartBtn);
  personal.section.appendChild(tutorialRow);
  syncStopOnBlurLock();

  const panelTitle = document.createElement("div");
  panelTitle.className = "panel-object-title";
  panelTitle.style.cursor = "text";
  objectPanel.appendChild(panelTitle);

  let currentDisplayTitle = "";
  let currentRawName = "";
  let panelRenameInput: HTMLInputElement | null = null;

  function endRename(commit: boolean): void {
    if (!panelRenameInput) {
      return;
    }
    const name = panelRenameInput.value.trim();
    panelTitle.textContent = currentDisplayTitle;
    panelRenameInput = null;
    if (commit) {
      handlers.onName(name);
    }
  }

  panelTitle.addEventListener("click", (): void => {
    if (panelRenameInput) {
      return;
    }
    const input = document.createElement("input");
    input.type = "text";
    input.className = "panel-title-input";
    input.value = currentRawName;
    panelTitle.textContent = "";
    panelTitle.appendChild(input);
    panelRenameInput = input;
    input.addEventListener("keydown", (ev: KeyboardEvent): void => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        endRename(true);
      } else if (ev.key === "Escape") {
        endRename(false);
      }
    });
    input.addEventListener("blur", (): void => { endRename(true); });
    input.focus();
    input.select();
  });

  /**
   * Commits an in-progress rename on any pointerdown outside the input, in the
   * capture phase so it runs before the canvas selection handlers change the
   * selected entity. A click on the canvas does not blur the input (the canvas
   * is not focusable), so without this the pending name would reach syncControls
   * only via its discard path and be lost; committing here applies the name to
   * the still-current entity, and the later blur/syncControls become no-ops.
   */
  window.addEventListener("pointerdown", (ev: PointerEvent): void => {
    if (panelRenameInput && ev.target !== panelRenameInput) {
      endRename(true);
    }
  }, true);

  const funnelShapeSection = addSection("Shape", objectPanel, "funnel-shape");
  const objectPhysicsSection = addSection("Physics", objectPanel, "object-physics");
  const objectContactSection = addSection("Contact", objectPanel, "object-contact");
  const objectAudioSection = addSection("Audio", objectPanel, "object-audio");
  const railAudioSection = addSection("Audio", objectPanel, "rail-audio");
  const forceFieldSection = addSection("Field", objectPanel, "force-field");
  const operatorSection = addSection("Operator", objectPanel);
  const timerSection = addSection("Timer", objectPanel);
  const decaySection = addSection("Decay", objectPanel);
  const delaySection = addSection("Delay", objectPanel);
  const routerSection = addSection("Router", objectPanel);
  const arraySection = addSection("Array", objectPanel);
  const curveSection = addSection("Curve", objectPanel);
  const buttonSection = addSection("Button", objectPanel);
  const lampSection = addSection("Lamp", objectPanel);
  const knobSection = addSection("Knob", objectPanel);
  const timbreNodeSection = addSection("Timbre", objectPanel);
  const countOwnerSection = addSection("Owner", objectPanel);
  const countOwnerRow = document.createElement("div");
  countOwnerRow.className = "row count-owner-row";
  attachTooltip(countOwnerRow, (): string => t("count.ownerSyncHint"));
  const countOwnerSwatch = document.createElement("span");
  countOwnerSwatch.className = "count-owner-swatch";
  countOwnerSwatch.style.cssText = "display:inline-block;width:0.8em;height:0.8em;border-radius:50%;margin-right:0.4em;vertical-align:middle";
  const countOwnerName = document.createElement("span");
  countOwnerRow.append(countOwnerSwatch, countOwnerName);
  countOwnerSection.appendChild(countOwnerRow);
  const orbitToggleBox = document.createElement("div");
  orbitToggleBox.className = "orbit-toggles";
  objectPanel.appendChild(orbitToggleBox);
  const rotationSection = addSection("Rotation", objectPanel);
  const waveSection = addSection("Wave", objectPanel);

  type GroupBulkParamSpec =
    | { name: string; control: "slider"; min: number; max: number; step: number; format: (v: number) => string }
    | { name: string; control: "select"; options: [number, string][] };
  const num2 = (v: number): string => v.toFixed(2);
  const massBulkFmt = (v: number): string => `${v.toFixed(1)} ${v > 0 ? "att" : v < 0 ? "rep" : "off"}`;
  const secFmt = (v: number): string => `${v.toFixed(1)}s`;
  const timbreOptions = TIMBRE_CATALOG.map((e, i): [number, string] => [i, e.name]);
  const railTimbreOptions = RAIL_TIMBRE_CATALOG.map((e, i): [number, string] => [i, e.name]);
  const colliderBulkParams: GroupBulkParamSpec[] = [
    { name: "bounce", control: "slider", min: 0, max: 1, step: 0.05, format: num2 },
    { name: "friction", control: "slider", min: 0, max: 1, step: 0.05, format: num2 },
    { name: "mass", control: "slider", min: -MASS_MAX, max: MASS_MAX, step: 0.1, format: massBulkFmt },
    { name: "timbre", control: "select", options: timbreOptions },
  ];
  const lampBulkParams: GroupBulkParamSpec[] = [
    { name: "value", control: "slider", min: 0, max: 1, step: 0.01, format: num2 },
    { name: "hue", control: "slider", min: 0, max: 1, step: 0.01, format: (v: number): string => `${Math.round(v * 360)}°` },
    { name: "sat", control: "slider", min: 0, max: 1, step: 0.01, format: num2 },
  ];
  const railBulkParams: GroupBulkParamSpec[] = [
    { name: "timbre", control: "select", options: railTimbreOptions },
  ];
  const emitterBulkParams: GroupBulkParamSpec[] = [
    { name: "spread", control: "slider", min: 0, max: 90, step: 1, format: decimalFmt(1, "°") },
    { name: "speed", control: "slider", min: 0, max: 30, step: 0.5, format: (v: number): string => v.toFixed(1) },
    { name: "spd range", control: "slider", min: 0, max: 30, step: 0.5, format: (v: number): string => v.toFixed(1) },
    { name: "life", control: "slider", min: 0.2, max: LIFESPAN_SLIDER_MAX, step: 0.1, format: secFmt },
    { name: "life range", control: "slider", min: 0, max: 12, step: 0.1, format: secFmt },
    { name: "bounce", control: "slider", min: 0, max: 1, step: 0.05, format: num2 },
    { name: "friction", control: "slider", min: 0, max: 1, step: 0.05, format: num2 },
    { name: "drag", control: "slider", min: 0, max: EMIT_DRAG_MAX, step: 0.1, format: (v: number): string => v.toFixed(1) },
  ];
  const groupBulkTypeSpecs: { type: BulkKind; params: GroupBulkParamSpec[] }[] = [
    { type: "box", params: colliderBulkParams },
    { type: "sphere", params: colliderBulkParams },
    { type: "funnel", params: colliderBulkParams },
    { type: "rail", params: railBulkParams },
    { type: "lamp", params: lampBulkParams },
    { type: "stream", params: emitterBulkParams },
    { type: "sequencer", params: emitterBulkParams },
  ];
  interface GroupBulkRow {
    name: string;
    row: HTMLElement;
    apply: (state: ParamState) => void;
  }

  function buildBulkRow(parent: HTMLElement, type: BulkKind, spec: GroupBulkParamSpec): GroupBulkRow {
    if (spec.control === "select") {
      const select = addSelectRow(parent, paramLabel(spec.name), [
        ["mixed", "—"],
        ...spec.options.map(([value, label]): [string, string] => [String(value), label]),
      ]);
      const row = select.parentElement as HTMLElement;
      tipRow(row, spec.name);
      select.addEventListener("change", (): void => {
        if (select.value !== "mixed") {
          handlers.onGroupBulkParam(type, spec.name, Number(select.value));
        }
      });
      const apply = (state: ParamState): void => {
        const editable = state.kind === "uniform";
        select.disabled = !editable;
        row.classList.toggle("disabled", !editable);
        row.dataset.driven = state.kind === "bound" ? "1" : "";
        select.value = state.kind === "uniform" ? String(state.value) : "mixed";
      };
      return { name: spec.name, row, apply };
    }
    const slider = addSlider(
      parent,
      paramLabel(spec.name),
      spec.min,
      spec.max,
      spec.step,
      spec.min,
      spec.format,
      (v: number): void => handlers.onGroupBulkParam(type, spec.name, v),
    );
    tipRow(slider.row, spec.name);
    const out = slider.row.querySelector(".row-value") as HTMLElement;
    const apply = (state: ParamState): void => {
      const editable = state.kind === "uniform";
      slider.input.disabled = !editable;
      slider.row.classList.toggle("disabled", !editable);
      slider.row.dataset.driven = state.kind === "bound" ? "1" : "";
      if (state.kind === "uniform") {
        slider.set(state.value);
      } else {
        out.textContent = "—";
      }
    };
    return { name: spec.name, row: slider.row, apply };
  }

  const groupBulkSection = addSection("Bulk", objectPanel, "group-bulk");
  const BULK_TYPE_LABELS: Record<string, MessageKey> = {
    box: "tool.box",
    sphere: "tool.sphere",
    funnel: "tool.funnel",
    rail: "tool.rail",
    lamp: "tool.lamp",
    stream: "tool.stream",
    sequencer: "tool.sequencer",
  };
  const groupBulkUi = new Map<BulkKind, { header: HTMLElement; rows: GroupBulkRow[] }>();
  for (const typeSpec of groupBulkTypeSpecs) {
    const header = document.createElement("div");
    header.className = "section-title section-title-plain";
    header.textContent = t(BULK_TYPE_LABELS[typeSpec.type]);
    groupBulkSection.appendChild(header);
    const rows = typeSpec.params.map((spec: GroupBulkParamSpec): GroupBulkRow => buildBulkRow(groupBulkSection, typeSpec.type, spec));
    groupBulkUi.set(typeSpec.type, { header, rows });
  }

  /**
   * Drives the per-type bulk-edit rows from the selected group's aggregated
   * model: a type section shows only when ≥2 of that kind are present, each
   * param row is an editable control for a uniform value or a disabled "—" for a
   * mixed / wire-driven param. Returns whether any row is visible so the caller
   * can hide the whole section for a group with no bulk-editable kind.
   */
  function applyGroupBulk(model: GroupBulkType[]): boolean {
    const byType = new Map<BulkKind, GroupBulkType>();
    for (const entry of model) {
      byType.set(entry.type, entry);
    }
    let anyShown = false;
    for (const [type, ui] of groupBulkUi) {
      const entry = byType.get(type);
      ui.header.style.display = entry ? "" : "none";
      for (const row of ui.rows) {
        const state = entry?.params.find((p): boolean => p.name === row.name)?.state;
        if (!state) {
          row.row.style.display = "none";
          continue;
        }
        row.row.style.display = "";
        row.apply(state);
        anyShown = true;
      }
    }
    return anyShown;
  }

  const groupExprOverlay = document.createElement("div");
  groupExprOverlay.className = "panel group-expr-overlay";
  groupExprOverlay.style.display = "none";

  const arcSlider = addSlider(
    funnelShapeSection,
    t("param.arc"),
    30,
    360,
    5,
    360,
    decimalFmt(1, "°"),
    (v: number): void => handlers.onArc(v),
  );
  tipRow(arcSlider.row, "arc");

  const bounce = addSlider(
    objectPhysicsSection,
    t("param.bounce"),
    0,
    1,
    0.05,
    init.bounce,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onBounce(v),
  );
  const friction = addSlider(
    objectPhysicsSection,
    t("param.friction"),
    0,
    1,
    0.05,
    init.friction,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onFriction(v),
  );
  const massSlider = addSlider(
    objectPhysicsSection,
    t("param.mass"),
    -MASS_MAX,
    MASS_MAX,
    0.1,
    init.mass,
    (v: number): string => `${v.toFixed(1)} ${v > 0 ? "att" : v < 0 ? "rep" : "off"}`,
    (v: number): void => handlers.onMass(v),
  );
  tipRow(bounce.row, "bounce");
  tipRow(friction.row, "friction");
  tipRow(massSlider.row, "mass");
  const massToggle = addCheckbox(objectPhysicsSection, t("param.massOn"), false, (v: boolean): void => handlers.onMassEnabled(v));
  const killToggle = addCheckbox(objectContactSection, t("param.killOnHit"), false, (v: boolean): void => handlers.onKillOnContact(v));
  const collisionToggle = addCheckbox(objectContactSection, t("param.collisionOn"), true, (v: boolean): void => handlers.onCollisionEnabled(v));
  tipRow(massToggle.row, "massOn");
  tipRow(killToggle.row, "killOnHit");
  tipRow(collisionToggle.row, "collisionOn");

  const operatorSelect = addSelectRow(
    operatorSection,
    t("param.operator"),
    VALUE_OPERATORS.map((o: ValueOperator): [string, string] => [o, OPERATOR_LABELS[o]]),
  );
  tipRow(operatorSelect.parentElement as HTMLElement, "operator");
  operatorSelect.addEventListener("change", (): void => handlers.onValueOperator(operatorSelect.value as ValueOperator));

  const operandSlider = addSlider(
    operatorSection,
    t("port.input2"),
    -10,
    10,
    0.1,
    0,
    formatSig4,
    (v: number): void => handlers.onValueOperand(v),
    undefined,
    undefined,
    true,
  );

  const timerModeSelect = addSelectRow(timerSection, t("param.mode"), [
    ["time", t("timer.modeTime")],
    ["loop", t("timer.modeLoop")],
  ]);
  tipRow(timerModeSelect.parentElement as HTMLElement, "timerMode");
  timerModeSelect.addEventListener("change", (): void => handlers.onTimerMode(timerModeSelect.value as TimerMode));

  const buttonModeSelect = addSelectRow(buttonSection, t("param.mode"), [
    ["push", t("button.modePush")],
    ["toggle", t("button.modeToggle")],
  ]);
  tipRow(buttonModeSelect.parentElement as HTMLElement, "buttonMode");
  buttonModeSelect.addEventListener("change", (): void => handlers.onButtonMode(buttonModeSelect.value as ButtonMode));
  const buttonPulseOnResetToggle = addCheckbox(
    buttonSection,
    t("button.pulseOnReset"),
    false,
    (v: boolean): void => handlers.onButtonPulseOnReset(v),
  );
  tipRow(buttonPulseOnResetToggle.row, "buttonPulseOnReset");

  const lampValueSlider = addSlider(
    lampSection,
    t("param.value"),
    0,
    1,
    0.01,
    0,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onLampValue(v),
  );
  const lampHueSlider = addSlider(
    lampSection,
    t("param.hue"),
    0,
    1,
    0.01,
    0,
    (v: number): string => `${Math.round(v * 360)}°`,
    (v: number): void => handlers.onLampHue(v),
  );
  const lampSatSlider = addSlider(
    lampSection,
    t("param.saturation"),
    0,
    1,
    0.01,
    0,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onLampSat(v),
  );
  tipRow(lampValueSlider.row, "value");
  tipRow(lampHueSlider.row, "hue");
  tipRow(lampSatSlider.row, "sat");

  const KNOB_PARAM_SLIDER_RANGE = 100;
  const knobRangeFmt = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(2));
  const knobMinRow = addSlider(
    knobSection,
    t("param.min"),
    -KNOB_PARAM_SLIDER_RANGE,
    KNOB_PARAM_SLIDER_RANGE,
    0.1,
    KNOB_MIN_DEFAULT,
    knobRangeFmt,
    (v: number): void => handlers.onKnobMin(v),
    undefined,
    undefined,
    true,
  );
  const knobMaxRow = addSlider(
    knobSection,
    t("param.max"),
    -KNOB_PARAM_SLIDER_RANGE,
    KNOB_PARAM_SLIDER_RANGE,
    0.1,
    KNOB_MAX_DEFAULT,
    knobRangeFmt,
    (v: number): void => handlers.onKnobMax(v),
    undefined,
    undefined,
    true,
  );
  const knobIntToggle = addCheckbox(knobSection, t("param.int"), false, (v: boolean): void => {
    handlers.onKnobInt(v);
  });

  const decaySlider = addSlider(
    decaySection,
    t("param.time"),
    0,
    DECAY_MAX_S,
    0.01,
    DECAY_DEFAULT_S,
    (v: number): string => `${v.toFixed(2)}s`,
    (v: number): void => handlers.onDecayS(v),
  );
  tipRow(decaySlider.row, "decayTime");

  const delaySlider = addSlider(
    delaySection,
    t("param.time"),
    0,
    DELAY_MAX_S,
    0.01,
    DELAY_DEFAULT_S,
    (v: number): string => `${v.toFixed(2)}s`,
    (v: number): void => handlers.onDelayS(v),
  );
  tipRow(delaySlider.row, "delayTime");

  const routerHoldSlider = addSlider(
    routerSection,
    t("param.hold"),
    0,
    ROUTER_HOLD_MAX_S,
    0.01,
    ROUTER_HOLD_DEFAULT_S,
    (v: number): string => `${v.toFixed(2)}s`,
    (v: number): void => {
      handlers.onRouterHoldS(v);
      applyRouterResetGate(v);
    },
  );
  tipRow(routerHoldSlider.row, "routerHold");
  const routerResetToggle = addCheckbox(
    routerSection,
    t("router.resetOnPulse"),
    false,
    (v: boolean): void => handlers.onRouterHoldResetOnPulse(v),
  );
  tipRow(routerResetToggle.row, "routerReset");
  const applyRouterResetGate = (holdS: number): void => {
    const off = holdS <= 0;
    routerResetToggle.input.disabled = off;
    routerResetToggle.row.classList.toggle("disabled", off);
  };

  let seqPanelTarget: ObjectTemplate | null = null;
  const clampArrayMin = (seqMax: number, next: number): number =>
    Math.min(seqMax, Math.max(next, seqMax - ARRAY_ROWS_MAX + 1));
  const clampArrayMax = (seqMin: number, next: number): number =>
    Math.max(seqMin, Math.min(next, seqMin + ARRAY_ROWS_MAX - 1));
  function setSeqParam(key: "seqMin" | "seqMax" | "seqLen" | "seqCycleLength", value: number): void {
    if (seqPanelTarget === null || !seqPanelTarget.id) {
      return;
    }
    let next = value;
    if (key === "seqMin") {
      next = seqPanelTarget.type === "array"
        ? clampArrayMin(seqPanelTarget.seqMax, Math.round(value))
        : Math.min(value, seqPanelTarget.seqMax);
      seqPanelTarget.seqMin = next;
    } else if (key === "seqMax") {
      next = seqPanelTarget.type === "array"
        ? clampArrayMax(seqPanelTarget.seqMin, Math.round(value))
        : Math.max(value, seqPanelTarget.seqMin);
      seqPanelTarget.seqMax = next;
    } else if (key === "seqLen") {
      next = Math.max(1, Math.min(ARRAY_LEN_MAX, Math.round(value)));
      seqPanelTarget.seqLen = next;
    } else {
      next = Math.max(1, Math.round(value));
      seqPanelTarget.seqCycleLength = next;
    }
    handlers.onSetSeqParam(seqPanelTarget.id, key, next);
  }
  const seqIntFmt = (v: number): string => v.toFixed(0);
  const seqCurveFmt = decimalFmt(2);
  const arrayMinSlider = addSlider(
    arraySection,
    t("param.min"),
    -32,
    32,
    1,
    0,
    seqIntFmt,
    (v: number): void => setSeqParam("seqMin", v),
    undefined,
    undefined,
    true,
  );
  const arrayMaxSlider = addSlider(
    arraySection,
    t("param.max"),
    -32,
    32,
    1,
    7,
    seqIntFmt,
    (v: number): void => setSeqParam("seqMax", v),
    undefined,
    undefined,
    true,
  );
  const arrayLenSlider = addSlider(
    arraySection,
    t("param.length"),
    1,
    ARRAY_LEN_MAX,
    1,
    8,
    seqIntFmt,
    (v: number): void => setSeqParam("seqLen", v),
  );
  const curveMinSlider = addSlider(
    curveSection,
    t("param.min"),
    -4,
    4,
    0.05,
    0,
    seqCurveFmt,
    (v: number): void => setSeqParam("seqMin", Number(v.toFixed(2))),
    undefined,
    undefined,
    true,
  );
  const curveMaxSlider = addSlider(
    curveSection,
    t("param.max"),
    -4,
    4,
    0.05,
    1,
    seqCurveFmt,
    (v: number): void => setSeqParam("seqMax", Number(v.toFixed(2))),
    undefined,
    undefined,
    true,
  );
  const curveLenSlider = addSlider(
    curveSection,
    t("param.length"),
    1,
    128,
    1,
    16,
    seqIntFmt,
    (v: number): void => setSeqParam("seqCycleLength", v),
    undefined,
    undefined,
    true,
  );
  tipRow(arrayMinSlider.row, "min");
  tipRow(arrayMaxSlider.row, "max");
  tipRow(arrayLenSlider.row, "length");
  tipRow(curveMinSlider.row, "min");
  tipRow(curveMaxSlider.row, "max");
  tipRow(curveLenSlider.row, "length");

  const semitoneOffsetFmt = (v: number): string => {
    const n = Math.round(v);
    return n > 0 ? `+${n}` : `${n}`;
  };
  const timbreSetRow = document.createElement("div");
  timbreSetRow.className = "row timbre-set-row";
  const timbreSetLabel = document.createElement("span");
  timbreSetLabel.className = "row-label";
  timbreSetLabel.textContent = t("param.timbre");
  const timbreSetChips = document.createElement("div");
  timbreSetChips.className = "timbre-set-chips";
  const timbreSetButtons = new Map<Timbre, HTMLButtonElement>();
  let timbreSetState: Timbre[] = [];
  function renderTimbreSet(): void {
    for (const [id, btn] of timbreSetButtons) {
      btn.classList.toggle("timbre-chip-on", timbreSetState.includes(id));
    }
  }
  for (const entry of TIMBRE_CATALOG) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "timbre-chip";
    chip.textContent = entry.name;
    chip.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
    chip.addEventListener("click", (): void => {
      timbreSetState = timbreSetState.includes(entry.id)
        ? timbreSetState.filter((x: Timbre): boolean => x !== entry.id)
        : [...timbreSetState, entry.id];
      renderTimbreSet();
      handlers.onTimbreSet([...timbreSetState]);
    });
    timbreSetButtons.set(entry.id, chip);
    timbreSetChips.appendChild(chip);
  }
  timbreSetRow.append(timbreSetLabel, timbreSetChips);
  timbreNodeSection.appendChild(timbreSetRow);
  tipRow(timbreSetRow, "timbre");
  const timbreVolumeSlider = addSlider(
    timbreNodeSection,
    t("param.volume"),
    0,
    1,
    0.01,
    1,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onTimbreParam("volume", v),
  );
  tipRow(timbreVolumeSlider.row, "volume");
  const timbreTransposeSlider = addSlider(
    timbreNodeSection,
    t("param.transpose"),
    -TRANSPOSE_RANGE,
    TRANSPOSE_RANGE,
    1,
    0,
    semitoneOffsetFmt,
    (v: number): void => handlers.onTimbreParam("transpose", Math.round(v)),
  );
  tipRow(timbreTransposeSlider.row, "transpose");

  let micPanelPitchIndex = 0;
  const micRow = document.createElement("div");
  micRow.className = "row mic-manage";
  const micAddBtn = document.createElement("button");
  micAddBtn.className = "mic-btn";
  micAddBtn.textContent = t("record.add");
  micAddBtn.addEventListener("click", (): void => showRecordModal(handlers.onRecordSimPause));
  const micPrevBtn = document.createElement("button");
  micPrevBtn.className = "mic-btn";
  micPrevBtn.textContent = "◀";
  attachTooltip(micPrevBtn, (): string => t("record.reorder"));
  micPrevBtn.addEventListener("click", (): void => void reorderSelectedMic(-1));
  const micNextBtn = document.createElement("button");
  micNextBtn.className = "mic-btn";
  micNextBtn.textContent = "▶";
  attachTooltip(micNextBtn, (): string => t("record.reorder"));
  micNextBtn.addEventListener("click", (): void => void reorderSelectedMic(1));
  const micDelBtn = document.createElement("button");
  micDelBtn.className = "mic-btn";
  micDelBtn.textContent = t("record.delete");
  micDelBtn.addEventListener("click", (): void => void removeSelectedMic());
  micRow.append(micAddBtn, micPrevBtn, micNextBtn, micDelBtn);
  objectAudioSection.appendChild(micRow);
  micRow.style.display = "none";

  /** Recording id the selected mic object's current degree resolves to, or null when the shared list is empty. */
  function selectedMicId(): string | null {
    const list = micStore.list();
    if (list.length === 0) {
      return null;
    }
    return list[kitPieceIndex(micPanelPitchIndex, list.length)].id;
  }

  /**
   * Moves the selected recording one slot in the shared list, then re-points the
   * object's degree at the recording's new position so the same recording stays
   * selected (the move walks it through the list rather than swapping the
   * displayed pitch). The store mutation notifies every consumer.
   */
  async function reorderSelectedMic(dir: -1 | 1): Promise<void> {
    const id = selectedMicId();
    if (id === null) {
      return;
    }
    await micStore.reorder(id, dir);
    const newIdx = micStore.list().findIndex((r: MicPitch): boolean => r.id === id);
    if (newIdx >= 0) {
      handlers.onPiece(newIdx);
    }
  }

  async function removeSelectedMic(): Promise<void> {
    const id = selectedMicId();
    if (id === null) {
      return;
    }
    if (loadMicDeleteWarnSuppressed()) {
      await micStore.remove(id);
      return;
    }
    showActionModal<"confirm" | "cancel">(
      t("mic.localDeleteWarn.title"),
      t("mic.localDeleteWarn.body"),
      [
        { label: t("mic.localDeleteWarn.confirm"), value: "confirm" },
        { label: t("mic.localDeleteWarn.cancel"), value: "cancel", subtle: true },
      ],
      (value: "confirm" | "cancel", checked: boolean): void => {
        if (value !== "confirm") {
          return;
        }
        if (checked) {
          saveMicDeleteWarnSuppressed(true);
        }
        void micStore.remove(id);
      },
      { checkbox: { label: t("mic.localDeleteWarn.dontShowAgain") } },
    );
  }

  const railTimbreSelect = addSelectRow(railAudioSection, t("param.timbre"), RAIL_TIMBRE_CATALOG.map((e): [string, string] => [e.id, e.name]));
  tipRow(railTimbreSelect.parentElement as HTMLElement, "timbre");
  railTimbreSelect.addEventListener("change", (): void => {
    handlers.onRailTimbre(railTimbreSelect.value as RailTimbre);
  });
  const railVolumeSlider = addSlider(
    railAudioSection,
    t("param.volume"),
    0,
    1,
    0.05,
    1,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onObjectVolume(v),
  );
  tipRow(railVolumeSlider.row, "volume");
  const timbreSelect = addSelectRow(objectAudioSection, t("param.timbre"), TIMBRE_CATALOG.map((e): [string, string] => [e.id, e.name]));
  tipRow(timbreSelect.parentElement as HTMLElement, "timbre");
  timbreSelect.addEventListener("change", (): void => {
    handlers.onTimbre(timbreSelect.value as Timbre);
  });
  const objMuteToggle = addCheckbox(
    objectAudioSection,
    t("param.mute"),
    false,
    (checked: boolean): void => handlers.onObjectMuted(checked),
  );
  tipRow(objMuteToggle.row, "mute");

  const signedFmt = (v: number): string => `${v > 0 ? "+" : ""}${v.toFixed(1)}`;
  function currentForceParams(): THREE.Vector4 {
    const mode = modeSelect.value === "drag" ? 1 : 0;
    const z = mode === 0 ? parseFloat(fieldStrength.input.value) : parseFloat(fieldOmega.input.value);
    return new THREE.Vector4(parseFloat(fieldR.input.value), mode, z, parseFloat(fieldK.input.value));
  }
  function emitForceSize(): void {
    handlers.onSize(currentForceParams());
  }
  function applyForceModeVisibility(): void {
    const drag = modeSelect.value === "drag";
    fieldStrength.row.style.display = drag ? "none" : "";
    fieldOmega.row.style.display = drag ? "" : "none";
    fieldK.row.style.display = drag ? "" : "none";
  }
  const modeSelect = addSelectRow(forceFieldSection, t("param.mode"), [
    ["lorentz", t("force.modeLorentz")],
    ["drag", t("force.modeDrag")],
  ]);
  modeSelect.addEventListener("change", (): void => {
    emitForceSize();
    applyForceModeVisibility();
  });
  const fieldR = addSlider(forceFieldSection, t("param.radius"), FIELD_RADIUS_MIN, FIELD_RADIUS_MAX, 0.1, DEFAULT_FIELD_RADIUS, (v: number): string => v.toFixed(1), emitForceSize);
  const fieldStrength = addSlider(forceFieldSection, t("param.strength"), -FIELD_STRENGTH_MAX, FIELD_STRENGTH_MAX, 0.5, DEFAULT_FIELD_STRENGTH, signedFmt, emitForceSize);
  const fieldOmega = addSlider(forceFieldSection, "omega", -FIELD_OMEGA_MAX, FIELD_OMEGA_MAX, 0.1, DEFAULT_FIELD_OMEGA, signedFmt, emitForceSize);
  const fieldK = addSlider(forceFieldSection, "k", 0, FIELD_K_MAX, 0.1, DEFAULT_FIELD_K, (v: number): string => v.toFixed(1), emitForceSize);
  const fieldFade = addSlider(forceFieldSection, t("param.fade"), 0, 1, 0.05, DEFAULT_FIELD_FADE, (v: number): string => `${(v * 100).toFixed(0)}%`, (v: number): void => handlers.onFade(v));
  tipRow(modeSelect.parentElement as HTMLElement, "fieldMode");
  tipRow(fieldR.row, "radius");
  tipRow(fieldStrength.row, "strength");
  tipRow(fieldOmega.row, "omega");
  tipRow(fieldK.row, "k");
  tipRow(fieldFade.row, "fade");

  let orbitTargetMode = false;
  const rotFmt = (v: number): string => (orbitTargetMode ? `${v.toFixed(0)}°` : orbitSync ? `${v.toFixed(0)}%` : `${v.toFixed(0)}°/s`);
  const perFmt = (v: number): string => (orbitTargetMode ? `${v.toFixed(2)}s` : orbitSync ? `${v.toFixed(0)}%` : `${v.toFixed(2)}s`);
  const ampFmt = (v: number): string => v.toFixed(2);
  const phaseFmt = (v: number): string => `${v.toFixed(0)}%`;
  function emitOrbit(): void {
    handlers.onOrbit(currentOrbit());
  }
  const orbPlayToggle = addCheckbox(orbitToggleBox, t("param.play"), true, (): void => emitOrbit());
  const orbRotX = addSlider(rotationSection, `${t("param.rot")} x`, -400, 400, 5, 0, rotFmt, emitOrbit);
  const orbRotY = addSlider(rotationSection, `${t("param.rot")} y`, -400, 400, 5, 0, rotFmt, emitOrbit);
  const orbRotZ = addSlider(rotationSection, `${t("param.rot")} z`, -400, 400, 5, 30, rotFmt, emitOrbit);
  const orbAmpX = addSlider(waveSection, `${t("param.amp")} x`, 0, 6, 0.1, 0, ampFmt, emitOrbit);
  const orbAmpY = addSlider(waveSection, `${t("param.amp")} y`, 0, 6, 0.1, 0, ampFmt, emitOrbit);
  const orbAmpZ = addSlider(waveSection, `${t("param.amp")} z`, 0, 6, 0.1, 0, ampFmt, emitOrbit);
  const orbPerX = addSlider(waveSection, `${t("param.period")} x`, 0.1, 8, 0.1, 1, perFmt, emitOrbit);
  const orbPerY = addSlider(waveSection, `${t("param.period")} y`, 0.1, 8, 0.1, 1, perFmt, emitOrbit);
  const orbPerZ = addSlider(waveSection, `${t("param.period")} z`, 0.1, 8, 0.1, 1, perFmt, emitOrbit);
  const orbPhX = addSlider(waveSection, `${t("param.phase")} x`, 0, 100, 1, 0, phaseFmt, emitOrbit);
  const orbPhY = addSlider(waveSection, `${t("param.phase")} y`, 0, 100, 1, 0, phaseFmt, emitOrbit);
  const orbPhZ = addSlider(waveSection, `${t("param.phase")} z`, 0, 100, 1, 0, phaseFmt, emitOrbit);
  tipRow(orbPlayToggle.row, "play");
  for (const s of [orbRotX, orbRotY, orbRotZ]) tipRow(s.row, "rot");
  for (const s of [orbAmpX, orbAmpY, orbAmpZ]) tipRow(s.row, "amp");
  for (const s of [orbPerX, orbPerY, orbPerZ]) tipRow(s.row, "period");
  for (const s of [orbPhX, orbPhY, orbPhZ]) tipRow(s.row, "phase");

  /**
   * Feature-gated orbit rows. 3D off hides the out-of-plane params (rot x/y tilt
   * children out of the z=0 plane, amp/period z vibrate along z — the transform
   * pass also treats those as 0). Phase rows are always shown: orbit motion is
   * always room-period synced, so the phase offset is always meaningful. Phase z
   * still needs 3D (its z vibration is out-of-plane).
   */
  const applyOrbitRowGates = (): void => {
    const show3d = effectiveFeature("3d");
    orbRotX.row.style.display = show3d ? "" : "none";
    orbRotY.row.style.display = show3d ? "" : "none";
    orbAmpZ.row.style.display = show3d ? "" : "none";
    orbPerZ.row.style.display = show3d && !orbitTargetMode ? "" : "none";
    orbPhZ.row.style.display = show3d && !orbitTargetMode ? "" : "none";
  };
  applyOrbitRowGates();

  function setOrbitRange(s: { input: HTMLInputElement }, min: number, max: number, step: number): void {
    s.input.min = String(min);
    s.input.max = String(max);
    s.input.step = String(step);
    s.input.dataset.originalStep = String(step);
  }

  /**
   * Period sliders read % of the room period (5–400, step 1) when synced and
   * absolute seconds (0.1–8, step 0.1) when free; rot/amp/phase keep one range.
   */
  function applyOrbitPeriodRange(): void {
    const lo = orbitSync ? 5 : 0.1;
    const hi = orbitSync ? 400 : 8;
    const step = orbitSync ? 1 : 0.1;
    setOrbitRange(orbPerX, orbitTargetMode ? 0.02 : lo, orbitTargetMode ? 2 : hi, orbitTargetMode ? 0.02 : step);
    setOrbitRange(orbPerY, lo, hi, step);
    setOrbitRange(orbPerZ, lo, hi, step);
  }

  function currentOrbit(): OrbitParams {
    const rotScale = orbitTargetMode ? 1 : orbitSync ? 0.01 : 1;
    const perScale = orbitTargetMode ? 1 : orbitSync ? 0.01 : 1;
    return {
      rotSpeed: new THREE.Vector3(
        parseFloat(orbRotX.input.value) * rotScale,
        parseFloat(orbRotY.input.value) * rotScale,
        parseFloat(orbRotZ.input.value) * rotScale,
      ),
      amp: new THREE.Vector3(
        parseFloat(orbAmpX.input.value),
        parseFloat(orbAmpY.input.value),
        parseFloat(orbAmpZ.input.value),
      ),
      periodPct: new THREE.Vector3(
        parseFloat(orbPerX.input.value) * perScale,
        parseFloat(orbPerY.input.value) * perScale,
        parseFloat(orbPerZ.input.value) * perScale,
      ),
      phasePct: new THREE.Vector3(
        parseFloat(orbPhX.input.value) / 100,
        parseFloat(orbPhY.input.value) / 100,
        parseFloat(orbPhZ.input.value) / 100,
      ),
      clockSync: orbitSync,
      play: orbPlayToggle.input.checked,
    };
  }

  function setOrbitSliders(o: OrbitParams): void {
    orbPlayToggle.input.checked = o.play;
    orbitSync = o.clockSync;
    applyOrbitPeriodRange();
    const rotScale = orbitTargetMode ? 1 : orbitSync ? 100 : 1;
    const perScale = orbitTargetMode ? 1 : orbitSync ? 100 : 1;
    orbRotX.set(o.rotSpeed.x * rotScale);
    orbRotY.set(o.rotSpeed.y * rotScale);
    orbRotZ.set(o.rotSpeed.z * rotScale);
    orbAmpX.set(o.amp.x);
    orbAmpY.set(o.amp.y);
    orbAmpZ.set(o.amp.z);
    orbPerX.set(o.periodPct.x * perScale);
    orbPerY.set(o.periodPct.y * perScale);
    orbPerZ.set(o.periodPct.z * perScale);
    orbPhX.set(o.phasePct.x * 100);
    orbPhY.set(o.phasePct.y * 100);
    orbPhZ.set(o.phasePct.z * 100);
    for (const s of [orbRotX, orbRotY, orbRotZ, orbAmpX, orbAmpY, orbAmpZ, orbPerX, orbPerY, orbPerZ, orbPhX, orbPhY, orbPhZ]) {
      s.set(parseFloat(s.input.value));
    }
  }

  function setRowLabel(s: { row: HTMLElement }, text: string): void {
    const el = s.row.querySelector<HTMLSpanElement>(".row-label");
    if (el) {
      el.textContent = text;
    }
  }
  function setControlHidden(c: { row: HTMLElement }, hidden: boolean): void {
    c.row.style.display = hidden ? "none" : "";
  }

  /**
   * Switches the orbit panel between continuous and target-pose mode. In target
   * mode (value-in wired) rot XYZ are absolute target angles (-360..360°), amp XYZ
   * are signed local target offsets (-50..50), and period X is repurposed as the
   * 0→1 glide time in seconds (its range/format follow from orbitTargetMode);
   * play, period Y/Z and phase are disabled because the wired value's progress,
   * not the clock, drives the pose. Continuous mode restores the original ranges,
   * labels and enabled state. Called before setOrbitSliders so the value snap
   * clamps into the active range.
   */
  function applyOrbitTargetMode(targetMode: boolean): void {
    orbitTargetMode = targetMode;
    const rotLabel = targetMode ? t("param.targetRot") : t("param.rot");
    const ampLabel = targetMode ? t("param.targetPos") : t("param.amp");
    const rotAxes: [{ input: HTMLInputElement; row: HTMLElement; set: (v: number) => void }, string][] = [[orbRotX, "x"], [orbRotY, "y"], [orbRotZ, "z"]];
    const ampAxes: [{ input: HTMLInputElement; row: HTMLElement; set: (v: number) => void }, string][] = [[orbAmpX, "x"], [orbAmpY, "y"], [orbAmpZ, "z"]];
    for (const [s, axis] of rotAxes) {
      setOrbitRange(s, targetMode ? -360 : -400, targetMode ? 360 : 400, 5);
      setRowLabel(s, `${rotLabel} ${axis}`);
    }
    for (const [s, axis] of ampAxes) {
      setOrbitRange(s, targetMode ? -50 : 0, targetMode ? 50 : 6, targetMode ? 0.5 : 0.1);
      setRowLabel(s, `${ampLabel} ${axis}`);
    }
    setRowLabel(orbPerX, targetMode ? t("param.transition") : `${t("param.period")} x`);
    setControlHidden(orbPlayToggle, targetMode);
    for (const s of [orbPerY, orbPhX, orbPhY]) {
      setControlHidden(s, targetMode);
    }
    applyOrbitRowGates();
  }

  const emitSection = addSection("Emit", objectPanel);
  const emitterPhysicsSection = addSection("Physics", objectPanel, "emitter-physics");
  const emitterRows = (): HTMLElement[] => [
    ...emitSection.querySelectorAll<HTMLElement>(".row"),
    ...emitterPhysicsSection.querySelectorAll<HTMLElement>(".row"),
  ];
  const RATE_SLIDER_STEPS = 1000;
  const rateFromSlider = (raw: number): number =>
    EMIT_RATE_MIN * Math.pow(EMIT_RATE_MAX / EMIT_RATE_MIN, raw / RATE_SLIDER_STEPS);
  const sliderFromRate = (rate: number): number =>
    (RATE_SLIDER_STEPS * Math.log(rate / EMIT_RATE_MIN)) / Math.log(EMIT_RATE_MAX / EMIT_RATE_MIN);
  const rateSlider = addSlider(
    emitSection,
    t("param.rate"),
    0,
    RATE_SLIDER_STEPS,
    1,
    sliderFromRate(emitterInit.rate),
    (raw: number): string => `${rateFromSlider(raw).toFixed(0)}/s`,
    (raw: number): void => {
      boundRate = rateFromSlider(raw);
      handlers.onEmitterRate(boundRate);
    },
    undefined,
    { toDisplay: (raw: number): number => Math.round(rateFromSlider(raw)), fromDisplay: sliderFromRate },
  );
  let boundRate = emitterInit.rate;
  const applyClockSyncMode = (on: boolean): void => {
    rateSlider.row.style.display = on ? "none" : "";
  };
  applyClockSyncMode(emitterInit.clockSync);
  const meterSelect = addSelectRow(emitSection, t("param.meter"), [
    ["4", "4/4"],
    ["3", "3/4"],
  ]);
  const meterRow = meterSelect.parentElement as HTMLElement;
  tipRow(meterRow, "meter");
  meterSelect.addEventListener("change", (): void =>
    handlers.onEmitterMeter(meterSelect.value === "3" ? 3 : 4),
  );
  const numSlider = addSlider(
    emitSection,
    t("param.bars"),
    1,
    SEQ_MAX_ROWS,
    1,
    1,
    (v: number): string => v.toFixed(0),
    (v: number): void => handlers.onEmitterNum(Math.round(v) * seqColumns(meterSelect.value === "3" ? 3 : 4)),
  );
  const numRow = numSlider.row;
  meterSelect.value = String(emitterInit.meter);
  numSlider.set(emitterInit.num / seqColumns(emitterInit.meter));
  let currentEmitterKind: "sync" | "full" = emitterInit.kind;
  const applyKindMode = (kind: "sync" | "full"): void => {
    currentEmitterKind = kind;
    const isSync = kind === "sync";
    spdRangeSlider.row.style.display = isSync ? "none" : "";
    lifeRangeSlider.row.style.display = isSync ? "none" : "";
    meterRow.style.display = isSync ? "" : "none";
    numRow.style.display = isSync ? "" : "none";
    applyRateCeiling();
  };
  const spreadSlider = addSlider(
    emitSection,
    t("param.spread"),
    0,
    90,
    1,
    emitterInit.coneDeg,
    decimalFmt(1, "°"),
    (v: number): void => handlers.onEmitterCone(v),
  );
  const pulseSpeedToggle = addCheckbox(
    emitSection,
    t("param.speedFromSettings"),
    emitterInit.pulseSpeedFromSettings,
    (v: boolean): void => handlers.onEmitterPulseSpeed(v),
  );
  tipRow(pulseSpeedToggle.row, "speedFromSettings");
  let speedCenter = (emitterInit.speedMin + emitterInit.speedMax) / 2;
  let speedRange = emitterInit.speedMax - emitterInit.speedMin;
  function applySpeed(): void {
    const half = speedRange / 2;
    handlers.onEmitterSpeed(Math.max(0, speedCenter - half), speedCenter + half);
  }
  const speedSlider = addSlider(
    emitSection,
    t("param.speed"),
    0,
    30,
    0.5,
    speedCenter,
    (v: number): string => v.toFixed(1),
    (v: number): void => {
      speedCenter = v;
      applySpeed();
    },
  );
  const spdRangeSlider = addSlider(
    emitSection,
    t("param.spdRange"),
    0,
    30,
    0.5,
    speedRange,
    (v: number): string => v.toFixed(1),
    (v: number): void => {
      speedRange = v;
      applySpeed();
    },
  );
  let lifeCenter = (emitterInit.lifeMin + emitterInit.lifeMax) / 2;
  let lifeRange = emitterInit.lifeMax - emitterInit.lifeMin;
  function applyLife(): void {
    const half = lifeRange / 2;
    handlers.onEmitterLife(Math.max(0.2, lifeCenter - half), lifeCenter + half);
    applyRateCeiling();
  }
  function streamRateCeiling(): number {
    if (currentEmitterKind !== "full") return EMIT_RATE_MAX;
    const lifeMax = lifeCenter + lifeRange / 2;
    return Math.min(EMIT_RATE_MAX, Math.max(EMIT_RATE_MIN, STREAM_RATE_LIFE_BUDGET / Math.max(lifeMax, 0.2)));
  }
  function applyRateCeiling(): void {
    const ceiling = streamRateCeiling();
    rateSlider.input.max = String(sliderFromRate(ceiling));
    if (boundRate > ceiling) {
      boundRate = ceiling;
      rateSlider.set(sliderFromRate(ceiling));
      handlers.onEmitterRate(ceiling);
    }
  }
  const lifeSlider = addSlider(
    emitSection,
    t("param.life"),
    0.2,
    LIFESPAN_SLIDER_MAX,
    0.1,
    lifeCenter,
    (v: number): string => `${v.toFixed(1)}s`,
    (v: number): void => {
      lifeCenter = v;
      applyLife();
    },
  );
  const lifeRangeSlider = addSlider(
    emitSection,
    t("param.lifeRange"),
    0,
    12,
    0.1,
    lifeRange,
    (v: number): string => `${v.toFixed(1)}s`,
    (v: number): void => {
      lifeRange = v;
      applyLife();
    },
  );
  applyKindMode(emitterInit.kind);
  const emBounceSlider = addSlider(
    emitterPhysicsSection,
    t("param.bounce"),
    0,
    1,
    0.05,
    emitterInit.bounce,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onEmitterBounce(v),
  );
  const emFrictionSlider = addSlider(
    emitterPhysicsSection,
    t("param.friction"),
    0,
    1,
    0.05,
    emitterInit.friction,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onEmitterFriction(v),
  );
  const emDragSlider = addSlider(
    emitterPhysicsSection,
    t("param.drag"),
    0,
    EMIT_DRAG_MAX,
    0.1,
    emitterInit.drag,
    (v: number): string => v.toFixed(1),
    (v: number): void => handlers.onEmitterDrag(v),
  );
  tipRow(rateSlider.row, "rate");
  tipRow(numRow, "bars");
  tipRow(spreadSlider.row, "spread");
  tipRow(speedSlider.row, "speed");
  tipRow(spdRangeSlider.row, "spdRange");
  tipRow(lifeSlider.row, "life");
  tipRow(lifeRangeSlider.row, "lifeRange");
  tipRow(emBounceSlider.row, "bounce");
  tipRow(emFrictionSlider.row, "friction");
  tipRow(emDragSlider.row, "drag");

  const objectSections: HTMLElement[] = [
    funnelShapeSection,
    objectPhysicsSection,
    objectContactSection,
    objectAudioSection,
    railAudioSection,
    forceFieldSection,
    operatorSection,
    timerSection,
    decaySection,
    delaySection,
    routerSection,
    arraySection,
    curveSection,
    buttonSection,
    lampSection,
    knobSection,
    timbreNodeSection,
    countOwnerSection,
    orbitToggleBox,
    rotationSection,
    waveSection,
    groupBulkSection,
  ];

  const OBJECT_TITLES: Record<ObjectType, string> = {
    box: "Box",
    sphere: "Sphere",
    funnel: "Funnel",
    force: "Force",
    rail: "Rail",
    calc: "Calc",
    count: "Count",
    display: "Display",
    timer: "Timer",
    decay: "Decay",
    delay: "Delay",
    router: "Router",
    orbit: "Orbit",
    group: "Group",
    timbre: "Timbre",
    pitchQuantize: "Pitch Quantize",
    global: "Global",
    button: "Button",
    lamp: "Lamp",
    knob: "Knob",
    envelope: "Envelope",
    array: "Array",
    curve: "Curve",
  };

  let lastSyncTarget: ContextTarget = { kind: "none" };
  function syncControls(target: ContextTarget): void {
    lastSyncTarget = target;
    endRename(false);
    if (target.kind === "none") {
      seqPanelTarget = null;
      closePopover();
      for (const s of objectSections) {
        s.style.display = "none";
      }
      emitSection.style.display = "none";
      emitterPhysicsSection.style.display = "none";
      objectPanelScroll.style.display = "none";
      groupExprOverlay.style.display = "none";
      return;
    }
    if (target.kind === "object") {
      emitSection.style.display = "none";
      emitterPhysicsSection.style.display = "none";
      const v = target.template;
      const isForce = v.type === "force";
      const isOrbit = v.type === "orbit";
      const isValue = v.type === "calc";
      const isTimer = v.type === "timer";
      const isDecay = v.type === "decay";
      const isDelay = v.type === "delay";
      const isRouter = v.type === "router";
      const isArray = v.type === "array";
      const isCurve = v.type === "curve";
      const isButton = v.type === "button";
      const isLamp = v.type === "lamp";
      const isKnob = v.type === "knob";
      const isTimbre = v.type === "timbre";
      const isGroup = v.type === "group";
      const isRail = v.type === "rail";
      const isFunnel = v.type === "funnel";
      const isPlain = v.type === "box" || v.type === "sphere" || v.type === "funnel";
      currentRawName = v.name ?? "";
      currentDisplayTitle = currentRawName || (v.id ? handlers.entityLabel(v.id) : "") || OBJECT_TITLES[v.type];
      panelTitle.textContent = currentDisplayTitle;
      panelTitle.classList.toggle("named", currentRawName !== "");
      funnelShapeSection.style.display = isFunnel ? "" : "none";
      objectPhysicsSection.style.display = isPlain ? "" : "none";
      objectContactSection.style.display = isPlain ? "" : "none";
      objectAudioSection.style.display = isPlain ? "" : "none";
      railAudioSection.style.display = isRail ? "" : "none";
      forceFieldSection.style.display = isForce ? "" : "none";
      operatorSection.style.display = isValue ? "" : "none";
      timerSection.style.display = isTimer ? "" : "none";
      decaySection.style.display = isDecay ? "" : "none";
      delaySection.style.display = isDelay ? "" : "none";
      routerSection.style.display = isRouter ? "" : "none";
      arraySection.style.display = isArray ? "" : "none";
      curveSection.style.display = isCurve ? "" : "none";
      buttonSection.style.display = isButton ? "" : "none";
      lampSection.style.display = isLamp ? "" : "none";
      knobSection.style.display = isKnob ? "" : "none";
      timbreNodeSection.style.display = isTimbre ? "" : "none";
      const ownerInfo = v.type === "count" && target.selected && v.id ? handlers.countOwner(v.id) : null;
      countOwnerSection.style.display = ownerInfo !== null ? "" : "none";
      if (ownerInfo !== null) {
        countOwnerName.textContent = ownerInfo.name;
        countOwnerSwatch.style.display = ownerInfo.color === null ? "none" : "inline-block";
        if (ownerInfo.color !== null) {
          countOwnerSwatch.style.background = ownerInfo.color;
        }
      }
      orbitToggleBox.style.display = isOrbit ? "" : "none";
      rotationSection.style.display = isOrbit ? "" : "none";
      waveSection.style.display = isOrbit ? "" : "none";
      if (isGroup) {
        groupExprOverlay.textContent = "";
        const exprs = v.id ? handlers.buildGroupExpressions(v.id) : [];
        groupExprOverlay.style.display = exprs.length > 0 && target.selected ? "" : "none";
        for (const line of exprs) {
          const row = document.createElement("div");
          row.className = "row group-expr-row";
          row.innerHTML = line.mathml;
          groupExprOverlay.appendChild(row);
        }
        const bulkShown = v.id ? applyGroupBulk(handlers.groupBulkModel(v.id)) : false;
        groupBulkSection.style.display = bulkShown ? "" : "none";
      } else {
        groupBulkSection.style.display = "none";
        groupExprOverlay.style.display = "none";
      }
      if (isValue) {
        operatorSelect.value = v.operator;
        const isBinary = valueOperatorKind(v.operator) === "binary";
        const operand2Wired = target.selected && v.id ? handlers.valueOperand2Wired(v.id) : false;
        operandSlider.row.style.display = isBinary && !operand2Wired ? "" : "none";
        operandSlider.set(v.operand);
      }
      if (isTimer) {
        timerModeSelect.value = v.timerMode;
      }
      if (isButton) {
        buttonModeSelect.value = v.buttonMode;
        buttonPulseOnResetToggle.input.checked = v.buttonPulseOnReset;
      }
      if (isLamp) {
        lampValueSlider.set(v.lampValue);
        lampHueSlider.set(v.lampHue);
        lampSatSlider.set(v.lampSat);
      }
      if (isKnob) {
        knobMinRow.set(v.knobMin);
        knobMaxRow.set(v.knobMax);
        knobIntToggle.input.checked = v.knobInt;
      }
      if (isDecay) {
        decaySlider.set(v.decayS);
      }
      if (isDelay) {
        delaySlider.set(v.delayS);
      }
      if (isRouter) {
        routerHoldSlider.set(v.routerHoldS);
        routerResetToggle.input.checked = v.routerHoldResetOnPulse;
        applyRouterResetGate(v.routerHoldS);
      }
      seqPanelTarget = target.selected && (isArray || isCurve) ? v : null;
      if (isArray) {
        arrayMinSlider.set(v.seqMin);
        arrayMaxSlider.set(v.seqMax);
        arrayLenSlider.set(v.seqLen);
      }
      if (isCurve) {
        curveMinSlider.set(v.seqMin);
        curveMaxSlider.set(v.seqMax);
        curveLenSlider.set(v.seqCycleLength);
      }
      if (isTimbre) {
        timbreSetState = [...v.timbreIds];
        renderTimbreSet();
        timbreVolumeSlider.set(v.params.x);
        timbreTransposeSlider.set(v.params.z);
      }
      if (isRail) {
        railTimbreSelect.value = v.railTimbre;
        railVolumeSlider.set(v.volume);
      }
      if (isOrbit) {
        applyOrbitTargetMode(target.selected && v.id ? handlers.orbitTargetMode(v.id) : false);
        setOrbitSliders(v.orbit);
      } else if (isForce) {
        modeSelect.value = v.params.y < 0.5 ? "lorentz" : "drag";
        fieldR.set(v.params.x);
        fieldStrength.set(v.params.z);
        fieldOmega.set(v.params.z);
        fieldK.set(v.params.w);
        fieldFade.set(v.fade);
        applyForceModeVisibility();
      } else if (isPlain) {
        if (isFunnel) {
          arcSlider.set(v.arcDeg);
        }
        bounce.set(v.restitution);
        friction.set(v.friction);
        massSlider.set(v.mass);
        massToggle.input.checked = v.massEnabled;
        massSlider.row.style.display = features.physics ? "" : "none";
        massToggle.row.style.display = features.physics ? "" : "none";
        killToggle.input.checked = v.killOnContact;
        collisionToggle.input.checked = v.collisionEnabled;
        const isMic = v.timbreId === "mic";
        const micEditable = isMic && !micStore.isImported();
        micRow.style.display = micEditable ? "" : "none";
        if (isMic) {
          micPanelPitchIndex = v.pitchIndex;
          const hasRec = micStore.count() > 0;
          micPrevBtn.style.display = hasRec ? "" : "none";
          micNextBtn.style.display = hasRec ? "" : "none";
          micDelBtn.style.display = hasRec ? "" : "none";
        }
        timbreSelect.value = v.timbreId;
        objMuteToggle.input.checked = v.muted;
      }
    } else {
      seqPanelTarget = null;
      for (const s of objectSections) {
        s.style.display = "none";
      }
      groupExprOverlay.style.display = "none";
      const p = target.params;
      currentRawName = p.name;
      currentDisplayTitle = currentRawName || (p.kind === "sync" ? "Rhythm" : "Stream");
      panelTitle.textContent = currentDisplayTitle;
      panelTitle.classList.toggle("named", currentRawName !== "");
      emitSection.style.display = "";
      emitterPhysicsSection.style.display = "";
      for (const row of emitterRows()) {
        row.style.display = "";
      }
      emDragSlider.row.style.display = features.physics ? "" : "none";
      boundRate = p.rate;
      rateSlider.set(sliderFromRate(p.rate));
      spreadSlider.set(p.coneDeg);
      speedCenter = (p.speedMin + p.speedMax) / 2;
      speedRange = p.kind === "sync" ? 0 : p.speedMax - p.speedMin;
      speedSlider.set(speedCenter);
      spdRangeSlider.set(speedRange);
      lifeCenter = (p.lifeMin + p.lifeMax) / 2;
      lifeRange = p.kind === "sync" ? 0 : p.lifeMax - p.lifeMin;
      lifeSlider.set(lifeCenter);
      lifeRangeSlider.set(lifeRange);
      emBounceSlider.set(p.bounce);
      emFrictionSlider.set(p.friction);
      emDragSlider.set(p.drag);
      pulseSpeedToggle.input.checked = p.pulseSpeedFromSettings;
      meterSelect.value = String(p.meter);
      numSlider.set(p.num / seqColumns(p.meter));
      applyKindMode(p.kind);
      applyClockSyncMode(p.clockSync);
    }
    objectPanelScroll.style.display = target.selected ? "" : "none";
  }

  micStore.subscribe((): void => syncControls(lastSyncTarget));

  const audioSection = addSection("Audio", audioHoverPanel);
  const volumeSlider = addSlider(
    audioSection,
    t("param.volume"),
    0,
    1,
    0.01,
    init.volume,
    (v: number): string => v.toFixed(2),
    (v: number): void => {
      if (v > 0) {
        lastVolume = v;
      }
      handlers.onVolume(v);
      renderAudioIcon(v, false);
    },
  );
  const timbreSection = timbreSub;
  addSlider(
    timbreSection,
    "wood decay",
    0.05,
    1.5,
    0.01,
    init.woodDecayS,
    (v: number): string => `${v.toFixed(2)}s`,
    (v: number): void => handlers.onWoodDecay(v),
  );
  addSlider(
    timbreSection,
    "bell oct",
    0,
    4,
    1,
    init.bellTranspose,
    (v: number): string => `+${v.toFixed(0)}`,
    (v: number): void => handlers.onBellTranspose(v),
  );
  addSlider(
    timbreSection,
    "bell ratio",
    0.5,
    8,
    0.1,
    init.bellModRatio,
    (v: number): string => v.toFixed(1),
    (v: number): void => handlers.onBellModRatio(v),
  );
  addSlider(
    timbreSection,
    "bell index",
    0,
    5,
    0.1,
    init.bellModIndex,
    (v: number): string => v.toFixed(1),
    (v: number): void => handlers.onBellModIndex(v),
  );
  addSlider(
    timbreSection,
    "bell level",
    0,
    1,
    0.05,
    init.bellLevel,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onBellLevel(v),
  );

  const densitySection = densitySub;
  addSlider(
    densitySection,
    "low",
    1,
    50,
    1,
    init.densityLow,
    decimalFmt(1, "/s"),
    (v: number): void => handlers.onDensityLow(v),
  );
  addSlider(
    densitySection,
    "high",
    20,
    200,
    5,
    init.densityHigh,
    decimalFmt(1, "/s"),
    (v: number): void => handlers.onDensityHigh(v),
  );
  addSlider(
    densitySection,
    "transient",
    0,
    1,
    0.05,
    init.densityTransient,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onDensityTransient(v),
  );
  addSlider(
    densitySection,
    "detune",
    0,
    50,
    1,
    init.densityDetune,
    decimalFmt(1, "c"),
    (v: number): void => handlers.onDensityDetune(v),
  );
  addSlider(
    densitySection,
    "voice gain",
    0,
    1,
    0.05,
    init.densityVoiceGain,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onDensityVoiceGain(v),
  );
  addSlider(
    densitySection,
    "duck",
    0,
    1,
    0.05,
    init.densityDuck,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onDensityDuck(v),
  );
  addSlider(
    densitySection,
    "reverb",
    0,
    1,
    0.05,
    init.densityReverb,
    (v: number): string => v.toFixed(2),
    (v: number): void => handlers.onDensityReverb(v),
  );

  const localSection = addSection("Scene Local", scenePanelCtl.hoverPanel);
  const uploadedSection = addSection("Scene Uploaded", scenePanelCtl.hoverPanel);
  const micSection = addSection("Scene Mic", scenePanelCtl.hoverPanel);

  function captureThumb(): Promise<string> {
    return new Promise<string>((resolve: (dataUrl: string) => void): void => {
      handlers.onCaptureThumb(resolve);
    });
  }

  const statusTimers = new Map<HTMLElement, number>();
  /**
   * Shows a transient toast in a `.scene-status` element and auto-clears it after
   * 3s. Closing/reopening the menu also wipes it (clearFlashedStatuses in showNav),
   * so a stale confirmation never lingers. Only elements written through here are
   * tracked — persistent notices written via raw textContent (foreign-scene lock,
   * in-flight progress) are left alone.
   */
  function flashStatus(el: HTMLElement, text: string): void {
    const prev = statusTimers.get(el);
    if (prev !== undefined) {
      window.clearTimeout(prev);
    }
    el.textContent = text;
    if (text === "") {
      statusTimers.delete(el);
      return;
    }
    statusTimers.set(
      el,
      window.setTimeout((): void => {
        el.textContent = "";
        statusTimers.delete(el);
      }, 3000),
    );
  }

  function clearFlashedStatuses(): void {
    for (const [el, id] of statusTimers) {
      window.clearTimeout(id);
      el.textContent = "";
    }
    statusTimers.clear();
  }

  const storeStatus = document.createElement("div");
  storeStatus.className = "row-value scene-status share-status";
  const titleLabel = document.createElement("span");
  titleLabel.className = "row-label";
  titleLabel.textContent = t("scene.titleLabel");
  const storeNameInput = document.createElement("input");
  storeNameInput.type = "text";
  storeNameInput.className = "text-input";
  storeNameInput.placeholder = t("scene.titlePlaceholder");
  storeNameInput.maxLength = 64;
  const titleRow = document.createElement("div");
  titleRow.className = "row";
  titleRow.append(titleLabel, storeNameInput);
  const saveLocalBtn = document.createElement("button");
  saveLocalBtn.textContent = t("scene.saveLocal");
  const storeRow = document.createElement("div");
  storeRow.className = "row";
  storeRow.append(saveLocalBtn);

  const uploadStatus = document.createElement("div");
  uploadStatus.className = "row-value scene-status share-status";
  const uploadTitleLabel = document.createElement("span");
  uploadTitleLabel.className = "row-label";
  uploadTitleLabel.textContent = t("scene.titleLabel");
  const uploadTitleInput = document.createElement("input");
  uploadTitleInput.type = "text";
  uploadTitleInput.className = "text-input";
  uploadTitleInput.placeholder = t("scene.titlePlaceholder");
  uploadTitleInput.maxLength = 64;
  const uploadTitleRow = document.createElement("div");
  uploadTitleRow.className = "row";
  uploadTitleRow.append(uploadTitleLabel, uploadTitleInput);
  const sceneUploadBtn = document.createElement("button");
  sceneUploadBtn.textContent = t("scene.upload");
  const uploadRow = document.createElement("div");
  uploadRow.className = "row";
  uploadRow.append(sceneUploadBtn);

  /** Keep this synced with the DB feedback length CHECK (2000 chars). */
  const FEEDBACK_MAX_LENGTH = 2000;
  const feedbackSection = addSection("Feedback");
  const feedbackInput = document.createElement("textarea");
  feedbackInput.className = "text-input";
  feedbackInput.placeholder = t("feedback.placeholder");
  feedbackInput.rows = 4;
  feedbackInput.maxLength = FEEDBACK_MAX_LENGTH;
  feedbackInput.style.resize = "vertical";
  feedbackInput.style.minHeight = "90px";
  const feedbackInputRow = document.createElement("div");
  feedbackInputRow.className = "row";
  feedbackInputRow.append(feedbackInput);
  const feedbackLimitReached = document.createElement("div");
  feedbackLimitReached.className = "row-value scene-status share-status";
  const updateFeedbackLimitReached = (): void => {
    const reached = feedbackInput.value.length >= FEEDBACK_MAX_LENGTH;
    feedbackLimitReached.textContent = reached ? t("feedback.limitReached") : "";
    feedbackLimitReached.style.display = reached ? "" : "none";
  };
  feedbackInput.addEventListener("input", updateFeedbackLimitReached);
  updateFeedbackLimitReached();
  const feedbackIncludeSceneLabel = document.createElement("label");
  feedbackIncludeSceneLabel.className = "row";
  const feedbackIncludeSceneText = document.createElement("span");
  feedbackIncludeSceneText.className = "row-label";
  feedbackIncludeSceneText.textContent = t("feedback.includeScene");
  const feedbackIncludeSceneInput = document.createElement("input");
  feedbackIncludeSceneInput.type = "checkbox";
  feedbackIncludeSceneInput.checked = false;
  feedbackIncludeSceneLabel.append(feedbackIncludeSceneText, feedbackIncludeSceneInput);
  attachTooltip(feedbackIncludeSceneLabel, (): string => t("feedback.includeSceneDesc"));
  const feedbackSendBtn = document.createElement("button");
  feedbackSendBtn.textContent = t("feedback.send");
  const feedbackSendRow = document.createElement("div");
  feedbackSendRow.className = "row";
  feedbackSendRow.append(feedbackSendBtn);
  const feedbackGate = document.createElement("div");
  feedbackGate.className = "row-value account-name";
  const feedbackStatus = document.createElement("div");
  feedbackStatus.className = "row-value scene-status share-status";
  feedbackSection.append(feedbackInputRow, feedbackLimitReached, feedbackIncludeSceneLabel, feedbackSendRow, feedbackGate, feedbackStatus);

  const accountSection = addSection("Account");
  const accountAvatarLabel = document.createElement("span");
  accountAvatarLabel.className = "row-label";
  accountAvatarLabel.textContent = t("account.avatar");
  const accountAvatarImg = document.createElement("img");
  accountAvatarImg.className = "account-avatar";
  accountAvatarImg.alt = "";
  const accountAvatarRow = document.createElement("div");
  accountAvatarRow.className = "row";
  accountAvatarRow.append(accountAvatarLabel, accountAvatarImg);
  const accountNameLabel = document.createElement("span");
  accountNameLabel.className = "row-label";
  accountNameLabel.textContent = t("account.name");
  const accountNameInput = document.createElement("input");
  accountNameInput.type = "text";
  accountNameInput.className = "text-input";
  accountNameInput.maxLength = DISPLAY_NAME_MAX;
  const accountSaveBtn = document.createElement("button");
  accountSaveBtn.textContent = t("account.save");
  accountSaveBtn.style.cssText = "flex:0 0 auto";
  const accountNameRow = document.createElement("div");
  accountNameRow.className = "row";
  accountNameRow.append(accountNameLabel, accountNameInput, accountSaveBtn);
  const accountSignedOut = document.createElement("div");
  accountSignedOut.className = "row-value account-name";
  accountSignedOut.textContent = t("account.notSignedIn");
  const accountStatus = document.createElement("div");
  accountStatus.className = "row-value scene-status share-status";
  accountSection.append(accountAvatarRow, accountNameRow, accountSignedOut, accountStatus);

  const micStatus = document.createElement("div");
  micStatus.className = "row-value scene-status share-status";
  const micUsageEl = document.createElement("div");
  micUsageEl.className = "row-value mic-uploads-usage";
  const micListEl = document.createElement("div");
  micListEl.className = "mic-uploads-list";

  async function renderMicAssets(): Promise<void> {
    micListEl.textContent = "";
    const res = await handlers.onListMicAssets();
    if ("error" in res) {
      micUsageEl.textContent = t("mic.loadFailed");
      return;
    }
    const items = res.items;
    micUsageEl.textContent = t("mic.usage", {
      count: String(items.length),
      maxCount: String(MIC_MAX_COUNT),
    });
    if (items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "row-value";
      empty.textContent = t("mic.noUploads");
      micListEl.appendChild(empty);
      return;
    }
    for (const asset of items) {
      const row = document.createElement("div");
      row.className = "row mic-upload-row";
      const label = document.createElement("span");
      label.className = "row-label mic-upload-name";
      label.textContent = asset.name;
      const meta = document.createElement("span");
      meta.className = "row-value mic-upload-meta";
      meta.textContent = t("mic.usedByScenes", { n: String(asset.sceneCount) });
      const delBtn = document.createElement("button");
      delBtn.className = "mic-btn";
      delBtn.textContent = t("mic.delete");
      delBtn.addEventListener("click", async (): Promise<void> => {
        if (!window.confirm(t("mic.deleteConfirm", { name: asset.name, n: String(asset.sceneCount) }))) {
          return;
        }
        delBtn.disabled = true;
        const err = await handlers.onDeleteMicAsset(asset.hash);
        flashStatus(micStatus, err === null ? t("mic.deleted") : `${t("mic.deleteFailed")}: ${err}`);
        await renderMicAssets();
      });
      row.append(label, meta, delBtn);
      micListEl.appendChild(row);
    }
  }

  const accountNavBtn = navButtons.get("Account") ?? null;
  const uploadedNavBtn = scenePanelCtl.navButtons.get("Scene Uploaded") ?? null;
  const micNavBtn = scenePanelCtl.navButtons.get("Scene Mic") ?? null;

  function setSignInGatedNav(btn: HTMLButtonElement | null, label: string, signedIn: boolean): void {
    if (btn === null) {
      return;
    }
    btn.style.display = "";
    btn.disabled = !signedIn;
    btn.textContent = signedIn ? label : `${label}${t("account.signInRequiredSuffix")}`;
  }

  const privacyNavBtn = document.createElement("button");
  privacyNavBtn.className = "main-nav-btn main-nav-btn-action";
  privacyNavBtn.textContent = sectionLabel("Privacy");
  privacyNavBtn.addEventListener("click", (): void => {
    window.open("/privacy", "_blank", "noopener");
  });
  navButtons.set("Privacy", privacyNavBtn);
  mainNav.appendChild(privacyNavBtn);

  const signOutNavBtn = document.createElement("button");
  signOutNavBtn.className = "main-nav-btn main-nav-btn-action";
  signOutNavBtn.textContent = t("account.signOut");
  navButtons.set("Sign-out", signOutNavBtn);
  mainNav.appendChild(signOutNavBtn);

  let authSignedIn = false;
  let feedbackEnabled = false;

  async function refreshAuthUi(): Promise<void> {
    if (!handlers.shareEnabled()) {
      feedbackEnabled = false;
      feedbackInput.disabled = true;
      feedbackIncludeSceneInput.disabled = true;
      feedbackSendBtn.disabled = true;
      feedbackGate.style.display = "";
      feedbackGate.textContent = t("scene.sharingNotConfigured");
      feedbackStatus.textContent = "";
      if (accountNavBtn !== null) accountNavBtn.style.display = "none";
      if (uploadedNavBtn !== null) uploadedNavBtn.style.display = "none";
      if (micNavBtn !== null) micNavBtn.style.display = "none";
      signOutNavBtn.style.display = "none";
      return;
    }
    const status = await handlers.onAuthStatus();
    authSignedIn = status.signedIn;
    feedbackEnabled = status.signedIn && (await handlers.onIsRealAccount());
    feedbackInput.disabled = !feedbackEnabled;
    feedbackIncludeSceneInput.disabled = !feedbackEnabled;
    feedbackSendBtn.disabled = !feedbackEnabled;
    feedbackGate.style.display = feedbackEnabled ? "none" : "";
    feedbackGate.textContent = feedbackEnabled ? "" : status.signedIn ? t("feedback.realAccountRequired") : t("feedback.signInRequired");
    feedbackStatus.textContent = "";
    setSignInGatedNav(uploadedNavBtn, t("section.sceneUploaded"), status.signedIn);
    setSignInGatedNav(accountNavBtn, t("section.account"), status.signedIn);
    if (micNavBtn !== null) {
      micNavBtn.style.display = "none";
      if (status.signedIn) {
        void handlers
          .onListMicAssets()
          .then((res: { items: MicAsset[] } | { error: string }): void => {
            if (!("error" in res) && res.items.length > 0) {
              micNavBtn.style.display = "";
            }
          })
          .catch((): void => {
            // fetch failure keeps the nav hidden (same as zero uploads)
          });
      }
    }
    if (status.signedIn) {
      accountNameInput.value = status.name ?? "";
      accountNameRow.style.display = "";
      accountAvatarRow.style.display = "";
      accountSignedOut.style.display = "none";
      accountStatus.textContent = "";
      void handlers.onCurrentUid().then((uid: string | null): void => {
        accountAvatarImg.src = uid === null ? "" : avatarFor(uid, undefined, Math.ceil(48 * (window.devicePixelRatio || 1)));
      });
      void renderMicAssets();
      signOutNavBtn.textContent = t("account.signOut");
    } else {
      accountNameRow.style.display = "none";
      accountAvatarRow.style.display = "none";
      accountSignedOut.style.display = "";
      accountStatus.textContent = "";
      signOutNavBtn.textContent = t("account.signIn");
    }
  }

  accountSaveBtn.addEventListener("click", async (): Promise<void> => {
    const name = accountNameInput.value.trim();
    if (name === "") {
      flashStatus(accountStatus, t("account.nameEmpty"));
      return;
    }
    accountSaveBtn.disabled = true;
    const err = await handlers.onSetDisplayName(name);
    accountSaveBtn.disabled = false;
    if (err === null) {
      accountNameInput.value = name;
    }
    flashStatus(accountStatus, err === null ? t("account.saved") : err);
  });

  feedbackSendBtn.addEventListener("click", async (): Promise<void> => {
    if (!feedbackEnabled) {
      flashStatus(feedbackStatus, authSignedIn ? t("feedback.realAccountRequired") : t("feedback.signInRequired"));
      return;
    }
    const body = feedbackInput.value.trim();
    if (body === "") {
      flashStatus(feedbackStatus, t("feedback.empty"));
      return;
    }
    feedbackSendBtn.disabled = true;
    const err = await handlers.onSubmitFeedback(body, feedbackIncludeSceneInput.checked);
    feedbackSendBtn.disabled = !feedbackEnabled;
    if (err === null) {
      feedbackInput.value = "";
      updateFeedbackLimitReached();
      flashStatus(feedbackStatus, t("feedback.sent"));
      return;
    }
    flashStatus(feedbackStatus, err);
  });

  signOutNavBtn.addEventListener("click", async (): Promise<void> => {
    if (authSignedIn) {
      await handlers.onSignOut();
      await refreshAuthUi();
    } else {
      const err = await handlers.onSignInWithGoogle();
      if (err !== null) {
        flashStatus(storeStatus, err);
      }
    }
  });

  if (debugUi) {
    const exportSceneBtn = document.createElement("button");
    exportSceneBtn.textContent = "Export";
    exportSceneBtn.addEventListener("click", (): void => {
      if (!navigator.clipboard) {
        flashStatus(storeStatus, "clipboard unavailable");
        return;
      }
      navigator.clipboard
        .writeText(handlers.onExportScene())
        .then((): void => {
          flashStatus(storeStatus, "scene JSON copied");
        })
        .catch((): void => {
          flashStatus(storeStatus, "copy failed");
        });
    });
    storeRow.append(exportSceneBtn);
  }

  const localGrid = document.createElement("div");
  localGrid.className = "scene-grid";
  const uploadedGrid = document.createElement("div");
  uploadedGrid.className = "scene-grid";

  const openSceneItem = (item: GalleryItem): void => {
    if (mpRoomActive) {
      return;
    }
    if (item.source === "local") {
      handlers.onLoadSlot(item.scene);
    } else {
      location.href = shareUrlFor(item.id);
    }
  };

  const sceneMenuActions: GalleryMenuAction[] = [
    {
      label: t("community.delete"),
      onClick: (item: GalleryItem): void => {
        if (item.source === "local") {
          handlers.onDeleteSlot(item.name);
          void refreshSceneGrid();
          return;
        }
        void handlers.onDeleteScene(item.id).then((err: string | null): void => {
          if (err === null) {
            void refreshSceneGrid();
          }
        });
      },
    },
  ];

  async function refreshSceneGrid(): Promise<void> {
    renderGalleryGrid(localGrid, localGridItems(handlers), openSceneItem, sceneMenuActions);
    renderGalleryGrid(uploadedGrid, await uploadedGridItems(handlers), openSceneItem, sceneMenuActions);
  }

  saveLocalBtn.addEventListener("click", async (): Promise<void> => {
    const name = storeNameInput.value.trim();
    if (name === "") {
      flashStatus(storeStatus, t("scene.enterName"));
      return;
    }
    storeStatus.textContent = t("scene.capturing");
    await captureThumb();
    const err = handlers.onSaveSlot(name);
    if (err !== null) {
      flashStatus(storeStatus, err);
      return;
    }
    flashStatus(storeStatus, t("scene.savedLocally"));
    refreshSceneGrid();
  });

  async function performUpload(title: string): Promise<void> {
    track("upload_click");
    sceneUploadBtn.disabled = true;
    const wait = showWaitModal(t("scene.uploading"), "");
    let result: UploadResult;
    try {
      result = await handlers.onUploadScene(title);
    } finally {
      wait.close();
      sceneUploadBtn.disabled = false;
    }
    if (result.kind === "timeout") {
      flashStatus(uploadStatus, t("scene.uploadTimeout"));
      return;
    }
    if (result.kind === "error") {
      flashStatus(uploadStatus, result.message);
      return;
    }
    if (result.kind === "reject") {
      flashStatus(uploadStatus, t("scene.uploadRejected", { reason: result.reason }));
      return;
    }
    if (result.kind === "needs-signin") {
      flashStatus(uploadStatus, t("scene.uploadNeedsSignin"));
      await refreshAuthUi();
      return;
    }
    if (result.kind === "throttled") {
      flashStatus(uploadStatus, result.reason);
      return;
    }
    if (result.kind === "blocked") {
      flashStatus(uploadStatus, t("scene.uploadBlocked"));
      return;
    }
    uploadStatus.textContent = "";
    track("upload_done");
    saveBookmarkThumb(result.id, await captureThumb());
    refreshSceneGrid();
    showUploadSuccessModal({
      title: t("share.uploadSuccessTitle"),
      urlLabel: t("share.uploadSuccessUrlLabel"),
      url: shareUrlFor(result.id),
      copyLabel: t("share.copyUrl"),
      copiedLabel: t("share.urlCopied"),
      closeLabel: t("share.close"),
    });
  }

  function buildUploadFields(): { label: string; value: string }[] {
    const s = handlers.onUploadSummary();
    const shownFeatures = s.simpleMode
      ? [t("feature.simple")]
      : s.features.map((cat: FeatureCategory): string => t(FEATURE_LABELS[cat]));
    const featureList = shownFeatures.length > 0 ? shownFeatures.join(", ") : t("upload.none");
    return [
      { label: t("upload.field_gravity"), value: s.gravityY.toFixed(1) },
      { label: t("upload.field_bpm"), value: String(Math.round(60 / s.clockPeriod)) },
      { label: t("upload.field_features"), value: featureList },
      { label: t("upload.field_wall"), value: s.wallPreset },
    ];
  }

  sceneUploadBtn.addEventListener("click", async (): Promise<void> => {
    if (!handlers.shareEnabled()) {
      flashStatus(uploadStatus, t("scene.sharingNotConfigured"));
      return;
    }
    const title = uploadTitleInput.value.trim();
    if (title === "") {
      flashStatus(uploadStatus, t("scene.enterTitle"));
      return;
    }
    uploadStatus.textContent = t("scene.capturing");
    const thumb = await handlers.onCaptureUploadPreview();
    uploadStatus.textContent = "";
    const usesMic = handlers.onUploadSummary().usesMic;
    const consented = hasTosConsent();
    const actions: ModalAction<string>[] = authSignedIn
      ? [
          { label: t("upload.confirm_ok"), value: "upload" },
          { label: t("upload.confirm_cancel"), value: "cancel", subtle: true },
        ]
      : [
          { label: t("upload.confirm_ok"), value: "upload", disabled: true },
          { label: t("account.signIn"), value: "signin" },
          { label: t("upload.confirm_cancel"), value: "cancel", subtle: true },
        ];
    const consentCheckboxes: ModalCheckbox[] = [
      {
        label: t("upload.consent_label"),
        checked: consented,
        requireLinkOpened: !consented,
        link: { label: t("terms.linkLabel"), onClick: (): void => showTermsModal() },
      },
    ];
    if (usesMic) {
      consentCheckboxes.push({
        label: t("upload.consent_label_mic"),
        link: { label: t("upload.micLifecycle.linkLabel"), onClick: (): void => showMicLifecycleModal() },
      });
    }
    showActionModal(
      t("upload.confirm_title"),
      usesMic ? `${t("upload.confirm_includes")}\n${t("upload.mic_notice")}` : t("upload.confirm_includes"),
      actions,
      (value: string): void => {
        if (value === "signin") {
          void handlers.onSignInWithGoogle();
          return;
        }
        if (value === "upload") {
          recordTosConsent();
          void performUpload(title);
        }
      },
      {
        previewSrc: thumb,
        fields: buildUploadFields(),
        gates: ["upload"],
        checkbox: consentCheckboxes,
      },
    );
  });

  localSection.append(titleRow, storeRow, localGrid, storeStatus);
  uploadedSection.append(uploadTitleRow, uploadRow, uploadedGrid, uploadStatus);
  micSection.append(micUsageEl, micListEl, micStatus);

  const resetSceneNavBtn = document.createElement("button");
  resetSceneNavBtn.className = "main-nav-btn main-nav-btn-action";
  resetSceneNavBtn.textContent = t("scene.reset");
  resetSceneNavBtn.addEventListener("click", (): void => handlers.onResetScene());
  scenePanelCtl.navButtons.set("Reset", resetSceneNavBtn);
  scenePanelCtl.nav.appendChild(resetSceneNavBtn);
  refreshAuthUi();

  let sceneGridLoaded = false;
  const sceneGridObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
    if (!sceneGridLoaded && entries.some((e: IntersectionObserverEntry): boolean => e.isIntersecting)) {
      sceneGridLoaded = true;
      refreshSceneGrid();
    }
  });
  sceneGridObserver.observe(localGrid);
  sceneGridObserver.observe(uploadedGrid);

  if (handlers.shareEnabled()) {
    const communityNavBtn = document.createElement("button");
    communityNavBtn.className = "main-nav-btn";
    communityNavBtn.textContent = t("community.title");
    communityNavBtn.addEventListener("click", (): void => {
      setOpenedScene(null);
      openCommunityModal(handlers);
    });
    scenePanelCtl.navButtons.set("Community", communityNavBtn);
    scenePanelCtl.nav.appendChild(communityNavBtn);

    const bookmarksSection = addSection("Bookmarks", scenePanelCtl.hoverPanel);
    const bmTabs = document.createElement("div");
    bmTabs.className = "bookmarks-tabs";
    const bmPage = document.createElement("div");
    bmPage.className = "bookmarks-page";
    const bmStatus = document.createElement("div");
    bmStatus.className = "row-value scene-status share-status";
    bookmarksSection.append(bmTabs, bmPage, bmStatus);

    let activeTab: string | null = null;
    let bmItems: BookmarkItem[] = [];
    let bmCats: BookmarkCategory[] = [];

    function bookmarkItemToGallery(b: BookmarkItem): GalleryItem {
      return {
        source: "uploaded",
        id: b.id,
        owner: b.owner,
        ownerName: b.ownerName,
        title: b.title,
        playCount: b.playCount,
        likeCount: b.likeCount,
        bookmarkCount: b.bookmarkCount,
        mine: false,
      };
    }

    function countFor(categoryId: string | null): number {
      return bmItems.filter((b: BookmarkItem): boolean => b.categoryId === categoryId).length;
    }

    function openBookmark(it: GalleryItem): void {
      if (it.source === "uploaded") {
        location.href = shareUrlFor(it.id);
      }
    }

    function bookmarkMenuActions(): GalleryMenuAction[] {
      return [
        {
          label: t("bookmark.remove"),
          onClick: (it: GalleryItem): void => {
            if (it.source !== "uploaded") {
              return;
            }
            void handlers.onRemoveBookmark(it.id).then((err: string | null): void => {
              if (err !== null) {
                flashStatus(bmStatus, err);
                return;
              }
              void refreshBookmarks();
            });
          },
        },
        {
          label: t("bookmark.move"),
          onClick: (it: GalleryItem, anchor: HTMLElement): void => {
            if (it.source !== "uploaded") {
              return;
            }
            openCategoryPopover(anchor, handlers, (catId: string | null): void => {
              void handlers.onSetBookmarkCategory(it.id, catId).then((err: string | null): void => {
                if (err !== null) {
                  flashStatus(bmStatus, err);
                  return;
                }
                void refreshBookmarks();
              });
            });
          },
        },
      ];
    }

    function renderActivePage(): void {
      bmPage.textContent = "";
      const items = bmItems.filter((b: BookmarkItem): boolean => b.categoryId === activeTab);
      if (items.length === 0) {
        const empty = document.createElement("div");
        empty.className = "row-value scene-status";
        empty.textContent = t("bookmark.none");
        bmPage.appendChild(empty);
        return;
      }
      const grid = document.createElement("div");
      grid.className = "scene-grid";
      for (const b of items) {
        grid.appendChild(galleryCard(bookmarkItemToGallery(b), openBookmark, bookmarkMenuActions()));
      }
      bmPage.appendChild(grid);
    }

    /**
     * Floating menu raised by long-pressing a category tab: Rename (inline input)
     * and Delete. Delete expands to a choice between dropping the category's
     * bookmarks too or keeping them (they fall to Uncategorized via on delete set
     * null).
     */
    function openTabMenu(anchor: HTMLElement, categoryId: string, name: string): void {
      const { pop, dismiss } = openFloatingMenu(anchor);
      const renameOpt = document.createElement("button");
      renameOpt.className = "bookmark-cat-opt";
      renameOpt.textContent = t("bookmark.rename");
      renameOpt.addEventListener("click", (ev: MouseEvent): void => {
        ev.stopPropagation();
        pop.textContent = "";
        const input = document.createElement("input");
        input.type = "text";
        input.className = "text-input bookmark-cat-input";
        input.value = name;
        input.maxLength = 40;
        let done = false;
        const commit = async (): Promise<void> => {
          if (done) {
            return;
          }
          done = true;
          const nv = input.value.trim();
          dismiss();
          if (nv === "" || nv === name) {
            return;
          }
          const err = await handlers.onRenameCategory(categoryId, nv);
          if (err !== null) {
            flashStatus(bmStatus, err);
            return;
          }
          void refreshBookmarks();
        };
        input.addEventListener("keydown", (ke: KeyboardEvent): void => {
          if (ke.key === "Enter") {
            void commit();
          }
        });
        input.addEventListener("blur", (): void => void commit());
        pop.appendChild(input);
        input.focus();
      });
      const deleteOpt = document.createElement("button");
      deleteOpt.className = "bookmark-cat-opt";
      deleteOpt.textContent = t("community.delete");
      deleteOpt.addEventListener("click", async (ev: MouseEvent): Promise<void> => {
        ev.stopPropagation();
        if (countFor(categoryId) === 0) {
          const err = await handlers.onDeleteCategory(categoryId);
          dismiss();
          if (err !== null) {
            flashStatus(bmStatus, err);
            return;
          }
          if (activeTab === categoryId) {
            activeTab = null;
          }
          void refreshBookmarks();
          return;
        }
        pop.textContent = "";
        const prompt = document.createElement("div");
        prompt.className = "bookmark-cat-prompt";
        prompt.textContent = t("bookmark.deletePrompt", { name });
        const keepOpt = document.createElement("button");
        keepOpt.className = "bookmark-cat-opt";
        keepOpt.textContent = t("bookmark.keep");
        keepOpt.addEventListener("click", async (e2: MouseEvent): Promise<void> => {
          e2.stopPropagation();
          const err = await handlers.onDeleteCategory(categoryId);
          dismiss();
          if (err !== null) {
            flashStatus(bmStatus, err);
            return;
          }
          if (activeTab === categoryId) {
            activeTab = null;
          }
          void refreshBookmarks();
        });
        const removeOpt = document.createElement("button");
        removeOpt.className = "bookmark-cat-opt bookmark-cat-danger";
        removeOpt.textContent = t("bookmark.removeAll");
        removeOpt.addEventListener("click", async (e2: MouseEvent): Promise<void> => {
          e2.stopPropagation();
          for (const b of bmItems.filter((x: BookmarkItem): boolean => x.categoryId === categoryId)) {
            const rerr = await handlers.onRemoveBookmark(b.id);
            if (rerr !== null) {
              dismiss();
              flashStatus(bmStatus, rerr);
              return;
            }
          }
          const err = await handlers.onDeleteCategory(categoryId);
          dismiss();
          if (err !== null) {
            flashStatus(bmStatus, err);
            return;
          }
          if (activeTab === categoryId) {
            activeTab = null;
          }
          void refreshBookmarks();
        });
        pop.append(prompt, keepOpt, removeOpt);
      });
      pop.append(renameOpt, deleteOpt);
    }

    function makeTab(categoryId: string | null, name: string): HTMLButtonElement {
      const tab = document.createElement("button");
      tab.className = categoryId === activeTab ? "bookmarks-tab active" : "bookmarks-tab";
      tab.textContent = `${name} (${countFor(categoryId)})`;
      let pressTimer: number | undefined;
      let longPressed = false;
      tab.addEventListener("click", (): void => {
        if (longPressed) {
          longPressed = false;
          return;
        }
        activeTab = categoryId;
        renderTabs();
        renderActivePage();
      });
      if (categoryId !== null) {
        tab.addEventListener("pointerdown", (): void => {
          longPressed = false;
          pressTimer = window.setTimeout((): void => {
            longPressed = true;
            openTabMenu(tab, categoryId, name);
          }, 500);
        });
        const cancelPress = (): void => window.clearTimeout(pressTimer);
        tab.addEventListener("pointerup", cancelPress);
        tab.addEventListener("pointerleave", cancelPress);
        tab.addEventListener("pointercancel", cancelPress);
      }
      return tab;
    }

    function renderTabs(): void {
      bmTabs.textContent = "";
      bmTabs.appendChild(makeTab(null, t("bookmark.uncategorized")));
      for (const c of bmCats) {
        bmTabs.appendChild(makeTab(c.id, c.name));
      }
    }

    async function refreshBookmarks(): Promise<void> {
      const [bmRes, catRes] = await Promise.all([handlers.onListBookmarks(), handlers.onListCategories()]);
      if ("error" in bmRes) {
        bmTabs.textContent = "";
        bmPage.textContent = "";
        flashStatus(bmStatus, bmRes.error);
        return;
      }
      bmStatus.textContent = "";
      bmItems = bmRes.items;
      bmCats = "error" in catRes ? [] : catRes.items;
      if (activeTab !== null && !bmCats.some((c: BookmarkCategory): boolean => c.id === activeTab)) {
        activeTab = null;
      }
      renderTabs();
      renderActivePage();
    }

    const bookmarksNavBtn = scenePanelCtl.navButtons.get("Bookmarks");
    if (bookmarksNavBtn !== undefined) {
      bookmarksNavBtn.addEventListener("click", (): void => void refreshBookmarks());
    }
  }

  const exportSection = addSection("Export", scenePanelCtl.hoverPanel);
  const exportRow = document.createElement("div");
  exportRow.className = "row";
  const exportCyclesLabel = document.createElement("span");
  exportCyclesLabel.textContent = t("video.cycles");
  const exportCyclesInput = document.createElement("input");
  exportCyclesInput.type = "number";
  exportCyclesInput.min = "1";
  exportCyclesInput.max = "8";
  exportCyclesInput.step = "1";
  exportCyclesInput.value = "1";
  exportCyclesInput.style.width = "4em";
  const exportVideoBtn = document.createElement("button");
  exportVideoBtn.textContent = t("video.exportVideo");
  const exportStatus = document.createElement("div");
  exportStatus.className = "row-value scene-status";
  let exportModalActive = false;
  window.addEventListener(
    "keydown",
    (ev: KeyboardEvent): void => {
      if (exportModalActive) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }
    },
    true,
  );
  exportVideoBtn.addEventListener("click", (): void => {
    const cycles = Math.min(8, Math.max(1, Math.round(Number(exportCyclesInput.value) || 1)));
    exportCyclesInput.value = String(cycles);
    exportVideoBtn.disabled = true;
    exportStatus.textContent = t("video.starting");
    const overlay = document.createElement("div");
    overlay.className = "modal-overlay";
    const box = document.createElement("div");
    box.className = "modal-box";
    const progressText = document.createElement("div");
    progressText.textContent = t("video.exporting");
    box.appendChild(progressText);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    exportModalActive = true;
    void handlers
      .onExportVideo(cycles, (text: string): void => {
        exportStatus.textContent = text;
        progressText.textContent = t("video.exportingProgress", { text });
      })
      .then((err: string | null): void => {
        exportModalActive = false;
        overlay.remove();
        if (err !== null) {
          flashStatus(exportStatus, err);
        }
        exportVideoBtn.disabled = false;
      });
  });
  exportRow.append(exportCyclesLabel, exportCyclesInput, exportVideoBtn);
  const exportDurationRow = document.createElement("div");
  exportDurationRow.className = "row";
  const exportDurationLabel = document.createElement("span");
  exportDurationLabel.className = "row-label";
  exportDurationLabel.textContent = t("video.length");
  const exportDurationValue = document.createElement("span");
  exportDurationValue.className = "row-value";
  exportDurationRow.append(exportDurationLabel, exportDurationValue);

  /**
   * Mirrors videoExport's frame math (round each cycle to whole EXPORT_FPS
   * frames, then multiply by the loop count) so the shown length matches the
   * encoded file rather than the raw cycle product.
   */
  function updateExportDuration(): void {
    const cycles = Math.min(8, Math.max(1, Math.round(Number(exportCyclesInput.value) || 1)));
    const framesPerCycle = Math.round(handlers.onCycleSec() * EXPORT_FPS);
    exportDurationValue.textContent = formatVideoLength((framesPerCycle * cycles) / EXPORT_FPS);
  }
  exportCyclesInput.addEventListener("input", updateExportDuration);
  updateExportDuration();
  exportSection.append(exportRow, exportDurationRow, exportStatus);

  const perfSection = perfSub;
  const debugRender: DebugRenderState = { dprCap1: false, bloom: initialBloomMode, postFx: true };
  const dprCheckbox = addCheckbox(perfSection, "DPR 1.0", debugRender.dprCap1, (v: boolean): void => {
    debugRender.dprCap1 = v;
    handlers.onDebugRender(debugRender);
  });
  const bloomSelect = addSelectRow(perfSection, "Bloom", [
    ["off", "Off"],
    ["quarter", "Quarter"],
    ["half", "Half"],
  ]);
  bloomSelect.value = debugRender.bloom;
  bloomSelect.addEventListener("change", (): void => {
    debugRender.bloom = bloomSelect.value as BloomMode;
    handlers.onDebugRender(debugRender);
  });
  addCheckbox(perfSection, "PostFX chain", debugRender.postFx, (v: boolean): void => {
    debugRender.postFx = v;
    handlers.onDebugRender(debugRender);
  });
  function syncDebugRender(bloom: BloomMode, dprCap1: boolean): void {
    debugRender.bloom = bloom;
    debugRender.dprCap1 = dprCap1;
    bloomSelect.value = bloom;
    dprCheckbox.input.checked = dprCap1;
  }
  addCheckbox(perfSection, "Simulation", true, (v: boolean): void => handlers.onDebugSim(v));
  addCheckbox(perfSection, "Contact readback", true, (v: boolean): void => handlers.onDebugReadback(v));
  addCheckbox(perfSection, "Spatial grid", true, (v: boolean): void => handlers.onDebugGrid(v));

  const wallSection = environmentSub;
  const wallParams: WallParams = { ...WALL_DEFAULT_PARAMS };
  const wallPresetSelect = addSelectRow(wallSection, t("wall.preset"), [
    ["none", t("wall.preset_none")],
    ["noise-cork", t("wall.preset_noise_cork")],
    ["noise-olive", t("wall.preset_noise_olive")],
    ["noise-slate", t("wall.preset_noise_slate")],
    ["voronoi-cork", t("wall.preset_voronoi_cork")],
    ["voronoi-olive", t("wall.preset_voronoi_olive")],
    ["voronoi-slate", t("wall.preset_voronoi_slate")],
  ]);
  wallPresetSelect.value = wallParamsToPreset(wallParams);
  const wallZSlider = addSlider(wallSection, "z", -10, 0, 0.1, WALL_DEFAULT_Z, (v: number): string => v.toFixed(1), (v: number): void => handlers.onWallZ(v));
  const wallTileSlider = addSlider(wallSection, t("wall.tile"), 0.25, 8, 0.25, WALL_DEFAULT_TILE, (v: number): string => v.toFixed(2), (v: number): void => handlers.onWallTile(v));
  const wallNormalSlider = addSlider(wallSection, t("wall.normal"), 0, 3, 0.05, WALL_DEFAULT_NORMAL_SCALE, (v: number): string => v.toFixed(2), (v: number): void => handlers.onWallNormalScale(v));
  const wallResolutionSelect = addSelectRow(
    wallSection,
    t("wall.resolution"),
    WALL_RESOLUTIONS.map((r): [string, string] => [r.key, `${r.w}×${r.h}`]),
  );
  wallResolutionSelect.value = WALL_DEFAULT_RESOLUTION;
  wallResolutionSelect.addEventListener("change", (): void => {
    const res = WALL_RESOLUTIONS.find((r): boolean => r.key === wallResolutionSelect.value);
    if (res === undefined) {
      return;
    }
    handlers.onWallResolution(res.w, res.h);
  });
  const wallResolutionRow = wallResolutionSelect.parentElement as HTMLElement;
  tipRow(wallPresetSelect.parentElement as HTMLElement, "wallPreset");
  tipRow(wallZSlider.row, "wallZ");
  tipRow(wallTileSlider.row, "wallTile");
  tipRow(wallNormalSlider.row, "wallNormal");
  tipRow(wallResolutionRow, "wallResolution");
  const updateWallRowVisibility = (): void => {
    const textured = wallParams.pattern !== "none";
    wallZSlider.row.style.display = textured && effectiveFeature("3d") ? "" : "none";
    wallTileSlider.row.style.display = "none";
    wallNormalSlider.row.style.display = "none";
    wallResolutionRow.style.display = "none";
  };
  refreshWallRows = updateWallRowVisibility;
  updateWallRowVisibility();
  wallPresetSelect.addEventListener("change", (): void => {
    const preset = wallPresetSelect.value as WallPreset;
    const next = wallPresetParams(preset);
    wallParams.pattern = next.pattern;
    wallParams.palette = next.palette;
    const normalScale = wallPresetNormalScale(preset);
    wallNormalSlider.set(normalScale);
    handlers.onWallNormalScale(normalScale);
    updateWallRowVisibility();
    handlers.onWallRegenerate({ ...wallParams });
  });
  const wallLightSection = personalEnvironmentSub;
  const wallLightHeading = document.createElement("div");
  wallLightHeading.className = "row-subheader";
  wallLightHeading.textContent = t("wall.light");
  wallLightSection.appendChild(wallLightHeading);
  let wallLightX = init.wallLightX;
  let wallLightY = init.wallLightY;
  tipRow(addSlider(wallLightSection, `${t("wall.light")} x`, -1, 1, 0.01, wallLightX, (v: number): string => v.toFixed(2), (v: number): void => {
    wallLightX = v;
    handlers.onWallLightDir(wallLightX, wallLightY);
  }).row, "wallLight");
  tipRow(addSlider(wallLightSection, `${t("wall.light")} y`, -1, 1, 0.01, wallLightY, (v: number): string => v.toFixed(2), (v: number): void => {
    wallLightY = v;
    handlers.onWallLightDir(wallLightX, wallLightY);
  }).row, "wallLight");
  const wallShadowSelect = addSelectRow(wallLightSection, t("wall.shadowRes"), [
    ["1024", "1024"],
    ["2048", "2048"],
    ["4096", "4096"],
    ["8192", "8192"],
  ]);
  tipRow(wallShadowSelect.parentElement as HTMLElement, "shadowRes");
  wallShadowSelect.value = String(init.wallShadowRes);
  wallShadowSelect.addEventListener("change", (): void => handlers.onWallShadowRes(Number(wallShadowSelect.value)));

  const syncWall = (params: WallParams): void => {
    wallParams.pattern = params.pattern;
    wallParams.palette = params.palette;
    const preset = wallParamsToPreset(params);
    wallPresetSelect.value = preset;
    const normalScale = wallPresetNormalScale(preset);
    wallNormalSlider.set(normalScale);
    handlers.onWallNormalScale(normalScale);
    updateWallRowVisibility();
  };

  const licenseCredits: readonly { label: string; url: string }[] = [
    { label: t("license.creditMaou"), url: "https://maou.audio/" },
    { label: t("license.creditSalamander"), url: "https://www.ir.isas.jaxa.jp/~cyamauch/AccurateSalamander/index.ni.html" },
    { label: t("license.creditOtoLogic"), url: "https://otologic.jp/" },
  ];
  for (const credit of licenseCredits) {
    const row = document.createElement("div");
    row.className = "row";
    const link = document.createElement("a");
    link.className = "ext-link";
    link.textContent = credit.label;
    link.href = credit.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    row.appendChild(link);
    licenseSection.appendChild(row);
  }

  /**
   * Locks the param sliders a setparam node currently drives: each driven param's
   * slider follows the live (overwritten) value and is disabled, while undriven
   * ones re-enable. Rate, speed and spd range also stay disabled when an emitter is
   * emitIn-driven, so the two lock sources combine on those sliders.
   */
  let lockedInputs: HTMLInputElement[] = [];

  /**
   * Disables (and value-follows) every slider whose param a Param-in edge is driving
   * on the selected entity, mapping each settable param NAME to its panel control so
   * the lock stays in lockstep with `settableParams`. Only sliders this function
   * locked last call are re-enabled, so unrelated disables (e.g. offset at high rate)
   * are left alone. Rate also stays disabled while emitIn-driven. Speed and spd
   * range stay disabled only while the driven emitter inherits impact speed —
   * the "speed from settings" toggle (shown only when emitIn-driven) re-enables
   * them because the emitter's own range applies then.
   */
  function applyLocks(emitInDriven: boolean, driven: Map<string, number>): void {
    pulseSpeedToggle.row.style.display = emitInDriven ? "" : "none";
    const impactSpeedLocked = emitInDriven && !pulseSpeedToggle.input.checked;
    for (const input of lockedInputs) {
      input.disabled = false;
    }
    lockedInputs = [];
    const lock = (name: string, ctrl: { set: (v: number) => void; input: HTMLInputElement }, display?: (v: number) => number): void => {
      const v = driven.get(name);
      if (v === undefined) {
        return;
      }
      ctrl.set(display ? display(v) : v);
      ctrl.input.disabled = true;
      lockedInputs.push(ctrl.input);
    };
    const lockCheck = (name: string, ctrl: { input: HTMLInputElement }): void => {
      const v = driven.get(name);
      if (v === undefined) {
        return;
      }
      ctrl.input.checked = v >= 0.5;
      ctrl.input.disabled = true;
      lockedInputs.push(ctrl.input);
    };
    lockCheck("mute", objMuteToggle);
    lockCheck("kill", killToggle);
    lockCheck("collision", collisionToggle);
    lockCheck("play", orbPlayToggle);
    lock("bounce", bounce);
    lock("friction", friction);
    lock("mass", massSlider);
    lock("bounce", emBounceSlider);
    lock("friction", emFrictionSlider);
    lock("drag", emDragSlider);
    lock("spread", spreadSlider);
    lock("life", lifeSlider);
    lock("life range", lifeRangeSlider);
    lock("radius", fieldR);
    lock("strength", fieldStrength);
    lock("strength", fieldOmega);
    lock("k", fieldK);
    lock("fade", fieldFade);
    const scale100 = orbitSync ? 100 : 1;
    lock("rot x", orbRotX, (v: number): number => v * scale100);
    lock("rot y", orbRotY, (v: number): number => v * scale100);
    lock("rot z", orbRotZ, (v: number): number => v * scale100);
    lock("amp x", orbAmpX);
    lock("amp y", orbAmpY);
    lock("amp z", orbAmpZ);
    lock("period x", orbPerX, (v: number): number => v * scale100);
    lock("period y", orbPerY, (v: number): number => v * scale100);
    lock("period z", orbPerZ, (v: number): number => v * scale100);
    lock("phase x", orbPhX, (v: number): number => v * 100);
    lock("phase y", orbPhY, (v: number): number => v * 100);
    lock("phase z", orbPhZ, (v: number): number => v * 100);
    lock("value", lampValueSlider);
    lock("hue", lampHueSlider);
    lock("sat", lampSatSlider);
    lock("volume", timbreVolumeSlider);
    lock("transpose", timbreTransposeSlider, (v: number): number => Math.round(v));
    const rate = driven.get("rate");
    if (rate !== undefined) {
      rateSlider.set(sliderFromRate(rate));
      rateSlider.input.disabled = true;
    } else {
      rateSlider.input.disabled = emitInDriven;
    }
    const speed = driven.get("speed");
    if (speed !== undefined) {
      speedSlider.set(speed);
      speedSlider.input.disabled = true;
    } else {
      speedSlider.input.disabled = impactSpeedLocked;
    }
    const spdRange = driven.get("spd range");
    if (spdRange !== undefined) {
      spdRangeSlider.set(spdRange);
      spdRangeSlider.input.disabled = true;
    } else {
      spdRangeSlider.input.disabled = impactSpeedLocked;
    }
  }

  const SCENE_NAV_ORDER: readonly string[] = [
    "Scene Settings",
    "Scene Local",
    "Scene Uploaded",
    "Scene Mic",
    "Community",
    "Bookmarks",
    "Export",
    "Reset",
  ];
  const SETTINGS_NAV_ORDER: readonly string[] = [
    "Personal Settings",
    "Debug Settings",
    "License",
    "Feedback",
    "Privacy",
    "Account",
    "Sign-out",
  ];
  for (const title of SCENE_NAV_ORDER) {
    const btn = scenePanelCtl.navButtons.get(title);
    if (btn !== undefined) {
      scenePanelCtl.nav.appendChild(btn);
    }
  }
  for (const title of SETTINGS_NAV_ORDER) {
    const btn = settingsPanelCtl.navButtons.get(title);
    if (btn !== undefined) {
      settingsPanelCtl.nav.appendChild(btn);
    }
  }
  const sceneSettingsNavBtn = scenePanelCtl.navButtons.get("Scene Settings");
  if (sceneSettingsNavBtn === undefined) {
    throw new Error("Scene Settings nav button missing");
  }
  const featuresNavBtn = sceneSettings.subNav.get(t("section.features"));
  if (featuresNavBtn === undefined) {
    throw new Error("Features nav button missing");
  }

  const RADIAL_MAX_W = 768;
  const radialForce = new URLSearchParams(location.search).get("radial");

  /**
   * Narrow viewports get the radial marking menu regardless of pointer type, so
   * a desktop browser resized below the width threshold switches too. The
   * `?radial=1|0` override forces it on / off. Evaluated live so a resize across
   * the width threshold flips the chrome without a reload.
   */
  function isRadialMode(): boolean {
    if (radialForce === "1") {
      return true;
    }
    if (radialForce === "0") {
      return false;
    }
    return window.innerWidth < RADIAL_MAX_W;
  }

  const radial = createRadialMenu({
    suspendControls: (): void => handlers.onRadialSuspendControls(),
    resumeControls: (): void => handlers.onRadialResumeControls(),
  });

  const RADIAL_SETTINGS_ORDER: readonly string[] = [...SETTINGS_NAV_ORDER];
  const SETTINGS_GEAR_SVG = `<circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M12 4v2.2M12 17.8V20M4 12h2.2M17.8 12H20M6.3 6.3l1.6 1.6M16.1 16.1l1.6 1.6M17.7 6.3l-1.6 1.6M7.9 16.1l-1.6 1.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>`;
  const GENERATE_PLUS_SVG = `<path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`;
  const OPERATE_EDIT_SVG = `<path d="M14.5 5.5l4 4L9 19l-4.5 1 1-4.5z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>`;

  function panelEntry(panelCtl: NavPanel): IconGroup | undefined {
    return iconGroups.find((g: IconGroup): boolean => g.btn === panelCtl.btn);
  }

  /**
   * Drives one of a top-controls nav panel's own nav buttons from the radial.
   * Section buttons open the panel pinned at that section; action buttons (Reset
   * / Sign-in — `main-nav-btn-action`) only fire their click, so the
   * radial never leaves an empty hover panel pinned open behind them.
   */
  function openRadialPanelSection(panelCtl: NavPanel, sectionTitle: string): void {
    const navBtn = panelCtl.navButtons.get(sectionTitle);
    if (navBtn === undefined) {
      return;
    }
    if (navBtn.classList.contains("main-nav-btn-action")) {
      navBtn.click();
      return;
    }
    panelCtl.showNav();
    const entry = panelEntry(panelCtl);
    if (entry !== undefined) {
      pinnedPanel = entry;
      setExclusivePanel(entry);
    }
    navBtn.click();
  }

  function panelChildren(panelCtl: NavPanel, order: readonly string[]): RadialItem[] {
    const items: RadialItem[] = [];
    for (const title of order) {
      const navBtn = panelCtl.navButtons.get(title);
      if (navBtn === undefined || navBtn.style.display === "none") {
        continue;
      }
      items.push({
        key: `panel:${title}`,
        label: navBtn.textContent ?? sectionLabel(title),
        disabled: navBtn.disabled,
        onSelect: (): void => openRadialPanelSection(panelCtl, title),
      });
    }
    return items;
  }

  function placeChild([tool, label, icon]: PaletteEntry): RadialItem {
    return {
      key: tool,
      label: t(label),
      iconSvg: icon,
      onSelect: (anchor: RadialAnchor): void => handlers.onRadialPlaceTool(tool, anchor.clientX, anchor.clientY),
    };
  }

  function generateChildren(): RadialItem[] {
    const transformTools = TRANSFORM_PALETTE_TOOLS.filter(([tool]: PaletteEntry): boolean =>
      effectiveFeature("node") || !isNodeGatedTool(tool),
    );
    const tools: PaletteEntry[] = [
      ...EMITTER_PALETTE_TOOLS,
      ...SHAPE_PALETTE_TOOLS.filter(([tool]: PaletteEntry): boolean => tool !== "funnel" || effectiveFeature("3d")),
      ...(effectiveFeature("physics") ? PHYSICS_PALETTE_TOOLS : []),
      ...transformTools,
    ];
    return tools.map(placeChild);
  }

  function operateChildren(): RadialItem[] {
    return [
      { key: "undo", label: t("action.undo"), iconSvg: UNDO_ICON, onSelect: (): void => handlers.onUndo() },
      { key: "redo", label: t("action.redo"), iconSvg: REDO_ICON, onSelect: (): void => handlers.onRedo() },
      { key: "copy", label: t("action.copy"), iconSvg: COPY_ICON, onSelect: (): void => handlers.onCopy() },
      {
        key: "paste",
        label: t("action.paste").replace(/\s*\([^)]*\)\s*$/, ""),
        iconSvg: PASTE_ICON,
        onSelect: (anchor: RadialAnchor): void => handlers.onRadialPaste(anchor.clientX, anchor.clientY),
      },
    ];
  }

  function mpChildren(): RadialItem[] {
    if (mpRoomActive) {
      return [{ key: "mp-leave", label: t("radial.leaveRoom"), iconSvg: MP_ICON_SVG, onSelect: (): void => handlers.onLeaveRoom() }];
    }
    return [
      { key: "mp-create", label: t("radial.createRoom"), iconSvg: MP_ICON_SVG, onSelect: (): void => handlers.onCreateRoom() },
      { key: "mp-public", label: t("radial.viewPublic"), iconSvg: MP_ICON_SVG, onSelect: (): void => handlers.onBrowsePublic() },
    ];
  }

  function buildRadialRoot(): RadialItem[] {
    const root: RadialItem[] = [
      { key: "generate", label: t("radial.generate"), iconSvg: GENERATE_PLUS_SVG, children: generateChildren },
      { key: "scene", label: t("radial.scene"), iconSvg: SCENE_ICON_SVG, children: (): RadialItem[] => panelChildren(scenePanelCtl, SCENE_NAV_ORDER) },
      { key: "settings", label: t("radial.settings"), iconSvg: SETTINGS_GEAR_SVG, children: (): RadialItem[] => panelChildren(settingsPanelCtl, RADIAL_SETTINGS_ORDER) },
      ...(MULTIPLAYER_ENABLED ? [{ key: "multiplayer", label: t("multiplayer.menuButton"), iconSvg: MP_ICON_SVG, children: mpChildren }] : []),
      { key: "operate", label: t("radial.operate"), iconSvg: OPERATE_EDIT_SVG, children: operateChildren },
    ];
    if (effectiveFeature("node")) {
      root.splice(1, 0, { key: "node", label: t("tool.node"), iconSvg: NODE_PARENT_ICON, children: (): RadialItem[] => NODE_PALETTE_TOOLS.filter(([tool]: PaletteEntry): boolean => tool !== "global" || !globalPlaced).map(placeChild) });
    }
    return root;
  }

  function refreshRadial(): void {
    radial.setRoot(buildRadialRoot());
  }

  settingsPanelCtl.btn.classList.add("radial-hidden-btn");
  scenePanelCtl.btn.classList.add("radial-hidden-btn");
  mpGroup.btn.parentElement?.classList.add("radial-hidden-full");

  function applyRadialChrome(): void {
    document.body.classList.toggle("radial-mode", isRadialMode());
    if (!isRadialMode()) {
      radial.close();
    }
  }

  radialRefreshHook = refreshRadial;
  refreshRadial();
  applyRadialChrome();
  window.addEventListener("resize", applyRadialChrome);

  document.body.appendChild(topControlsBar);
  document.body.appendChild(objectPanelScroll);
  document.body.appendChild(groupExprOverlay);
  document.body.appendChild(palette);
  const panelScrollGutterW = objectPanelScroll.offsetWidth - objectPanelScroll.clientWidth;
  objectPanelScroll.style.right = `${12 - panelScrollGutterW}px`;

  if (uiView) {
    topControlsBar.style.display = "none";
    objectPanelScroll.style.display = "none";
    palette.style.display = "none";
  }

  const syncSceneSettings = (clockPeriod: number, gravityY: number): void => {
    currentClockPeriod = clockPeriod;
    tempoSlider.set(60 / clockPeriod);
    gravitySlider.set(-gravityY);
  };

  const openedOverlay = document.createElement("div");
  openedOverlay.className = "opened-scene-overlay";
  openedOverlay.style.display = "none";
  topControlsBar.appendChild(openedOverlay);
  let openedGen = 0;

  const roomOverlay = document.createElement("div");
  roomOverlay.className = "opened-scene-overlay room-info-overlay";
  roomOverlay.style.display = "none";
  topControlsBar.appendChild(roomOverlay);

  /**
   * Shows the scene chrome for a scene opened from Community as a bar at the right
   * end of the top-left control row: a two-line title / author block with the
   * copy, like and bookmark buttons beside it, sized to the control-button height.
   * Hidden when meta is null (and while the feed is open — see setProvenance). The
   * like/bookmark states are fetched async and guarded by a generation counter so
   * a re-open mid-fetch never populates a stale overlay.
   */
  function setOpenedScene(meta: SceneListItem | null): void {
    openedGen++;
    const gen = openedGen;
    openedOverlay.textContent = "";
    if (meta === null) {
      openedOverlay.style.display = "none";
      return;
    }
    openedOverlay.style.display = "";
    track("scene_view", { scene_id: meta.id });

    const titleEl = document.createElement("div");
    titleEl.className = "opened-scene-title";
    titleEl.textContent = meta.title;
    const author = document.createElement("button");
    author.className = "scene-modal-author opened-scene-author";
    author.textContent = meta.ownerName ?? t("community.unknownAuthor");
    author.addEventListener("click", (): void => {
      if (meta.ownerName === null) {
        return;
      }
      if (activeCommunitySearch !== null) {
        activeCommunitySearch(meta.ownerName);
      } else {
        setOpenedScene(null);
        openCommunityModal(handlers, meta.ownerName);
      }
    });
    const textBlock = document.createElement("div");
    textBlock.className = "opened-scene-text";
    textBlock.append(titleEl, author);

    const actions = document.createElement("div");
    actions.className = "opened-scene-actions";
    const status = document.createElement("div");
    status.className = "row-value scene-status opened-scene-status";
    openedOverlay.append(textBlock, actions, status);

    const copyBtn = document.createElement("button");
    copyBtn.className = "opened-scene-copy";
    copyBtn.textContent = "🔗";
    copyBtn.setAttribute("aria-label", t("community.copy_url"));
    attachTooltip(copyBtn, (): string => t("community.copy_url"));
    copyBtn.addEventListener("click", async (): Promise<void> => {
      await navigator.clipboard.writeText(shareUrlFor(meta.id));
      flashStatus(status, t("community.url_copied"));
    });
    actions.append(copyBtn);

    void Promise.all([handlers.onHasLiked(meta.id), handlers.onBookmarkedSceneIds([meta.id])]).then(
      ([liked0, bookmarkedIds]: [boolean, string[]]): void => {
        if (gen !== openedGen) {
          return;
        }
        const likeBtn = document.createElement("button");
        likeBtn.className = "feed-card-like opened-scene-like";
        const heart = document.createElement("span");
        heart.className = "feed-card-heart";
        const likeCountSpan = document.createElement("span");
        likeCountSpan.className = "opened-scene-like-count";
        likeBtn.append(heart, likeCountSpan);
        let liked = liked0;
        let count = meta.likeCount;
        const syncLike = (): void => {
          heart.textContent = liked ? "♥" : "♡";
          likeCountSpan.textContent = ` ${count}`;
          likeBtn.classList.toggle("liked", liked);
        };
        syncLike();
        likeBtn.addEventListener("click", async (): Promise<void> => {
          likeBtn.disabled = true;
          const err = liked ? await handlers.onUnlikeScene(meta.id) : await handlers.onLikeScene(meta.id);
          likeBtn.disabled = false;
          if (err !== null) {
            flashStatus(status, err);
            return;
          }
          count += liked ? -1 : 1;
          liked = !liked;
          syncLike();
        });
        const bookmarkBtn = makeBookmarkButton(
          handlers,
          meta.id,
          bookmarkedIds.includes(meta.id),
          (msg: string): void => {
            flashStatus(status, msg);
          },
          "opened-scene-bookmark",
          undefined,
          (sceneId: string, bookmarked: boolean): void => {
            if (bookmarked) {
              void captureThumb().then((dataUrl: string): void => saveBookmarkThumb(sceneId, dataUrl));
            }
          },
        );
        actions.append(likeBtn, bookmarkBtn);
      },
    );
  }

  /**
   * Renders the room-info chip in its own overlay (room-info-overlay), shown
   * alongside the scene chip rather than replacing it: room name / owner name (not
   * clickable — a room is not an author-searchable Community scene) / live
   * occupancy. No like or bookmark — a room is not a likeable scene. The two chips
   * are independent so a room created from a Community scene shows both the source
   * scene's chrome and the room info. Null clears it.
   */
  function setRoomChip(info: { name: string; ownerName: string | null; count: number; capacity: number | null } | null): void {
    roomOverlay.textContent = "";
    if (info === null) {
      roomOverlay.style.display = "none";
      return;
    }
    roomOverlay.style.display = "";

    const titleEl = document.createElement("div");
    titleEl.className = "opened-scene-title";
    titleEl.textContent = info.name;
    const ownerEl = document.createElement("div");
    ownerEl.className = "opened-scene-author opened-scene-author-static";
    ownerEl.textContent = info.ownerName ?? t("community.unknownAuthor");
    const textBlock = document.createElement("div");
    textBlock.className = "opened-scene-text";
    textBlock.append(titleEl, ownerEl);

    const actions = document.createElement("div");
    actions.className = "opened-scene-actions";
    const occ = document.createElement("div");
    occ.className = "opened-scene-occupancy";
    occ.textContent =
      info.capacity === null
        ? t("multiplayer.occupancy", { count: info.count })
        : t("multiplayer.occupancyCap", { count: info.count, cap: info.capacity });
    actions.append(occ);

    roomOverlay.append(textBlock, actions);
  }

  let foreignSceneLocked = false;
  let roomGuestLocked = false;
  let communitySceneLoaded = false;

  /**
   * Disables the affordances that do not apply to someone else's scene while a
   * foreign scene is loaded: save-slot and upload (keeping it under your own name
   * is an attribution violation — retention goes through bookmarks), plus the
   * scene-settings and video-export panels (editing or exporting another author's
   * scene as your own is out of scope). The reset button stays enabled — it is the
   * way back to your own scene.
   */
  function setForeignSceneLock(blocked: boolean): void {
    foreignSceneLocked = blocked;
    applySceneLocks();
  }

  /**
   * Whether a foreign-authored Community scene is currently loaded (its info panel
   * is showing). Unlike the foreign-scene lock this is orthogonal to room role, so
   * a room guest can still save a local copy of the shared room scene; only a
   * Community scene by another author blocks local save — the same attribution
   * rule as single-player.
   */
  function setCommunitySceneLoaded(loaded: boolean): void {
    communitySceneLoaded = loaded;
    applySceneLocks();
  }

  /**
   * Locks the owner-only room operations for a room guest (anyone in a room who is
   * not its creator): scene reset, the scene-settings panel, and the clock reset
   * button. Held independently of the foreign-scene lock so releasing one never
   * clears the other — the scene-settings nav, which both can target, stays
   * disabled while either is active (see applySceneLocks). A reset wired through a
   * global node's reset pulse-in is unaffected — it fires for everyone.
   */
  function setRoomGuestLock(blocked: boolean): void {
    roomGuestLocked = blocked;
    applySceneLocks();
  }

  /**
   * Locks the SimpleMode + Node toggles to a public room's fixed mode (SimpleMode
   * OFF + Node ON for every participant). Both toggles are disabled with a "fixed in
   * public room" tooltip while locked. Independent of the guest lock (a public room
   * has no guests) and of the node's Simple-mode gate: when the public lock releases
   * on leave the node toggle falls back to its Simple-mode disabled state (`simpleOn`)
   * and the wire-driven global locks, so neither reason clobbers the other. The
   * checkbox values themselves are pinned by the caller (applyRoomPublicState); this
   * only owns the disabled state and tooltip.
   */
  function setRoomModeLock(locked: boolean): void {
    roomModeLocked = locked;
    const reason = locked ? t("multiplayer.fixedInPublicRoom") : "";
    simpleToggle.input.disabled = locked || globalLockedParams.has("simple");
    simpleToggle.row.dataset.hintTip = reason;
    const nodeInput = featureInputByCat.get("node");
    if (nodeInput !== undefined) {
      nodeInput.disabled = locked || simpleOn || globalLockedParams.has("node");
    }
    const nodeRow = featureRowByCat.get("node");
    if (nodeRow !== undefined) {
      nodeRow.dataset.hintTip = reason;
    }
  }

  /**
   * Sets a lazily-attached hover/long-press hint (see attachTooltip) whose text
   * can change over time, e.g. a disabled-state reason. Reused calls on the same
   * element only update the text, since attachTooltip's getText re-reads the
   * dataset at show time.
   */
  function setHintTip(el: HTMLElement, text: string): void {
    if (el.dataset.hintTipAttached === undefined) {
      el.dataset.hintTipAttached = "1";
      attachTooltip(el, (): string => el.dataset.hintTip ?? "");
    }
    el.dataset.hintTip = text;
  }

  const disabledTipWraps = new WeakMap<HTMLButtonElement, HTMLDivElement>();
  /**
   * A disabled button dispatches no pointer events, so a tooltip attached to it
   * directly would never show while it is disabled — which is exactly when a
   * lock-reason hint is needed. Wraps the button in an always-enabled container
   * the first time it is called and reuses that wrapper afterward, so the hint
   * fires on the wrapper instead.
   */
  function wrapForTip(btn: HTMLButtonElement): HTMLDivElement {
    const existing = disabledTipWraps.get(btn);
    if (existing !== undefined) {
      return existing;
    }
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;flex:0 0 auto;";
    btn.style.width = "100%";
    btn.replaceWith(wrap);
    wrap.appendChild(btn);
    disabledTipWraps.set(btn, wrap);
    return wrap;
  }

  let globalLockedControls: Array<{ input: HTMLInputElement; restore: boolean }> = [];
  const globalLockedParams = new Set<string>();

  /**
   * Locks the scene controls a global node's setParamIn edges currently drive, so a
   * driven gravity / tempo slider or simple / node toggle follows the live override
   * value and cannot be operated — the same lock-and-follow idiom as the object
   * panel's Param-in locks. Each control's pre-lock disabled state is captured so
   * releasing the wire restores it (guest lock for the sliders, Simple-mode gate for
   * the node toggle). applySceneLocks ORs globalLockedParams so a room-state change
   * cannot transiently re-enable a driven slider.
   */
  function applyGlobalParamLocks(driven: Map<string, number>): void {
    for (const { input, restore } of globalLockedControls) {
      input.disabled = restore;
    }
    globalLockedControls = [];
    globalLockedParams.clear();
    const lockSlider = (name: string, ctrl: { set: (v: number) => void; input: HTMLInputElement }, restore: boolean): void => {
      const v = driven.get(name);
      if (v === undefined) {
        return;
      }
      ctrl.set(v);
      ctrl.input.disabled = true;
      globalLockedControls.push({ input: ctrl.input, restore });
      globalLockedParams.add(name);
    };
    const lockCheck = (name: string, input: HTMLInputElement | undefined, restore: boolean): void => {
      const v = driven.get(name);
      if (v === undefined || !input) {
        return;
      }
      input.checked = v >= 0.5;
      input.disabled = true;
      globalLockedControls.push({ input, restore });
      globalLockedParams.add(name);
    };
    lockSlider("gravity", gravitySlider, roomGuestLocked);
    lockSlider("tempo", tempoSlider, roomGuestLocked);
    lockCheck("simple", simpleToggle.input, false);
    lockCheck("node", featureInputByCat.get("node"), simpleOn);
  }

  /**
   * Recomputes the disabled state + tooltip of every lockable affordance from the
   * two independent lock reasons. Save-slot / upload / export are foreign-only;
   * scene reset, gravity/tempo, tap-tempo, and the clock reset button are
   * guest-only (gravity / clock are owner-only room-synced settings, so a guest
   * dragging them would silently desync — locking them prevents that confusion;
   * the clock reset is the shared-clock owner action). The scene-settings nav is
   * the one affordance both target, so its disabled is the OR of the two and its
   * tooltip prefers the foreign reason when both apply. A reset wired through a
   * global node's reset pulse-in still fires for everyone — only the direct UI
   * button is gated. While in any room (owner included, `mpRoomActive`) the
   * scene-replacing / heavy actions — Community, Bookmarks, video Export, Reset,
   * and Tutorial — are disabled, since they would swap or tear down the shared
   * scene out from under the other participants. Local save stays available to
   * everyone in a room; it is blocked only when a foreign Community scene is loaded
   * (`communitySceneLoaded`), the same rule as single-player.
   */
  function applySceneLocks(): void {
    const foreignReason = foreignSceneLocked ? t("scene.foreignLocked") : "";
    const guestReason = roomGuestLocked ? t("multiplayer.ownerOnly") : "";
    const roomReason = mpRoomActive ? t("multiplayer.disabledInRoom") : "";
    gravitySlider.input.disabled = roomGuestLocked || globalLockedParams.has("gravity");
    gravitySlider.row.dataset.hintTip = guestReason;
    tempoSlider.input.disabled = roomGuestLocked || globalLockedParams.has("tempo");
    tempoSlider.row.dataset.hintTip = guestReason;
    tapBtn.disabled = roomGuestLocked;
    setHintTip(tapRow, guestReason);
    resetBtn.disabled = roomGuestLocked;
    setHintTip(resetRow, guestReason);
    saveLocalBtn.disabled = communitySceneLoaded;
    sceneUploadBtn.disabled = foreignSceneLocked;
    setHintTip(storeRow, communitySceneLoaded ? t("scene.foreignLocked") : "");
    setHintTip(uploadRow, foreignReason);
    const exportBtn = scenePanelCtl.navButtons.get("Export");
    if (exportBtn !== undefined) {
      exportBtn.disabled = foreignSceneLocked || mpRoomActive;
      setHintTip(wrapForTip(exportBtn), mpRoomActive ? roomReason : foreignReason);
    }
    const settingsBtn = scenePanelCtl.navButtons.get("Scene Settings");
    if (settingsBtn !== undefined) {
      settingsBtn.disabled = foreignSceneLocked || roomGuestLocked;
      setHintTip(wrapForTip(settingsBtn), foreignSceneLocked ? foreignReason : guestReason);
    }
    const resetNavBtn = scenePanelCtl.navButtons.get("Reset");
    if (resetNavBtn !== undefined) {
      resetNavBtn.disabled = roomGuestLocked || mpRoomActive;
      setHintTip(wrapForTip(resetNavBtn), mpRoomActive ? roomReason : guestReason);
    }
    const communityNavBtn = scenePanelCtl.navButtons.get("Community");
    if (communityNavBtn !== undefined) {
      communityNavBtn.disabled = mpRoomActive;
      setHintTip(wrapForTip(communityNavBtn), roomReason);
    }
    const bookmarksNavBtn = scenePanelCtl.navButtons.get("Bookmarks");
    if (bookmarksNavBtn !== undefined) {
      bookmarksNavBtn.disabled = mpRoomActive;
      setHintTip(wrapForTip(bookmarksNavBtn), roomReason);
    }
    if (tutorialStartBtn !== null) {
      tutorialStartBtn.disabled = mpRoomActive;
      setHintTip(tutorialRow, roomReason);
    }
    storeStatus.textContent = communitySceneLoaded ? t("scene.foreignLocked") : "";
    localGrid.style.opacity = mpRoomActive ? "0.5" : "";
    localGrid.style.pointerEvents = mpRoomActive ? "none" : "";
  }

  /**
   * Locks the object parameter panel when the selected object is being edited by
   * another player in the room: pointer-events off + dimmed, so its controls
   * cannot mutate a claimed object (the gizmo move block is enforced separately).
   */
  function setObjectPanelLocked(locked: boolean): void {
    objectPanelScroll.style.pointerEvents = locked ? "none" : "";
    objectPanelScroll.style.opacity = locked ? "0.5" : "";
  }

  function setObjectPanelBottom(bottom: boolean, clearancePx: number = 12): void {
    objectPanelScroll.classList.toggle("at-bottom", bottom);
    objectPanelScroll.style.bottom = bottom ? `${clearancePx}px` : "";
  }

  function setObjectPanelTopOffset(px: number): void {
    objectPanelScroll.style.setProperty("--obj-panel-top-offset", `${px}px`);
  }

  /** Closes any open top-bar hover panel and blocks new ones from opening on hover (used while the tutorial owns the screen). */
  function setHoverPanelsSuppressed(suppressed: boolean): void {
    hoverPanelsSuppressed = suppressed;
    if (suppressed) {
      pinnedPanel = null;
      setExclusivePanel(null);
    }
  }

  /** Keeps a hover-opened panel open after the pointer leaves it, so a tutorial step can guide the user from the button into the panel without it closing mid-move. */
  function setHoverLeaveDisabled(disabled: boolean): void {
    hoverLeaveDisabled = disabled;
  }

  /** Clears mute and sets the master volume, keeping the slider and speaker icon in sync. */
  function setMasterVolumeUnmuted(v: number): void {
    muted = false;
    lastVolume = v;
    handlers.onVolume(v);
    volumeSlider.set(v);
    renderAudioIcon(v, false);
  }

  return { syncControls, highlightTool, setToolsEnabled, setGlobalPlaced, applyLocks, applyGlobalParamLocks, setUndoRedoEnabled, setCopyPasteEnabled, showPaletteHint, nodeSubmenu: { toolAt: nodeSubmenuToolAt, setHover: setNodeMenuHover, close: closeNodeSubmenu }, setSimpleChecked: setSimple, setFeatureChecked, setCameraState, setTouchPlayToggleVisible, setTouchPlayToggleActive, setDimensionToggleVisible, setDimensionToggleActive, setViewMode, setPlayModeHidden, syncSceneSettings, syncWall, setOpenedScene, setRoomChip, setForeignSceneLock, setCommunitySceneLoaded, setRoomGuestLock, setRoomModeLock, setObjectPanelLocked, setObjectPanelBottom, setObjectPanelTopOffset, syncDebugRender, tutorialTargets: { sequencerBtn: (): HTMLElement | null => toolButtons.get("emitter-sync") ?? null, boxBtn: (): HTMLElement | null => toolButtons.get("box") ?? null, scenePanelBtn: scenePanelCtl.btn, sceneSettingsNavBtn, featuresNavBtn, communityNavBtn: (): HTMLElement | null => scenePanelCtl.navButtons.get("Community") ?? null, touchPlayBtn: touchPlayToggleBtn, timbreRow: (): HTMLElement | null => timbreSelect.parentElement }, tutorialOpenScenePanelSection: (sectionTitle: string): void => openRadialPanelSection(scenePanelCtl, sectionTitle), setHoverPanelsSuppressed, setHoverLeaveDisabled, setMasterVolumeUnmuted, setRoomActive, isRadialMode, radial, uiMin, uiView, uiNoSeq };
}
